import { type Application, Sprite, Texture, Container, Text, TextStyle, Graphics } from 'pixi.js';
import { GameLoop } from './gameLoop';
import { WA_PHYSICS } from './constants';
import { getGameRNG, setGameSeed } from './rng';
import { TerrainCollisionMask } from '../physics/terrainCollision';
import { checkWormBarrelCollision, resolveWormBarrelCollision } from '../physics/barrelCollision';
import { TurnManager } from './turnManager';
import { CameraController, type TrackedEntities, type TargetingState } from './cameraController';
import { EventBus } from './eventBus';
import { EntityRegistry } from './entityRegistry';
import { TargetingSystem } from './targetingSystem';
import { WeaponSelectionManager } from './weaponSelectionManager';
import { ChargeStateManager } from './chargeStateManager';
import { ToolSystemManager } from './toolSystemManager';
import { InputManager, type InputAction } from './inputManager';
import { SpriteManager } from './spriteManager';
import {
  EntityFactory,
  type EntityFactoryCallbacks,
  type EntityFactoryDependencies,
} from './entityFactory';
import {
  WeaponExecutor,
  type WeaponExecutorCallbacks,
  type WeaponExecutorDependencies,
} from './weaponExecutor';
import {
  ExplosionManager,
  type ExplosionManagerCallbacks,
  type ExplosionManagerDependencies,
} from './explosionManager';
import { WorldStabilityCheckerImpl } from './worldStabilityChecker';
import { HazardSpawner } from './hazardSpawner';
import { JetpackManager } from './jetpackManager';
import { SuddenDeathManager } from './suddenDeathManager';
import { CrateManager } from './crateManager';
import { PhysicsStep } from '../sim/physicsStep';
import { Simulation } from '../sim/simulation';
import { gameLogger } from './gameLogger';
import {
  createTerrainCanvas,
  generateTerrainMask,
  renderTerrainVisuals,
  findSpawnPoints,
  type TerrainOptions,
} from '../terrain/bitmap';
import { MaskCanvas } from '../sim/maskCanvas';
import { createExplosion } from '../terrain/destruction';
import type { Worm } from '../entities/worm';
import { resolveTankAnimState } from '../render/tankAnimState';
import { Grave } from '../entities/grave';
import { Crate } from '../entities/crate';
import { OilBarrel } from '../entities/oilBarrel';
import { Projectile } from '../entities/projectile';
import { Fire } from '../entities/fire';
import { createTeam, Team, type TeamConfig } from '../logic/team';
import { GameState } from '../logic/gameState';
import { weaponRegistry } from '../weapons/registry';
import { WeaponGrenade } from '../weapons/grenade';
import { WeaponGrub } from '../weapons/grub';
import { WeaponGrubSS } from '../weapons/grubSS';
import { WeaponAduka } from '../weapons/aduka';
import { WeaponNak } from '../weapons/nak';
import { WeaponAdukaSS } from '../weapons/adukaSS';
import { WeaponLightningT1, WeaponLightningT2 } from '../weapons/lightning';
import { WeaponKnight } from '../weapons/knight';
import { WeaponTrico } from '../weapons/trico';
import { WeaponKalsiddon } from '../weapons/kalsiddon';
import { WeaponFrogLeft, WeaponFrogRight } from '../weapons/frog';
import { WeaponRaon, MiniRaon } from '../weapons/raon';
import { WeaponJdT2, WeaponJdSS } from '../weapons/jd';
import { WeaponBazooka } from '../weapons/bazooka';
import { WeaponDynamite } from '../weapons/dynamite';
import { WeaponClusterBomb } from '../weapons/clusterBomb';
import { WeaponShotgun } from '../weapons/shotgun';
import {
  WeaponNinjaRope,
  releaseRope,
  extendRope,
  isRopeActive,
  getRopeRenderData,
} from '../weapons/ninjaRope';
import { WeaponAirstrike } from '../weapons/airstrike';
import { WeaponMine, Mine } from '../weapons/mine';
import { WeaponTeleport } from '../weapons/teleport';
import { WeaponParachute } from '../weapons/parachute';
import { WeaponHomingMissile, HomingMissile } from '../weapons/homingMissile';
import { WeaponFirePunch } from '../weapons/firePunch';
import { WeaponBaseballBat } from '../weapons/baseballBat';
import { WeaponBlowtorch } from '../weapons/blowtorch';
import { WeaponDrill } from '../weapons/drill';
import { WeaponHolyHandGrenade } from '../weapons/holyHandGrenade';
import { WeaponSheep, Sheep } from '../weapons/sheep';
import { WeaponJetpack } from '../weapons/jetpack';
import { WeaponGirder, drawGirderOnTerrain, GIRDER_ANGLES, GIRDER_SIZES } from '../weapons/girder';
import { WeaponSuperSheep, type SuperSheep } from '../weapons/superSheep';
import { WeaponBananaBomb } from '../weapons/bananaBomb';
import { WeaponMortar } from '../weapons/mortar';
import { WeaponMolotov } from '../weapons/molotov';
import { WeaponNapalmStrike, NapalmDroplet } from '../weapons/napalmStrike';
import { WeaponKamikaze, type Kamikaze } from '../weapons/kamikaze';
import {
  WeaponBungee,
  isBungeeActive,
  releaseBungee,
  getBungeeRenderData,
} from '../weapons/bungee';
import type { GameWorld } from '../weapons/types';
import type { Entity } from '../entities/entity';
import {
  createPixiApp,
  createRenderLayers,
  createSpriteFromCanvas,
  type RenderLayers,
} from '../render/pixiApp';
import { Camera } from '../render/camera';

import { AnimatedWormSprite } from '../render/animatedSprite';
import { getTankSpriteConfig } from '../render/tankSprites';
import { getTankType } from './tankTypes';
import { ParticleSystem } from '../render/particles';
import { SkyBackground } from '../render/sky';
import { GameOverScreen } from '../ui/gameOverScreen';
import { WeaponPanel, type WeaponSlot } from '../ui/weaponPanel';
import { WindIndicator } from '../ui/windIndicator';
import { WeatherIndicator } from '../ui/weatherIndicator';
import { ALLIANCE_COLORS } from '../network/room';
import { TeamHealthSummary } from '../ui/teamHealthSummary';
import { FloatingDamageManager } from '../ui/floatingDamage';
import { CommentaryManager } from '../ui/commentary';
import { VirtualGamepad } from '../ui/virtualGamepad';
import { isMobileDevice } from '../ui/mobileDetect';
import { PauseMenu } from '../ui/pauseMenu';
import { NetworkOSD } from '../ui/networkOSD';
import { PowerBar } from '../ui/powerBar';
import { ItemBar } from '../ui/itemBar';
import { COLORS, textStyle } from '../ui/theme';
import { ensureFontsReady } from '../ui/styles/designSystem';
import { KillFeed } from '../ui/killFeed';
import { StatsPanel } from '../ui/statsPanel';
import { SpectatorHUD } from '../ui/spectatorHUD';
import { soundManager } from '../audio/soundManager';
import { ResyncManager } from '../network/resync';
import { generateCrosshairSprite } from '../assets/manifest';
import { applyDelta } from '../network/deltaSync';

import type { GameSession } from '../network/gameSession';
import type { NetworkManager } from '../network/networkManager';
import { analytics } from '../utils/analytics';

import type { PlayerAction } from '../network/protocol';
import type {
  GameStateSnapshot,
  GameStateMessage,
  WormSync,
  ProjectileSync,
  MineSync,
  BarrelSync,
  CrateSync,
  NapalmDropletSync,
  HomingMissileSync,
  GraveSync as NetGraveSync,
  FireSync as NetFireSync,
  GameEvent,
} from '../network/gameStateSync';
import { type GameSettings, createGameSettings } from './gameSettings';
import { MATCH_ITEMS, useMatchItem } from '../logic/matchItems';
import { getWeaponDelay } from '../weapons/types';
import { AimStepper } from '../sim/aimStepper';
import {
  WeatherSystem,
  WEATHER_ZONE_WIDTH,
  WEATHER_ZONE_TOP,
  WEATHER_LABELS,
  WEATHER_ACTIVE_MESSAGES,
  WEATHER_COLORS,
  traceThorZap,
  THOR_ALTITUDE,
  THOR_HOME_X,
  THOR_ZAP_RADIUS,
  THOR_ZAP_DAMAGE,
  type WeatherEvent,
} from '../sim/weather';
import type { LastShotInfo, DualShotRequest } from './weaponExecutor';
import {
  type SerializedGameState,
  parseSerializedState,
  decodeTerrainData,
  triggerDownload,
  generateExportFilename,
} from '../testing/stateSerializer';
import {
  createSerializedState as createSerializedStateImpl,
  restoreExtendedEntities as restoreExtendedEntitiesImpl,
  type SerializationContext,
} from './gameStateSerializer';

export interface GameConfig {
  container: HTMLElement;
  teams: TeamConfig[];
  session?: GameSession;
  networkManager?: NetworkManager;
  isHost?: boolean;
  terrainSeed?: number;
  settings?: GameSettings;
  onQuitToLobby?: () => void;
}

type InputState = {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  jump: boolean;
  fire: boolean;
  charging: boolean;
};

export class Game implements GameWorld {
  private app!: Application;
  private gameLoop!: GameLoop;
  private terrain!: TerrainCollisionMask;
  private terrainCanvas!: HTMLCanvasElement;
  private state!: GameState;
  private layers!: RenderLayers;
  private camera!: Camera;
  private worldContainer!: Container;
  private screenHudContainer!: Container;

  private trackedSuperSheep: SuperSheep | null = null;
  private wormSprites = new Map<string, AnimatedWormSprite>();
  private wormHealthBars = new Map<string, Container>();
  private projectileSprites = new Map<string, Sprite | Graphics>();
  private projectileTrailFrame = 0;
  private projectileSeen = new Set<string>();
  private mineSprites = new Map<string, Sprite | Graphics>();
  private sheepSprites = new Map<string, Sprite | Graphics>();
  private raonSprites = new Map<string, Sprite | Graphics>();
  private superSheepSprites = new Map<string, Container>();
  private kamikazeSprites = new Map<string, Container>();
  private napalmDropletSprites = new Map<string, Graphics>();
  private fireSprites = new Map<string, Graphics>();
  private homingSprites = new Map<string, Container>();
  private graveSprites = new Map<string, Container>();
  private crateSprites = new Map<string, Container>();
  private barrelSprites = new Map<string, Container>();
  private terrainSprite!: Sprite;

  private targetingCursor!: Graphics;
  private lockedTargetIndicator!: Graphics;
  private girderAngleIndex = 0;
  private girderSizeIndex = 1;
  private girderPreviewGraphics!: Graphics;

  private cameraMode:
    | 'worm'
    | 'projectile'
    | 'manual'
    | 'thrown'
    | 'crate'
    | 'homing'
    | 'resolution'
    | 'sheep'
    | 'supersheep'
    | 'raon' = 'worm';
  private trackedProjectile: Projectile | null = null;
  private trackedHomingMissile: HomingMissile | null = null;
  private thrownWorm: Worm | null = null;
  private trackedCrate: Crate | null = null;
  private resolvingWorm: Worm | null = null;
  private trackedSheep: Sheep | null = null;
  private trackedRaon: MiniRaon | null = null;
  private waitingForCrates = false;
  private isDraggingCamera = false;
  private terrainTheme = 'dirt';
  private lastDragPos = { x: 0, y: 0 };

  private input: InputState = {
    left: false,
    right: false,
    up: false,
    down: false,
    jump: false,
    fire: false,
    charging: false,
  };

  private cameraPanInput = {
    left: false,
    right: false,
    up: false,
    down: false,
  };

  private lastJumpTime: number = 0;
  private pendingJump: boolean = false;
  private readonly DOUBLE_TAP_THRESHOLD = 250;
  private weaponJustSelected: boolean = false;

  private turnManager!: TurnManager;
  private cameraController!: CameraController;
  private eventBus: EventBus;
  private entityRegistry: EntityRegistry;
  private targetingSystem: TargetingSystem;
  private weaponSelection: WeaponSelectionManager;
  private chargeState: ChargeStateManager;
  private toolSystem: ToolSystemManager;
  private inputManager: InputManager;
  private entityFactory!: EntityFactory;
  private weaponExecutor!: WeaponExecutor;
  private explosionManager!: ExplosionManager;
  private worldStabilityChecker!: WorldStabilityCheckerImpl;
  private hazardSpawner!: HazardSpawner;
  private jetpackManager!: JetpackManager;
  private suddenDeathManager!: SuddenDeathManager;
  private crateManager!: CrateManager;
  private physicsStep!: PhysicsStep;
  private simulation!: Simulation;
  private weatherSystem!: WeatherSystem;
  private weatherGraphics!: Graphics;
  private lastWeatherActiveKey = '';
  private lastWeatherNextKey = '';
  /** Last seen wind vector (any change triggers the wind gust sfx). */
  private lastWindSfx: { x: number; y: number } | null = null;
  private cameraTransitionDelay = 0;

  private terrainDirty = false;

  private pendingSheepTurnEnd = false;
  private lastTurnWormId: string | null = null;

  private crosshairSprite!: Sprite;
  private chargeBarGraphics!: Graphics;
  private hudContainer!: Container;
  private hudStatusBg!: Graphics;
  private flashOverlay!: Graphics;
  private flashAlpha = 0;
  private hudStatusSize = { w: 0, h: 0 };
  private weaponText!: Sprite;
  private timerText!: Sprite;
  private spriteManager = new SpriteManager();
  private ropeGraphics!: Graphics;

  private particles!: ParticleSystem;
  private sky!: SkyBackground;
  private gameOverScreen!: GameOverScreen;
  private pauseMenu!: PauseMenu;
  private pauseButton!: Container;
  private weaponPanel!: WeaponPanel;
  private windIndicator!: WindIndicator;
  private weatherIndicator!: WeatherIndicator;
  private teamHealthSummary!: TeamHealthSummary;
  private floatingDamage!: FloatingDamageManager;
  private commentary!: CommentaryManager;
  private virtualGamepad: VirtualGamepad | null = null;
  private networkOSD: NetworkOSD | null = null;
  private resyncManager: ResyncManager | null = null;
  private killFeed: KillFeed | null = null;
  private statsPanel: StatsPanel | null = null;
  private spectatorHUD: SpectatorHUD | null = null;
  private lastAttackingTeamId: string | null = null;
  private lastAttackingWeaponName: string = '';
  private session: GameSession | null = null;
  private networkManager: NetworkManager | null = null;
  private _terrainSeed: number = 0;
  private settings: GameSettings;
  private uiSettings = {
    showDamageNumbers: true,
    cameraShake: true,
    showNetworkOSD: localStorage.getItem('vvorms_show_network_osd') === '1',
  };
  private gameTick: number = 0;
  private gameStartTime: number = 0;
  private networkStateBuffer: Array<{
    state: GameStateSnapshot;
    recvTime: number;
  }> = [];
  private lastAppliedNetSnapshot: {
    state: GameStateSnapshot;
    recvTime: number;
  } | null = null;
  /** De-jitter state: last snapshot's tick + smoothed arrival time. */
  private lastNetArrival: { tick: number; recvTime: number } | null = null;
  /** Snapshots render this far in the past so positions can be interpolated. */
  private readonly INTERP_DELAY_MS = 110;
  /** Online: the worm we control is predicted locally and reconciled to the server. */
  private predictedWormId: string | null = null;
  private predictedWormServerPos: { x: number; y: number } | null = null;
  /** The server's view of the predicted worm stopped moving between snapshots. */
  private predictedWormServerSettled = false;
  private lastSentAim: { facing: 'left' | 'right'; angle: number } | null = null;
  private clientAimStepper = new AimStepper();
  /** Pre-aiming the own worm while an opponent acts (off-turn W/S). */
  private offTurnAimStepper = new AimStepper();
  /** Team id the weapon panel's membership is currently built for, so the
   *  render loop only rebuilds it when the controlled team changes. */
  private lastPanelTeamId: string | null = null;
  private hudResizeObserver: ResizeObserver | null = null;
  /** Own last shot memory (online), keyed by worm id: private, never synced. */
  private myLastShotByWorm = new Map<string, LastShotInfo>();
  /** The local player's most recent shot (angle + power), shown on the power
   *  bar at all times - including other players' turns - and only replaced
   *  when they fire their next shot. */
  private myLastShot: LastShotInfo | null = null;
  /** Active player's weapon, mirrored from snapshots (display for others). */
  private syncedSelectedWeaponId: string | null = null;
  /** Turn sequence whose remembered weapon was already applied locally. */
  private weaponSyncedTurnSeq = -1;
  private aimAngleText!: Text;
  /** Angle readout above the own worm while pre-aiming off turn. */
  private offTurnAimText!: Text;
  private aimArcGraphics!: Graphics;
  /** Active drag shot power (GunBound-style fire input). Aim comes from the worm. */
  private dragShot: { power: number } | null = null;
  /** Free-drag anchor (clientX): power scales with horizontal drag distance. */
  private dragShotOrigin: number | null = null;
  /** Press on the power bar: quick taps place the goal marker, drags fire. */
  private barPress: {
    x: number;
    at: number;
    power: number;
    moved: boolean;
  } | null = null;
  private dragShotGraphics!: Graphics;
  private powerBar: PowerBar | null = null;
  private itemBar: ItemBar | null = null;
  /** How many entries of the server's terrain modification log we've replayed. */
  private appliedTerrainVersion = 0;
  private stateSeqCounter: number = 0;
  private highestAppliedStateSeq: number = 0;
  private baselineSnapshot: GameStateSnapshot | null = null;

  private currentWaterLevel: number = WA_PHYSICS.WORLD.WATER_LEVEL;
  private waterGraphics!: Graphics;
  private wormHurtFlashTimers = new Map<string, number>();

  constructor(private config: GameConfig) {
    this.session = config.session || null;
    this.networkManager = config.networkManager || null;
    this._terrainSeed = config.terrainSeed ?? getGameRNG().nextInt(0, 1000000);
    // Normalize: fills defaults (incl. mode config) and deep-copies.
    this.settings = createGameSettings(config.settings);
    this.pauseMenu = new PauseMenu(this, 1024, 768);
    this.eventBus = new EventBus();
    this.entityRegistry = new EntityRegistry(this.eventBus);
    this.targetingSystem = new TargetingSystem(this.eventBus);
    this.weaponSelection = new WeaponSelectionManager(this.eventBus);
    this.chargeState = new ChargeStateManager(this.eventBus);
    this.chargeState.setChargeRate(this.settings.mode.fire.chargeRate);
    this.toolSystem = new ToolSystemManager(this.eventBus);
    this.inputManager = new InputManager(this.eventBus);
  }

  get terrainSeed(): number {
    return this._terrainSeed;
  }

  async init(): Promise<void> {
    this.app = await createPixiApp(
      this.config.container,
      WA_PHYSICS.WORLD.WIDTH,
      WA_PHYSICS.WORLD.HEIGHT,
    );

    // Gradient sky sits at the very back (screen-space canvas Sprite; theme-set
    // after the terrain is generated below).
    this.sky = new SkyBackground();
    this.app.stage.addChild(this.sky.container);
    this.sky.resize(this.app.screen.width, this.app.screen.height);

    this.worldContainer = new Container();
    this.worldContainer.label = 'world';
    this.app.stage.addChild(this.worldContainer);

    this.screenHudContainer = new Container();
    this.screenHudContainer.label = 'screenHud';
    this.app.stage.addChild(this.screenHudContainer);

    this.layers = createRenderLayers(this.worldContainer);

    const padding = WA_PHYSICS.WORLD.CAMERA_PADDING;
    this.layers.terrain.position.x = padding;
    this.layers.entities.position.x = padding;
    this.layers.projectiles.position.x = padding;
    this.layers.effects.position.x = padding;
    this.layers.ui.position.x = padding;

    const viewWidth = this.app.screen.width;
    const viewHeight = this.app.screen.height;
    console.log('Viewport:', viewWidth, 'x', viewHeight);
    console.log('World:', WA_PHYSICS.WORLD.WIDTH, 'x', WA_PHYSICS.WORLD.HEIGHT);

    const cameraWorldWidth = WA_PHYSICS.WORLD.WIDTH + WA_PHYSICS.WORLD.CAMERA_PADDING * 2;
    this.camera = new Camera(
      this.worldContainer,
      viewWidth,
      viewHeight,
      cameraWorldWidth,
      WA_PHYSICS.WORLD.HEIGHT,
    );
    this.cameraController = new CameraController(this.camera);

    await this.loadSprites();
    await this.initSound();
    // Rasterise the HUD fonts before any Pixi text is measured/drawn so the
    // first frame uses the Fusion type, not a system fallback.
    await ensureFontsReady();

    const terrainOptions: TerrainOptions = {
      girders: this.settings.terrain.girders,
      style: this.settings.terrain.style,
      theme: this.settings.terrain.theme,
      complexity: this.settings.terrain.complexity,
      floatingIslands: this.settings.terrain.floatingIslands,
      bridges: this.settings.terrain.bridges,
      pillars: this.settings.terrain.pillars,
      caves: this.settings.terrain.caves,
    };
    // Authoritative collision mask (headless, deterministic), then visual replay.
    const generatedTerrain = generateTerrainMask(this._terrainSeed, terrainOptions);
    this.terrainCanvas = createTerrainCanvas();
    this.terrainTheme = renderTerrainVisuals(
      this.terrainCanvas,
      this._terrainSeed,
      terrainOptions,
      generatedTerrain.attempts,
    );
    this.sky.setTheme(this.terrainTheme as Parameters<SkyBackground['setTheme']>[0]);
    this.terrain = new TerrainCollisionMask(this.terrainCanvas, generatedTerrain.mask.getAlpha());

    this.terrainSprite = createSpriteFromCanvas(this.terrainCanvas);
    this.layers.terrain.addChild(this.terrainSprite);

    this.createExtendedWaterBackground();

    weaponRegistry.register(WeaponGrenade);
    weaponRegistry.register(WeaponGrub);
    weaponRegistry.register(WeaponGrubSS);
    weaponRegistry.register(WeaponAduka);
    weaponRegistry.register(WeaponNak);
    weaponRegistry.register(WeaponAdukaSS);
    weaponRegistry.register(WeaponLightningT1);
    weaponRegistry.register(WeaponLightningT2);
    weaponRegistry.register(WeaponKnight);
    weaponRegistry.register(WeaponTrico);
    weaponRegistry.register(WeaponKalsiddon);
    weaponRegistry.register(WeaponFrogLeft);
    weaponRegistry.register(WeaponFrogRight);
    weaponRegistry.register(WeaponRaon);
    weaponRegistry.register(WeaponJdT2);
    weaponRegistry.register(WeaponJdSS);
    weaponRegistry.register(WeaponBazooka);
    weaponRegistry.register(WeaponDynamite);
    weaponRegistry.register(WeaponClusterBomb);
    weaponRegistry.register(WeaponShotgun);
    weaponRegistry.register(WeaponNinjaRope);
    weaponRegistry.register(WeaponAirstrike);
    weaponRegistry.register(WeaponHomingMissile);
    weaponRegistry.register(WeaponMine);
    weaponRegistry.register(WeaponTeleport);
    weaponRegistry.register(WeaponParachute);
    weaponRegistry.register(WeaponFirePunch);
    weaponRegistry.register(WeaponBaseballBat);
    weaponRegistry.register(WeaponBlowtorch);
    weaponRegistry.register(WeaponDrill);
    weaponRegistry.register(WeaponHolyHandGrenade);
    weaponRegistry.register(WeaponSheep);
    weaponRegistry.register(WeaponBananaBomb);
    weaponRegistry.register(WeaponMortar);
    weaponRegistry.register(WeaponMolotov);
    weaponRegistry.register(WeaponNapalmStrike);
    weaponRegistry.register(WeaponKamikaze);
    weaponRegistry.register(WeaponBungee);
    weaponRegistry.register(WeaponJetpack);
    weaponRegistry.register(WeaponGirder);
    weaponRegistry.register(WeaponSuperSheep);
    this.weaponSelection.select('grenade');

    this.state = new GameState();
    this.state.configureMode({
      windModel: this.settings.mode.wind.model,
      windMax: this.settings.mode.wind.maxStrength,
      turnTime: this.settings.turn.turnTime,
      retreatTime: this.settings.turn.retreatTime,
      movementBudgetPx: this.settings.mode.worm.movementBudgetPx,
      lockMovementAfterFire: this.settings.mode.worm.lockMovementAfterFire,
      turnOrder: this.settings.mode.turnOrder.kind,
      delayPerSecond: this.settings.mode.turnOrder.delayPerSecond,
      trackDelays: this.settings.mode.turnOrder.trackDelays,
    });
    this.weatherSystem = new WeatherSystem(
      this.settings.mode.weather.enabled ? this.settings.mode.weather.types : [],
      this.settings.mode.weather.intervalTurns,
      () => this.state.rerollWind(),
    );
    this.worldStabilityChecker = new WorldStabilityCheckerImpl({
      entityRegistry: this.entityRegistry,
      getWorms: () => this.getWorms(),
      terrain: this.terrain,
      networkManager: undefined,
      turnManager: {
        clearDamageQueue: () => this.turnManager.clearDamageQueue(),
        resetForceStableAttempts: () => this.worldStabilityChecker.resetForceStableAttempts(),
      },
      state: {
        finishDamageResolution: () => this.state.finishDamageResolution(),
      },
      setCameraMode: (mode: string) => {
        this.cameraMode = mode as any;
      },
    });
    this.turnManager = new TurnManager(this.state, this.worldStabilityChecker, {
      onStartResolution: () => {
        this.cameraMode = 'resolution';
        this.resolvingWorm = null;
        this.eventBus.emit('turn:phaseChanged', {
          from: this.simulation.getLastPhase() as 'playing' | 'retreat',
          to: 'damage_resolution',
        });
      },
      onWormDamageResolved: (worm, damage, died) => {
        this.cameraMode = 'resolution';
        this.resolvingWorm = worm;
        this.cameraTransitionDelay = 0;

        if (damage > 0) {
          soundManager.playDamage();
          if (this.uiSettings.showDamageNumbers) {
            this.floatingDamage.spawn(
              worm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
              worm.position.y,
              damage,
            );
          }

          if (this.statsPanel && this.lastAttackingTeamId) {
            const attackingTeam = this.state.teams.find((t) => t.id === this.lastAttackingTeamId);
            if (attackingTeam) {
              this.statsPanel.recordDamage(attackingTeam.id, attackingTeam.name, damage);
            }
          }
        }

        if (died) {
          soundManager.playDeath();
          try {
            this.commentary.showDeath(worm.name);
          } catch {}

          const victimTeam = this.state.teams.find((t) => t.id === worm.teamId);
          const attackingTeam = this.lastAttackingTeamId
            ? this.state.teams.find((t) => t.id === this.lastAttackingTeamId)
            : null;

          if (this.statsPanel && victimTeam) {
            this.statsPanel.recordDeath(victimTeam.id, victimTeam.name);
            if (attackingTeam) {
              this.statsPanel.recordKill(attackingTeam.id, attackingTeam.name);
            }
          }

          if (this.killFeed) {
            const killerName = attackingTeam?.name ?? 'Environment';
            const weaponName = this.lastAttackingWeaponName || 'Unknown';
            this.killFeed.addKill(killerName, worm.name, weaponName);
          }

          this.eventBus.emit('worm:died', {
            wormId: worm.id,
            teamId: worm.teamId,
            position: { x: worm.position.x, y: worm.position.y },
            cause: 'damage',
          });
        }

        this.eventBus.emit('worm:damageResolved', {
          wormId: worm.id,
          teamId: worm.teamId,
          damage,
          died,
        });
      },
      onResolutionComplete: () => {
        this.cameraMode = 'worm';
        this.eventBus.emit('turn:phaseChanged', {
          from: 'damage_resolution',
          to: 'between_turns',
        });
      },
    });
    this.setupTeams();
    this.initEntityFactory();
    this.initWeaponExecutor();
    this.initExplosionManager();
    this.initHazardSpawner();
    this.initJetpackManager();
    this.initSuddenDeathManager();
    this.initCrateManager();
    this.initSimulation();

    this.createCrosshair();
    this.createHUD();
    this.createRopeGraphics();
    this.particles = new ParticleSystem(this.layers.effects);
    this.gameOverScreen = new GameOverScreen(this.app.screen.width, this.app.screen.height);
    this.gameOverScreen.setOnRestart(() => this.restartGame());
    this.gameOverScreen.setOnQuit(() => this.handleQuitToLobby());
    this.screenHudContainer.addChild(this.gameOverScreen.container);

    const initialSettings = {
      masterVolume: 0.5,
      voiceVolume: 0.5,
      soundEnabled: true,
      showDamageNumbers: true,
      cameraShake: true,
    };
    this.pauseMenu = new PauseMenu(
      this,
      this.app.screen.width,
      this.app.screen.height,
      initialSettings,
      () => this.handleQuitToLobby(),
    );
    this.app.stage.addChild(this.pauseMenu.container);
    if (this.pauseMenu.settingsMenu) {
      this.app.stage.addChild(this.pauseMenu.settingsMenu.container);
    }

    if (this.networkManager) {
      this.networkOSD = new NetworkOSD(this, this.app.screen.width, this.app.screen.height);
      this.networkOSD.setNetworkManager(this.networkManager);
      this.app.stage.addChild(this.networkOSD.container);
      if (this.uiSettings.showNetworkOSD) {
        this.networkOSD.show();
      }

      this.resyncManager = new ResyncManager(this.app.screen.width, this.app.screen.height);
      this.app.stage.addChild(this.resyncManager.getOverlay());
      this.resyncManager.setResyncRequestCallback(() => {
        this.networkManager?.requestKeyframe();
      });
      this.resyncManager.setResyncCompleteCallback(() => {
        const lastState = this.resyncManager?.getLastReceivedState();
        if (lastState) {
          if (lastState.stateSeq <= this.highestAppliedStateSeq) {
            return;
          }
          this.highestAppliedStateSeq = lastState.stateSeq;
          this.applyStateDirectly(lastState);
          this.lastAppliedNetSnapshot = {
            state: lastState,
            recvTime: performance.now(),
          };
        }
      });
    }

    this.createPauseButton();

    this.setupInput();
    await this.createWormSprites();

    this.gameLoop = new GameLoop(
      (dt) => this.update(dt),
      (interp) => this.render(interp),
    );

    this.state.startGame();
    this.hazardSpawner.spawnInitialHazards();
    // Initial weapon for the starting team's tank, so a wizard (no grenade)
    // never opens on an unusable default slot.
    const startTeam = this.state.getCurrentTeam();
    if (startTeam) {
      this.refreshWeaponPanelForTeam(startTeam);
      let startWeapon = startTeam.getCurrentWorm()?.lastWeaponId || 'grenade';
      if (!startTeam.hasAmmo(startWeapon)) {
        startWeapon = this.firstUsableWeaponId(startTeam) ?? startWeapon;
      }
      this.selectWeapon(startWeapon);
    }
    this.focusCurrentWorm();
    this.setupNetworkHandlers();
    this.forceUpdateAllSprites();
    this.setupResizeHandler();
    this.setupGameLogger();
  }

  private createPauseButton(): void {
    this.pauseButton = new Container();
    this.pauseButton.label = 'pauseButton';

    const buttonSize = 44;
    const bg = new Graphics();
    bg.roundRect(0, 0, buttonSize, buttonSize, 10).fill({
      color: COLORS.bg,
      alpha: 0.78,
    });
    bg.roundRect(2, 2, buttonSize - 4, buttonSize * 0.4, 8).fill({
      color: COLORS.white,
      alpha: 0.12,
    });
    bg.roundRect(0, 0, buttonSize, buttonSize, 10).stroke({
      width: 2,
      color: COLORS.bevelLight,
      alpha: 0.8,
    });

    const icon = new Graphics();
    const barWidth = 5;
    const barHeight = 18;
    const gap = 6;
    const ix = (buttonSize - barWidth * 2 - gap) / 2;
    const iy = (buttonSize - barHeight) / 2;
    icon.roundRect(ix, iy, barWidth, barHeight, 2).fill({ color: COLORS.white });
    icon.roundRect(ix + barWidth + gap, iy, barWidth, barHeight, 2).fill({ color: COLORS.white });

    this.pauseButton.addChild(bg);
    this.pauseButton.addChild(icon);

    this.pauseButton.position.set(10, 10);
    this.pauseButton.eventMode = 'static';
    this.pauseButton.cursor = 'pointer';

    this.pauseButton.on('pointerdown', () => {
      if (this.pauseMenu.isVisible()) {
        this.pauseMenu.hide();
      } else {
        this.pauseMenu.show();
      }
    });

    this.screenHudContainer.addChild(this.pauseButton);
  }

  private setupResizeHandler(): void {
    const onResize = () => this.layoutHud();

    window.addEventListener('resize', onResize);
    // The visual viewport resizes without a window resize when the mobile
    // URL bar shows/hides.
    window.visualViewport?.addEventListener('resize', onResize);
    // Orientation changes settle late on some mobile browsers (the first
    // resize event still reports the old dimensions): re-run the layout a
    // few times until the final size sticks.
    window.addEventListener('orientationchange', () => {
      for (const delay of [50, 250, 600]) {
        setTimeout(onResize, delay);
      }
    });
    const canvasParent = this.app.canvas?.parentElement;
    if (typeof ResizeObserver !== 'undefined' && canvasParent) {
      this.hudResizeObserver = new ResizeObserver(onResize);
      this.hudResizeObserver.observe(canvasParent);
    }
    onResize();
  }

  /**
   * Position all screen-anchored HUD (GunBound-style chrome): wind indicator
   * top-center, team health top-right, weapon/turn text by the pause button,
   * power bar pinned to the bottom strip.
   */
  private layoutHud(): void {
    // resizeTo defers the renderer resize to the next frame; force it now so
    // the HUD lays out against fresh dimensions (rotating the phone
    // otherwise leaves the bottom bar outside the viewport).
    this.app.resize();
    const width = this.app.screen.width;
    const height = this.app.screen.height;
    const portrait = width < 700;
    this.camera.resize(width, height);

    this.windIndicator.container.position.set((width - 200) / 2, 10);
    // Sit the weather badge just below the wind compass/bar (which extends to
    // ~y=72 in compass mode), not on top of it.
    this.weatherIndicator.container.position.set(width / 2, 80);
    // Compact summary on portrait so it never collides with the status panel
    // across the narrow top strip; keep a safe-zone margin from the edge.
    this.teamHealthSummary.setCompact(portrait);
    this.teamHealthSummary.container.position.set(
      width - this.teamHealthSummary.width - 12,
      portrait ? 46 : 4,
    );

    // Weapon/turn status lives below the pause button (it used to share
    // the top-left corner with it and the wind/health chrome).
    const weaponText = this.weaponText as unknown as Text;
    const timerText = this.timerText as unknown as Text;
    if (portrait) {
      weaponText.style.fontSize = 14;
      weaponText.style.wordWrap = true;
      weaponText.style.wordWrapWidth = Math.max(140, width - 230);
      weaponText.position.set(16, 62);
      timerText.style.fontSize = 17;
      timerText.position.set(16, 62 + (weaponText as unknown as Text).height + 8);
    } else {
      weaponText.style.fontSize = 15;
      weaponText.style.wordWrap = false;
      weaponText.position.set(16, 62);
      timerText.style.fontSize = 18;
      timerText.position.set(16, 90);
    }
    this.layoutStatusPanel();

    // Mobile reserves the bottom-left corner for the gamepad's FUSE/weapon
    // buttons (two 44px buttons + margins), GunBound bottom-panel style.
    const reservedLeft = this.virtualGamepad ? 8 + 44 + 6 + 44 : 0;
    this.powerBar?.layout(reservedLeft, width, height);
    this.sky?.resize(width, height);
    this.flashOverlay.clear();
    this.flashOverlay.rect(0, 0, width, height).fill({ color: 0xffffff });
    this.itemBar?.resize(width, height);
    this.commentary?.setScreenSize(width);
    this.gameOverScreen.resize(width, height);
  }

  private async loadSprites(): Promise<void> {
    await this.spriteManager.loadSprites();
  }

  private async initSound(): Promise<void> {
    try {
      await soundManager.init();
      console.log('Sound system initialized');
    } catch (e) {
      console.warn('Failed to initialize sound:', e);
    }
  }

  private setupTeams(): void {
    const wormsPerTeam = Math.min(4, Math.max(1, this.settings.worm.wormsPerTeam ?? 4));
    const teamConfigs = this.config.teams.map((t) => ({
      ...t,
      wormNames: t.wormNames.slice(0, wormsPerTeam),
    }));
    const totalWorms = teamConfigs.reduce((sum, t) => sum + t.wormNames.length, 0);
    const isCavern = this.settings.terrain.style === 'cavern';
    const spawnPoints = findSpawnPoints(
      (x, y) => this.terrain.isSolid(x, y),
      this.terrain.getWidth(),
      this.terrain.getHeight(),
      totalWorms,
      isCavern,
    );

    const shuffledSpawns = getGameRNG().shuffle(spawnPoints);

    let spawnIndex = 0;
    for (const teamConfig of teamConfigs) {
      const teamSpawns = shuffledSpawns.slice(spawnIndex, spawnIndex + teamConfig.wormNames.length);
      const team = createTeam(teamConfig, teamSpawns, {
        initialHealth: this.settings.worm.initialHealth,
        aimLimits: {
          min: this.settings.mode.aim.minAngle,
          max: this.settings.mode.aim.maxAngle,
        },
        aimRelativeToGround: this.settings.mode.aim.relativeToGround ?? false,
        applyTankType:
          (this.settings.mode.tankSelection ?? false) || (this.settings.mode.randomTank ?? false),
      });
      if (this.settings.worm.infiniteAmmo) {
        team.setInfiniteAmmo();
      } else if (this.settings.mode.items.whitelist) {
        // Legacy per-mode whitelist (only when tank selection is off).
        team.restrictInventory(this.settings.mode.items.whitelist);
      }
      team.setMatchItems(this.settings.mode.items.matchItems);
      this.state.addTeam(team);
      spawnIndex += teamConfig.wormNames.length;
    }
  }

  private initEntityFactory(): void {
    const callbacks: EntityFactoryCallbacks = {
      createExplosion: (
        x,
        y,
        radius,
        damage,
        skipTerrain?,
        directHitWorm?,
        pushMultiplier?,
        magnet?,
      ) => {
        this.createExplosion(
          x,
          y,
          radius,
          damage,
          false,
          directHitWorm,
          pushMultiplier,
          skipTerrain ?? false,
          magnet,
        );
      },
      spawnFire: (x, y, count) => this.spawnFire(x, y, count),
      spawnFireAt: (x, y) => this.spawnFireAt(x, y),
      playSound: (soundId) => soundManager.play(soundId as Parameters<typeof soundManager.play>[0]),
      stopSound: (soundId) => soundManager.stop(soundId as Parameters<typeof soundManager.stop>[0]),
      handleCrateCollection: (crate, wormId) =>
        this.crateManager.handleCrateCollection(crate, wormId),
      destroyTerrain: (x, y, radius) => {
        this.destroyTerrainCircle(x, y, radius);
        this.terrainDirty = true;
      },
      onWeaponFired: (endTurn) => {
        this.state.onWeaponFired(endTurn);
      },
      showFloatingDamage: (x, y, damage) => {
        if (this.uiSettings.showDamageNumbers) {
          this.floatingDamage.spawn(x, y, damage);
        }
      },
      getTeamColor: (teamId) => {
        const team = this.state.teams.find((t) => t.id === teamId);
        return team?.color ?? 0xffffff;
      },
      updateWeaponPanelAmmo: (team) => {
        if (team instanceof Team) {
          this.updateWeaponPanelAmmo(team);
        }
      },
      findWormById: (id) => this.findWormById(id) ?? undefined,
      getCurrentWaterLevel: () => this.currentWaterLevel,
      isSolid: (x, y) => this.terrain.isSolid(x, y),
      onProjectileExploded: (x, y, type, ownerId) => {
        // Authority only: online clients receive thorZap events instead.
        if (!this.networkManager) {
          if (type === 'lightningT1' || type === 'lightningT2') {
            this.queueLightning(x, y, type);
          } else if (type === 'knight') {
            this.queueKnight(x, y, ownerId);
          } else {
            // Aduka tracers always call Thor down; otherwise only during a
            // Thor weather event.
            this.queueThorZap(x, y, type === 'aduka');
          }
        }
      },
    };

    const deps: EntityFactoryDependencies = {
      entityRegistry: this.entityRegistry,
      eventBus: this.eventBus,
    };

    this.entityFactory = new EntityFactory(deps, callbacks);
  }

  // ----- Thor weather: zaps at projectile impact points (offline authority) -----

  private thorZapFx: Array<{
    fromX: number;
    fromY: number;
    x: number;
    y: number;
    color: number;
    until: number;
  }> = [];

  private queueThorZap(targetX: number, targetY: number, force = false): void {
    // Aduka (force) always zaps; otherwise only during a Thor weather event.
    if (!force && this.weatherSystem.getActive()?.type !== 'thor') return;
    // Each tracer calls its OWN strike right on impact (short beam delay), so
    // the three strikes land in rhythm with the three hits. Aduka strikes
    // crater terrain too (like weather Thor). Always from the permanent sat.
    const delayMs = WA_PHYSICS.ADUKA.THOR_STRIKE_DELAY_MS;
    setTimeout(
      () =>
        this.fireZap(
          THOR_HOME_X,
          THOR_ALTITUDE,
          targetX,
          targetY,
          THOR_ZAP_RADIUS,
          THOR_ZAP_DAMAGE,
        ),
      delayMs,
    );
  }

  /**
   * Lightning bolt(s) from directly above the impact (offline authority). T1 =
   * one vertical bolt; T2 = a V of two bolts splaying onto points either side.
   */
  private queueLightning(x: number, y: number, type: 'lightningT1' | 'lightningT2'): void {
    const L = WA_PHYSICS.LIGHTNING;
    // T1: one vertical bolt from directly above. T2: two bolts whose ORIGINS
    // are up-and-out by V_REACH on each side (rise == run -> ~45deg arms) that
    // converge INTO the impact point, forming a wide "V" from farther away.
    const origins =
      type === 'lightningT2'
        ? [
            { ox: x - L.V_REACH, oy: Math.max(0, y - L.V_REACH) },
            { ox: x + L.V_REACH, oy: Math.max(0, y - L.V_REACH) },
          ]
        : [{ ox: x, oy: Math.max(0, y - L.BOLT_ALTITUDE) }];
    for (const { ox, oy } of origins) {
      setTimeout(
        () => this.fireZap(ox, oy, x, y, L.BOLT_RADIUS, L.BOLT_DAMAGE, false, 0x66e0ff),
        L.STRIKE_DELAY_MS,
      );
    }
  }

  /**
   * Knight: the satellite hovers directly above the FIRER; each sword is fired
   * from that single point and converges into the tracer's landing point,
   * sequentially (like Aduka), slightly spaced.
   */
  private queueKnight(x: number, y: number, ownerId: string): void {
    const K = WA_PHYSICS.KNIGHT;
    const firer = this.findWormById(ownerId);
    const satX = firer ? firer.position.x + WA_PHYSICS.WORM.WIDTH / 2 : x;
    const satY = Math.max(0, y - K.SATELLITE_ALTITUDE);
    for (let i = 0; i < K.COUNT; i++) {
      setTimeout(
        () => this.fireZap(satX, satY, x, y, K.SWORD_RADIUS, K.SWORD_DAMAGE, false, 0xcfe4ff),
        i * K.STRIKE_INTERVAL_MS,
      );
    }
  }

  private fireZap(
    fromX: number,
    fromY: number,
    targetX: number,
    targetY: number,
    radius: number,
    damage: number,
    skipTerrain = false,
    color = 0xff5555,
  ): void {
    // The zap is blocked by terrain along the way (and blasts into it).
    const hit = traceThorZap(fromX, fromY, targetX, targetY, (x, y) => this.terrain.isSolid(x, y));
    this.showThorZap(fromX, fromY, hit.x, hit.y, color);
    this.createExplosion(hit.x, hit.y, radius, damage, false, undefined, 1, skipTerrain);
  }

  /** Brief zap beam visual from the origin to the strike point. */
  private showThorZap(fromX: number, fromY: number, x: number, y: number, color = 0xff5555): void {
    this.thorZapFx.push({ fromX, fromY, x, y, color, until: performance.now() + 350 });
  }

  private initWeaponExecutor(): void {
    const deps: WeaponExecutorDependencies = {
      eventBus: this.eventBus,
      entityRegistry: this.entityRegistry,
      weaponSelection: this.weaponSelection,
      chargeState: this.chargeState,
      targetingSystem: this.targetingSystem,
      gameState: this.state,
      mode: this.settings.mode,
    };

    const callbacks: WeaponExecutorCallbacks = {
      getWorld: () => this,
      canFireWeapon: () => this.canFireWeapon(),
      isWaitingForCrates: () => this.waitingForCrates,
      canSwitchWeapon: (weaponId) => this.toolSystem.canSwitchWeapon(weaponId),
      addProjectile: (p) => this.addProjectile(p),
      addMine: (m) => this.addMine(m),
      addHomingMissile: (h) => this.addHomingMissile(h),
      addSheep: (s) => this.addSheep(s),
      addRaon: (r) => this.addRaon(r),
      addSuperSheep: (ss) => this.addSuperSheep(ss),
      addKamikaze: (k, w) => this.addKamikaze(k, w),
      addNapalmDroplet: (n) => this.addNapalmDroplet(n),
      placeGirder: (x, y) => this.placeGirder(x, y),
      activateTool: (tool) => this.activateTool(tool),
      activateJetpack: (worm) => this.jetpackManager.activateJetpack(worm),
      selectWeapon: (weaponId) => this.selectWeapon(weaponId),
      setPendingSheepTurnEnd: (value) => {
        this.pendingSheepTurnEnd = value;
      },
      setThrownWorm: (worm) => {
        this.thrownWorm = worm;
      },
      setCameraMode: (mode) => {
        this.cameraMode = mode as typeof this.cameraMode;
      },
      setLastAttackingTeam: (teamId, weaponName) => {
        this.lastAttackingTeamId = teamId;
        this.lastAttackingWeaponName = weaponName;
      },
      updateWeaponPanelAmmo: (team) => {
        if (team instanceof Team) {
          this.updateWeaponPanelAmmo(team);
        }
      },
      exitTargetingMode: () => this.exitTargetingMode(),
      broadcastWeaponFired: () => {},
      playWeaponSound: (weaponId) => this.playWeaponSound(weaponId),
      trackAnalytics: (weaponId) => analytics.weaponFired(weaponId),
      scheduleDualShot: (request) => this.scheduleDualShot(request),
      scheduleEntity: (delayMs, entity) => {
        setTimeout(() => {
          if (this.state.phase !== 'finished') this.addEntity(entity);
        }, delayMs);
      },
    };

    this.weaponExecutor = new WeaponExecutor(deps, callbacks);
  }

  /** Dual item (offline): the identical shot repeats automatically. */
  private scheduleDualShot(request: DualShotRequest): void {
    const facing = request.worm.facing;
    const angleDeg = request.worm.aimAngle;
    setTimeout(() => this.fireDualShot(request, facing, angleDeg), 450);
  }

  private fireDualShot(request: DualShotRequest, facing: 'left' | 'right', angleDeg: number): void {
    const { worm, weapon, power, lockedTarget, target } = request;
    if (this.state.phase !== 'playing') return;
    if (worm.state === 'dead' || worm.hasPendingDamage()) {
      this.state.onWeaponFired(true); // close the turn instead of hanging
      return;
    }
    worm.facing = facing;
    worm.setAimAngle(angleDeg);
    if (lockedTarget) {
      this.targetingSystem.lockTarget(lockedTarget);
    }
    // Dual costs a single ammo: refund the duplicate's consumption.
    this.state.teams.find((t) => t.id === worm.teamId)?.addAmmo(weapon.id, 1);
    const fired = target
      ? this.weaponExecutor.fireWithTarget(worm, target)
      : this.weaponExecutor.executeWeaponFire(worm, weapon, power);
    if (!fired) {
      this.state.onWeaponFired(true);
    }
  }

  private initExplosionManager(): void {
    const deps: ExplosionManagerDependencies = {
      entityRegistry: this.entityRegistry,
      eventBus: this.eventBus,
    };

    const callbacks: ExplosionManagerCallbacks = {
      destroyTerrain: (x, y, radius) => {
        createExplosion(this.terrain, x, y, radius);
      },
      emitParticles: (x, y, radius) => {
        this.particles.emitExplosion(x, y, radius);
        if (this.uiSettings.cameraShake) this.flashScreen(radius);
      },
      playExplosionSound: () => {
        soundManager.playExplosion();
      },
      playDamageSound: () => {
        soundManager.playDamage();
      },
      flashWormHurt: (worm) => {
        this.flashWormHurt(worm);
      },
      updateTerrainTexture: () => {
        this.updateTerrainTexture();
      },
      shakeCamera: (intensity, duration) => {
        this.camera.shake(intensity, duration);
      },
      setThrownWorm: (worm) => {
        this.thrownWorm = worm;
        this.cameraMode = 'thrown';
      },
      checkGameOver: () => {
        const wasFinished = this.state.phase === 'finished';
        this.state.checkGameOver();
        return !wasFinished && this.state.phase === 'finished';
      },
      broadcastExplosion: () => {},
      broadcastGameOver: () => {},
      getTeams: () => this.state.teams,
      // The server is the sole authority in online games.
      isAuthority: () => !this.networkManager,
      shouldShakeCamera: () => this.uiSettings.cameraShake,
      getExplosionPushScale: () => this.settings.mode.fire.explosionPushScale ?? 1,
    };

    this.explosionManager = new ExplosionManager(deps, callbacks);
  }

  private initHazardSpawner(): void {
    this.hazardSpawner = new HazardSpawner({
      settings: {
        oilBarrels: this.settings.hazards.oilBarrels,
        oilBarrelCount: this.settings.hazards.oilBarrelCount,
        prePlacedMines: this.settings.hazards.prePlacedMines,
        prePlacedMineCount: this.settings.hazards.prePlacedMineCount,
      },
      terrain: this.terrain,
      terrainWidth: WA_PHYSICS.WORLD.WIDTH,
      terrainHeight: WA_PHYSICS.WORLD.HEIGHT,
      waterLevel: WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel,
      getWorms: () => this.getWorms(),
      addBarrel: (x, y) => {
        const barrel = new OilBarrel(x, y);
        this.addBarrel(barrel);
      },
      addMine: (x, y) => {
        const mine = new Mine(x, y, 'map');
        mine.armed = true;
        mine.armTimer = mine.armDelay;
        mine.isGrounded = true;
        mine.velocity.x = 0;
        mine.velocity.y = 0;
        this.addMine(mine);
      },
      getRNG: () => getGameRNG(),
    });
  }

  private initJetpackManager(): void {
    this.jetpackManager = new JetpackManager({
      input: this.input,
      terrain: this.terrain,
      playSound: (sound) => soundManager.play(sound),
      stopSound: (sound) => soundManager.stop(sound),
      onTurnEnd: () => {
        this.state.onWeaponFired(true);
      },
    });
  }

  private initSuddenDeathManager(): void {
    this.suddenDeathManager = new SuddenDeathManager({
      settings: this.settings.suddenDeath,
      getTeams: () => this.state.teams,
      getWaterLevel: () => this.currentWaterLevel,
      setWaterLevel: (level) => {
        this.currentWaterLevel = level;
      },
      maxWaterLevel: WA_PHYSICS.WORLD.HEIGHT - 100,
      playSound: (sound) => soundManager.play(sound),
      showCommentary: () => this.commentary.showSuddenDeath(),
      showDamageNumber: (x, y, damage) =>
        this.floatingDamage.spawn(x + WA_PHYSICS.WORM.WIDTH / 2, y, damage),
      updateWaterVisual: () => this.updateWaterVisual(),
      shouldShowDamageNumbers: () => this.uiSettings.showDamageNumbers,
    });
  }

  private initCrateManager(): void {
    this.crateManager = new CrateManager({
      worldWidth: WA_PHYSICS.WORLD.WIDTH,
      crateDropChance: this.settings.worm.crateDropChance,
      weaponOptions: [
        'grenade',
        'bazooka',
        'cluster',
        'dynamite',
        'mine',
        'airstrike',
        'holyHandGrenade',
        'banana',
        'sheep',
        'supersheep',
        'napalmStrike',
      ],
      getEntityRegistry: () => this.entityRegistry,
      getRNG: () => getGameRNG(),
      createCrate: (x, y, type, contents) => new Crate(x, y, type, contents),
      addCrateEntity: (crate) => this.entityFactory.addCrate(crate),
      addCrateSprite: (crate) => {
        const container = this.spriteManager.createCrateSprite(crate.type, true);
        const parachute = container.children.find((c) => c.label === 'parachute');
        if (parachute) {
          parachute.visible = crate.parachuteOpen;
        }
        container.position.set(crate.position.x, crate.position.y);
        this.crateSprites.set(crate.id, container);
        this.layers.entities.addChild(container);
      },
      trackCrate: (crate) => {
        this.trackedCrate = crate;
        this.cameraMode = 'crate';
        this.waitingForCrates = true;
      },
      findWormById: (id) => this.findWormById(id),
      findTeamByWormId: (wormId) => {
        const worm = this.findWormById(wormId);
        return worm ? this.state.teams.find((t) => t.id === worm.teamId) || null : null;
      },
      healWorm: (worm, amount) => {
        worm.health = Math.min(worm.maxHealth, worm.health + amount);
      },
      addWeaponToTeam: (team, weaponId, count) => {
        team.addAmmo(weaponId, count);
      },
      updateWeaponPanel: (team) => this.updateWeaponPanelAmmo(team),
      showDamageNumber: (x, y, damage) =>
        this.floatingDamage.spawn(x + WA_PHYSICS.WORM.WIDTH / 2, y, damage),
      showCommentary: (type) => {
        if (type === 'health') this.commentary.showHealthCrateDrop();
        else if (type === 'weapon') this.commentary.showWeaponCrateDrop();
        else this.commentary.showUtilityCrateDrop();
      },
      showCollectionMessage: (wormName, itemName) => {
        const weapon = weaponRegistry.get(itemName);
        const displayName = weapon ? weapon.name : itemName;
        if (this.killFeed) {
          this.killFeed.addKill(wormName, displayName, '📦 Collected');
        }
      },
      playSound: (sound) => soundManager.play(sound),
      shouldShowDamageNumbers: () => this.uiSettings.showDamageNumbers,
    });
  }

  private initSimulation(): void {
    this.physicsStep = new PhysicsStep(
      {
        state: this.state,
        terrain: this.terrain,
        entityRegistry: this.entityRegistry,
        weaponSelection: this.weaponSelection,
        getWorms: () => this.getWorms(),
        getWaterLevel: () => this.currentWaterLevel,
        getTerrainFriction: () => this.getTerrainFriction(),
        applyJetpackTerrainCollision: (worm) =>
          this.jetpackManager.applyJetpackTerrainCollision(worm),
        weather: this.weatherSystem,
      },
      {
        playSound: (soundId) =>
          soundManager.play(soundId as Parameters<typeof soundManager.play>[0]),
        stopSound: (soundId) =>
          soundManager.stop(soundId as Parameters<typeof soundManager.stop>[0]),
        onWormDrowned: (worm, isActiveWorm) => {
          const waterY = WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel;
          const splashX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
          this.particles.emitSplash(splashX, waterY);
          soundManager.play('splash');
          soundManager.playDeath();
          try {
            this.commentary.showDrown(worm.name);
          } catch {}

          const victimTeam = this.state.teams.find((t) => t.id === worm.teamId);
          if (this.statsPanel && victimTeam) {
            this.statsPanel.recordDeath(victimTeam.id, victimTeam.name);
          }
          if (this.killFeed) {
            this.killFeed.addKill('Water', worm.name, 'Drowned');
          }

          this.simulation.notifyWormDrowned(isActiveWorm);
        },
        onBungeeAutoActivated: (team) => {
          if (team instanceof Team) {
            this.updateWeaponPanelAmmo(team);
          }
        },
        onSheepLost: (sheep) => {
          if (this.trackedSheep === sheep) {
            this.trackedSheep = null;
            this.cameraMode = 'manual';
          }
        },
        onSuperSheepLost: (superSheep) => {
          if (this.trackedSuperSheep === superSheep) {
            this.trackedSuperSheep = null;
            this.cameraMode = 'manual';
          }
        },
        requestForceBroadcast: () => {},
        isWaitingForCrates: () => this.waitingForCrates,
        onCratesSettled: () => {
          this.waitingForCrates = false;
          this.trackedCrate = null;
          this.cameraMode = 'worm';
        },
        destroyTerrain: (x, y, radius) => {
          this.destroyTerrainCircle(x, y, radius);
          this.terrainDirty = true;
        },
        createExplosion: (x, y, radius, maxDamage) => this.createExplosion(x, y, radius, maxDamage),
      },
    );

    this.simulation = new Simulation(
      {
        state: this.state,
        terrain: this.terrain,
        mode: this.settings.mode,
        entityRegistry: this.entityRegistry,
        weaponSelection: this.weaponSelection,
        chargeState: this.chargeState,
        targetingSystem: this.targetingSystem,
        toolSystem: this.toolSystem,
        turnManager: this.turnManager,
        worldStabilityChecker: this.worldStabilityChecker,
        suddenDeathManager: this.suddenDeathManager,
        crateManager: this.crateManager,
        jetpackManager: this.jetpackManager,
        getWorms: () => this.getWorms(),
        getFlyingSuperSheep: () =>
          this.trackedSuperSheep?.isFlying ? this.trackedSuperSheep : null,
        weather: this.weatherSystem,
      },
      this.physicsStep,
      {
        onTurnStarted: (currentTeam) => {
          soundManager.playTurnStart();
          this.forceUpdateAllSprites();
          this.cameraMode = 'worm';
          this.trackedProjectile = null;
          this.thrownWorm = null;
          this.pendingSheepTurnEnd = false;
          if (currentTeam) {
            // Rebuild the panel for this team's tank kit (worm/wizard/...).
            this.refreshWeaponPanelForTeam(currentTeam);
            this.updateWeaponPanelAmmo(currentTeam);
            // Per-worm weapon memory: restore what this worm last used (as-is,
            // even if depleted). Only with NO memory do we pick a default -
            // grenade, or the first weapon this tank can fire if it has no
            // grenade (e.g. the wizard), so the default is never an empty slot.
            let remembered = currentTeam.getCurrentWorm()?.lastWeaponId;
            if (!remembered) {
              remembered = currentTeam.hasAmmo('grenade')
                ? 'grenade'
                : (this.firstUsableWeaponId(currentTeam) ?? 'grenade');
            }
            if (this.weaponSelection.getSelectedWeaponId() !== remembered) {
              this.selectWeapon(remembered);
            }
          }
        },
        onMovementPhaseEnded: () => this.resetInput(),
        onTurnAdvanced: () => {},
        shouldJumpNow: () => {
          if (!this.pendingJump) return false;
          if (Date.now() - this.lastJumpTime >= this.DOUBLE_TAP_THRESHOLD) {
            this.pendingJump = false;
            return true;
          }
          return false;
        },
        aimFromHeldInput: () => true, // offline: keys drive aiming directly
        onWormJumped: () => soundManager.playJump(),
        updateGirderPreview: () => this.updateGirderPreview(),
        isWaitingForCrates: () => this.waitingForCrates,
      },
    );
  }

  private async createWormSprites(): Promise<void> {
    for (const team of this.state.teams) {
      for (const worm of team.worms) {
        worm.onDamage = () => {
          if (isBungeeActive(worm)) {
            releaseBungee(worm);
          }
          const currentWorm = this.state.getCurrentWorm();
          if (currentWorm && currentWorm.id === worm.id) {
            this.state.onCurrentWormTookDamage();
          }
        };

        worm.onDie = (position) => {
          const waterY = WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel;
          const isInWater = position.y > waterY - 20;
          if (!isInWater && this.settings.worm.gravesOnDeath && !worm.skipGraveOnDeath) {
            const graveX = position.x + WA_PHYSICS.WORM.WIDTH / 2;
            const graveY = position.y + WA_PHYSICS.WORM.HEIGHT;
            this.createExplosion(graveX, graveY - 8, 25, 25);

            if (graveY < waterY - 10) {
              const grave = new Grave(graveX, graveY);
              this.addGrave(grave);
            }
          }

          this.state.checkGameOver();
        };

        // Pick the sprite for this team's tank type (worm, wizard, ...).
        const animatedSprite = new AnimatedWormSprite(getTankSpriteConfig(team.tankTypeId));
        await animatedSprite.load();
        // Team color tint removed - worms now use original sprite colors
        // This allows damage flash (red tint) to be clearly visible
        animatedSprite.setPosition(
          worm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
          worm.position.y + WA_PHYSICS.WORM.HEIGHT,
        );
        animatedSprite.setVisible(true);
        this.wormSprites.set(worm.id, animatedSprite);
        this.layers.entities.addChild(animatedSprite.container);
        console.log(
          `[SPRITE] Created sprite for worm: ${worm.id} at (${worm.position.x.toFixed(0)}, ${worm.position.y.toFixed(0)})`,
        );

        const healthBar = this.createHealthBar(worm, team.color);
        this.wormHealthBars.set(worm.id, healthBar);
        this.layers.ui.addChild(healthBar);
      }
    }
  }

  private createExtendedWaterBackground(): void {
    this.waterGraphics = new Graphics();
    this.layers.background.addChild(this.waterGraphics);
    this.updateWaterVisual();
  }

  private updateWaterVisual(): void {
    const waterY = WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel;
    const totalWidth = WA_PHYSICS.WORLD.WIDTH + WA_PHYSICS.WORLD.CAMERA_PADDING * 2;
    const waterHeight = this.currentWaterLevel;

    const g = this.waterGraphics;
    g.clear();
    // Deep body + a lighter near-surface band approximate a depth gradient.
    g.rect(0, waterY, totalWidth, waterHeight).fill({
      color: 0x1f4fa0,
      alpha: 0.8,
    });
    g.rect(0, waterY, totalWidth, Math.min(70, waterHeight)).fill({
      color: 0x3a7bd5,
      alpha: 0.45,
    });
    // Bright foam highlight at the surface.
    g.rect(0, waterY, totalWidth, 3).fill({ color: 0xbfe3ff, alpha: 0.85 });
    // Animated shimmer lines (time-based; purely decorative).
    const t = performance.now() * 0.0012;
    for (let y = waterY + 8; y < WA_PHYSICS.WORLD.HEIGHT; y += 22) {
      const off = Math.sin(y * 0.08 + t) * 7;
      g.rect(off, y, totalWidth, 2).fill({ color: 0x9fd0ff, alpha: 0.2 });
    }
  }

  private createHealthBar(worm: Worm, teamColor: number): Container {
    const container = new Container();

    const nameText = new Text({
      text: worm.name,
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 10,
        fill: teamColor,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    nameText.anchor.set(0.5, 1);
    nameText.position.set(0, 0);
    container.addChild(nameText);

    const bg = new Graphics();
    bg.rect(-20, 2, 40, 6);
    bg.fill({ color: 0x000000, alpha: 0.7 });
    container.addChild(bg);

    const healthFill = new Graphics();
    healthFill.rect(-19, 3, 38, 4);
    healthFill.fill({ color: 0x00ff00 });
    healthFill.label = 'healthFill';
    container.addChild(healthFill);

    return container;
  }

  private createRopeGraphics(): void {
    this.ropeGraphics = new Graphics();
    this.layers.effects.addChild(this.ropeGraphics);
  }

  private renderRope(): void {
    this.ropeGraphics.clear();

    for (const team of this.state.teams) {
      for (const worm of team.worms) {
        const ropeData = getRopeRenderData(worm);
        if (ropeData) {
          this.ropeGraphics.moveTo(ropeData.startX, ropeData.startY);
          this.ropeGraphics.lineTo(ropeData.endX, ropeData.endY);
          this.ropeGraphics.stroke({ width: 2, color: 0x8b4513 });

          this.ropeGraphics.circle(ropeData.startX, ropeData.startY, 3);
          this.ropeGraphics.fill({ color: 0x654321 });
        }

        const bungeeData = getBungeeRenderData(worm);
        if (bungeeData) {
          this.ropeGraphics.moveTo(bungeeData.startX, bungeeData.startY);
          this.ropeGraphics.lineTo(bungeeData.endX, bungeeData.endY);
          this.ropeGraphics.stroke({ width: 3, color: 0x00aa00 });

          this.ropeGraphics.circle(bungeeData.startX, bungeeData.startY, 4);
          this.ropeGraphics.fill({ color: 0x008800 });
        }
      }
    }
  }

  private createCrosshair(): void {
    let texture: Texture;
    if (this.spriteManager.crosshairTextures.length > 0) {
      texture = this.spriteManager.crosshairTextures[0];
    } else {
      const img = new Image();
      img.src = generateCrosshairSprite();
      texture = Texture.from(img);
    }
    this.crosshairSprite = new Sprite(texture);
    this.crosshairSprite.anchor.set(0.5, 0.5);
    this.crosshairSprite.visible = false;
    this.layers.ui.addChild(this.crosshairSprite);

    this.chargeBarGraphics = new Graphics();
    this.layers.ui.addChild(this.chargeBarGraphics);

    this.aimAngleText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Courier New, monospace',
        fontSize: 13,
        fill: '#ffffff',
        stroke: { color: '#000000', width: 3 },
      }),
    });
    this.aimAngleText.anchor.set(0.5, 1);
    this.aimAngleText.visible = false;
    this.layers.ui.addChild(this.aimAngleText);

    this.offTurnAimText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Courier New, monospace',
        fontSize: 13,
        fill: '#aaddff',
        stroke: { color: '#000000', width: 3 },
      }),
    });
    this.offTurnAimText.anchor.set(0.5, 1);
    this.offTurnAimText.visible = false;
    this.layers.ui.addChild(this.offTurnAimText);

    this.aimArcGraphics = new Graphics();
    this.layers.ui.addChild(this.aimArcGraphics);

    this.dragShotGraphics = new Graphics();
    this.layers.ui.addChild(this.dragShotGraphics);

    this.weatherGraphics = new Graphics();
    this.layers.effects.addChild(this.weatherGraphics);
  }

  // ----- drag shot (GunBound-style fire input) -----

  private isDragFireMode(): boolean {
    return this.settings.mode.fire.input === 'drag';
  }

  /** The worm the local player may drag-fire with right now, if any. */
  private controlledWormForDrag(): Worm | null {
    if (!this.isDragFireMode()) return null;
    if (this.targetingSystem.isActive() || this.toolSystem.isActive()) return null;
    if (this.waitingForCrates) return null;
    if (!this.state.canFire() || !this.canFireWeapon()) return null;
    if (this.networkManager) return this.getPredictedWorm();
    const worm = this.state.getCurrentWorm();
    if (!worm || worm.state === 'dead' || worm.hasPendingDamage()) return null;
    return worm;
  }

  /** Client coords to pixi screen coords (canvas may be CSS-scaled). */
  private toScreenPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.app.canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left) * (this.app.screen.width / rect.width),
      y: (clientY - rect.top) * (this.app.screen.height / rect.height),
    };
  }

  /**
   * Press on the bottom power bar. A real drag sets power and fires on
   * release (when allowed); a quick tap places/clears the local goal marker
   * instead (also during enemy turns).
   */
  private tryStartPowerDrag(clientX: number, clientY: number): boolean {
    if (!this.powerBar?.isVisible()) return false;
    const point = this.toScreenPoint(clientX, clientY);
    if (!this.powerBar.containsPoint(point.x, point.y)) return false;
    this.barPress = {
      x: clientX,
      at: performance.now(),
      power: this.powerBar.powerForX(point.x),
      moved: false,
    };
    // The power bar only manages the goal marker - it never fires (a tap with
    // a little finger jitter on mobile used to start a drag-shot and fire on
    // release). Firing is via the shoot button (mobile) or a free drag in the
    // play area (desktop).
    return true; // consume the press either way (no camera pan)
  }

  /** Horizontal drag distance (px) for full power (free left-mouse drag). */
  private readonly DRAG_SHOT_RANGE = 220;

  private updatePowerDrag(clientX: number, clientY: number): void {
    if (this.barPress) {
      if (Math.abs(clientX - this.barPress.x) > 6) {
        this.barPress.moved = true;
      }
      // Track the latest bar position and drag the goal marker live so it
      // follows the finger/mouse (a tap that never moves only toggles the
      // marker on release).
      if (this.powerBar) {
        this.barPress.power = this.powerBar.powerForX(this.toScreenPoint(clientX, clientY).x);
        if (this.barPress.moved) {
          this.powerBar.setGoalMarker(this.barPress.power);
        }
      }
    }
    if (!this.dragShot || !this.powerBar) return;
    if (!this.controlledWormForDrag()) {
      this.dragShot = null;
      this.dragShotOrigin = null;
      return;
    }
    if (this.dragShotOrigin !== null) {
      const power = (Math.abs(clientX - this.dragShotOrigin) / this.DRAG_SHOT_RANGE) * 100;
      this.dragShot.power = Math.round(Math.max(0, Math.min(100, power)));
    } else {
      this.dragShot.power = this.powerBar.powerForX(this.toScreenPoint(clientX, clientY).x);
    }
  }

  /** Fire at the worm's current aim (W/S or d-pad) with the dragged power. */
  private releaseDragShot(): void {
    const drag = this.dragShot;
    const barPress = this.barPress;
    this.dragShot = null;
    this.dragShotOrigin = null;
    this.barPress = null;
    this.dragShotGraphics.clear();

    // Quick tap on the bar: place/clear the goal marker instead of firing.
    if (barPress && !barPress.moved && performance.now() - barPress.at < 350) {
      this.powerBar?.toggleGoalMarker(barPress.power);
      return;
    }

    if (!drag) {
      // Bar drag without firing rights (e.g. waiting for our turn): place
      // the goal marker where the drag ended.
      if (barPress?.moved) {
        this.powerBar?.setGoalMarker(barPress.power);
      }
      return;
    }
    const worm = this.controlledWormForDrag();
    if (!worm || drag.power < 2) return; // tap or invalid: cancel

    const lastShot: LastShotInfo = {
      angleDeg: Math.round(worm.aimAngle),
      power: drag.power,
      weaponId: this.weaponSelection.getSelectedWeaponId() ?? '',
    };
    // Persist the player's own last shot for the power-bar marker (shown
    // always, replaced only by the next shot).
    this.myLastShot = lastShot;
    if (this.networkManager) {
      this.sendAction({
        type: 'fireShot',
        facing: worm.facing,
        angleDeg: worm.aimAngle,
        power: drag.power,
      });
      this.myLastShotByWorm.set(worm.id, lastShot);
    } else {
      this.chargeState.setInstantCharge(drag.power);
      this.fireWeapon();
    }
  }

  // ----- in-match items -----

  /** Use a match item (Dual, Power-Up, ...) on the local player's turn. */
  private useItem(itemId: string): void {
    if (this.pauseMenu.isVisible()) return;
    if (!this.isMyTurn() || this.state.phase !== 'playing') return;

    if (this.networkManager) {
      this.sendAction({ type: 'useItem', itemId });
      return;
    }

    const team = this.state.getCurrentTeam();
    const worm = this.state.getCurrentWorm();
    if (team && worm && useMatchItem(itemId, { state: this.state, team, worm })) {
      this.onItemUsed(itemId, team.id);
    }
  }

  /** Feedback when any team uses an item (local or via server event). */
  private onItemUsed(itemId: string, teamId: string): void {
    const def = MATCH_ITEMS[itemId];
    const team = this.state.teams.find((t) => t.id === teamId);
    if (def && team) {
      try {
        this.commentary.show(`${team.name} used ${def.name}!`);
      } catch {
        /* HUD not ready */
      }
    }
  }

  /** Current team's item buttons, shown on the active player's turn. */
  private updateItemBar(): void {
    if (!this.itemBar) return;
    const team = this.state.getCurrentTeam();
    const myTurn = this.networkManager ? this.isMyTurn() : true;
    const entries = team
      ? Object.entries(team.getItemsSnapshot())
          .filter(([id]) => MATCH_ITEMS[id])
          .map(([id, count]) => ({ def: MATCH_ITEMS[id], count }))
      : [];
    const visible =
      entries.length > 0 && myTurn && this.state.phase === 'playing' && !this.isSpectator();
    this.itemBar.update(entries, visible, !this.state.itemUsedThisTurn);
  }

  /** Weather visuals (preview arrow, animated zones, Thor) + banners. */
  private renderWeather(): void {
    if (!this.weatherGraphics) return;
    this.weatherGraphics.clear();

    const time = performance.now() / 1000;

    // The Thor satellite is a permanent fixture in the sky: it fires for Thor
    // weather AND for Aduka shots, so it is always visible regardless of the
    // weather system being enabled.
    this.drawThor(THOR_HOME_X, time);

    const active = this.weatherSystem.enabled ? this.weatherSystem.getActive() : null;
    const next = this.weatherSystem.enabled ? this.weatherSystem.getNext() : null;
    this.weatherIndicator?.update(active, next, this.weatherSystem.enabled);

    if (this.weatherSystem.enabled) {
      const waterY = WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel;
      // Announced event: a simple bobbing arrow marking the incoming zone.
      if (next && WEATHER_ZONE_WIDTH[next.type] > 0) {
        this.drawWeatherArrow(next, time);
      }
      if (active) {
        if (active.type === 'tornado') {
          this.drawTornado(active.x, waterY, time);
        } else if (active.type === 'force') {
          this.drawForceZone(active.x, waterY, time);
        }
        // Thor's satellite is drawn persistently above.
      }
    }

    // Fading zap beams from the satellite to recent strike points (Thor
    // weather or Aduka).
    if (this.thorZapFx.length > 0) {
      const now = performance.now();
      this.thorZapFx = this.thorZapFx.filter((fx) => fx.until > now);
      const g = this.weatherGraphics;
      for (const fx of this.thorZapFx) {
        const fade = (fx.until - now) / 350;
        // Thor beams start just under the satellite; vertical lightning bolts
        // carry their own (overhead) origin Y.
        const beamFromY = fx.fromY > 0 ? fx.fromY : THOR_ALTITUDE + 16;
        g.moveTo(fx.fromX, beamFromY);
        g.lineTo(fx.x, fx.y);
        g.stroke({ width: 3, color: fx.color, alpha: 0.85 * fade });
        g.circle(fx.x, fx.y, 6 + 10 * (1 - fade));
        g.fill({ color: fx.color, alpha: 0.5 * fade });
      }
    }

    const activeKey = active ? `${active.type}@${active.x}` : '';
    const nextKey = next ? `${next.type}@${next.x}` : '';
    const activeChanged = activeKey !== this.lastWeatherActiveKey;
    const nextChanged = nextKey !== this.lastWeatherNextKey;
    this.lastWeatherActiveKey = activeKey;
    this.lastWeatherNextKey = nextKey;
    try {
      if (activeChanged && active) {
        this.commentary.show(WEATHER_ACTIVE_MESSAGES[active.type], 4);
      } else if (nextChanged && next) {
        this.commentary.show(`Incoming: ${WEATHER_LABELS[next.type]}`);
      }
    } catch {
      /* HUD not ready */
    }
  }

  /** Bobbing arrow pointing at where the announced weather will appear. */
  private drawWeatherArrow(event: WeatherEvent, time: number): void {
    const g = this.weatherGraphics;
    const color = WEATHER_COLORS[event.type];
    const x = event.x;
    const y = 26 + Math.sin(time * 3) * 6;

    g.moveTo(x, y);
    g.lineTo(x, y + 22);
    g.stroke({ width: 4, color, alpha: 0.9 });
    g.poly([x - 9, y + 22, x + 9, y + 22, x, y + 38]);
    g.fill({ color, alpha: 0.9 });
  }

  /** Swirling funnel: stacked, swaying ellipses widening toward the sky. */
  private drawTornado(x: number, waterY: number, time: number): void {
    const g = this.weatherGraphics;
    const half = WEATHER_ZONE_WIDTH.tornado / 2;
    // Extend the column up into the sky (above the world top) so it covers the
    // full height that shots arc through, not just the area below y=0.
    const top = WEATHER_ZONE_TOP;
    const span = waterY - top;

    g.rect(x - half, top, half * 2, span);
    g.fill({ color: 0xaaddff, alpha: 0.05 });

    const steps = Math.max(8, Math.floor(span / 44));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps; // 0 = top (sky), 1 = ground
      const y = top + t * span;
      const radius = half * (1 - t * 0.72);
      const sway = Math.sin(time * 5 + t * 9) * half * 0.3 * t;
      g.ellipse(x + sway, y, Math.max(4, radius), 7);
      g.stroke({
        width: 2,
        color: 0xbbe6ff,
        alpha: 0.2 + 0.3 * Math.abs(Math.sin(time * 4 + i * 1.7)),
      });
    }
  }

  /** Energy beam with rising chevrons (shots through it gain damage). */
  private drawForceZone(x: number, waterY: number, time: number): void {
    const g = this.weatherGraphics;
    const width = WEATHER_ZONE_WIDTH.force;
    // Extend up into the sky so the beam covers the full height shots fly
    // through (see drawTornado).
    const top = WEATHER_ZONE_TOP;
    const span = waterY - top;

    g.rect(x - width / 2, top, width, span);
    g.fill({ color: 0xff8833, alpha: 0.07 });
    g.rect(x - width / 6, top, width / 3, span);
    g.fill({ color: 0xffcc66, alpha: 0.14 });

    g.moveTo(x - width / 2, top);
    g.lineTo(x - width / 2, waterY);
    g.moveTo(x + width / 2, top);
    g.lineTo(x + width / 2, waterY);
    g.stroke({ width: 1.5, color: 0xffaa33, alpha: 0.5 });

    const spacing = 80;
    const offset = (time * 120) % spacing;
    for (let y = waterY - offset; y > top; y -= spacing) {
      g.moveTo(x - 18, y + 10);
      g.lineTo(x, y);
      g.lineTo(x + 18, y + 10);
      g.stroke({ width: 2.5, color: 0xffdd88, alpha: 0.55 });
    }
  }

  /** Thor satellite hovering in the sky (zap beams render via thorZapFx). */
  private drawThor(x: number, time: number): void {
    const g = this.weatherGraphics;
    const y = THOR_ALTITUDE + Math.sin(time * 2) * 5;

    // Satellite: solar panels, diamond body, pulsing red core.
    g.rect(x - 36, y - 5, 18, 10);
    g.rect(x + 18, y - 5, 18, 10);
    g.fill({ color: 0x2255aa, alpha: 0.95 });
    g.poly([x, y - 18, x + 14, y, x, y + 18, x - 14, y]);
    g.fill({ color: 0x334466, alpha: 0.95 });
    g.stroke({ width: 2, color: 0x88aaff, alpha: 0.9 });
    g.circle(x, y, 5 + Math.sin(time * 6) * 1.5);
    g.fill({ color: 0xff4444, alpha: 0.9 });
  }

  /** Power bar + aim arrow + mobile shoot circle (drag fire mode only). */
  private renderDragShot(): void {
    this.dragShotGraphics.clear();
    const myTurn = this.networkManager ? this.isMyTurn() : true;
    const canDrag =
      this.isDragFireMode() && myTurn && this.state.phase === 'playing' && this.canFireWeapon();
    this.virtualGamepad?.setShootDragVisible(canDrag && this.controlledWormForDrag() !== null);

    // The power bar stays on screen for the whole match (drag fire mode),
    // showing the player's own last shot memory beside it.
    if (
      !this.isDragFireMode() ||
      this.isSpectator() ||
      this.state.phase === 'setup' ||
      this.state.phase === 'finished'
    ) {
      this.powerBar?.hide();
      return;
    }
    const power = canDrag
      ? this.dragShot
        ? this.dragShot.power
        : this.chargeState.getChargePower()
      : 0;
    this.powerBar?.show();
    this.powerBar?.update(power, this.getMyLastShot());

    if (!canDrag) return;
    const worm = this.state.getCurrentWorm();
    if (this.dragShot && worm) {
      const dir = worm.getAimDirection();
      const cx = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
      const cy = worm.position.y + WA_PHYSICS.WORM.HEIGHT / 2;
      const len = 24 + (this.dragShot.power / 100) * 90;
      this.dragShotGraphics.moveTo(cx, cy);
      this.dragShotGraphics.lineTo(cx + dir.x * len, cy + dir.y * len);
      this.dragShotGraphics.stroke({ width: 4, color: 0xffcc33, alpha: 0.9 });
    }
  }

  /** Visualize the mode's allowed aim range and full-damage (true) arc. */
  private renderAimArc(cx: number, cy: number, worm: Worm): void {
    const aim = this.settings.mode.aim;
    this.aimArcGraphics.clear();
    if (aim.minAngle <= -90 && aim.maxAngle >= 90 && !aim.trueAngle) return;

    // Slope-relative aim: the whole span rotates with the ground tilt under
    // the tank (0 on flat / classic mode).
    const tilt = worm.groundTiltDeg;
    const drawBand = (from: number, to: number, radius: number, color: number, alpha: number) => {
      let first = true;
      for (let a = from; a <= to; a += 2) {
        const eff = a + tilt;
        const base = worm.facing === 'right' ? eff : 180 - eff;
        const rad = (base * Math.PI) / 180;
        const px = cx + Math.cos(rad) * radius;
        const py = cy - Math.sin(rad) * radius;
        if (first) {
          this.aimArcGraphics.moveTo(px, py);
          first = false;
        } else {
          this.aimArcGraphics.lineTo(px, py);
        }
      }
      this.aimArcGraphics.stroke({ width: 3, color, alpha });
    };

    drawBand(aim.minAngle, aim.maxAngle, 44, 0x88ff88, 0.22);
    if (aim.trueAngle) {
      drawBand(aim.trueAngle.min, aim.trueAngle.max, 44, 0x33dd33, 0.5);
    }
  }

  private createHUD(): void {
    this.hudContainer = new Container();
    this.hudContainer.label = 'hud';

    // Backing panel keeps the status text legible over any terrain/sky
    // (game-ui-design: never HUD text without a readable backdrop).
    this.hudStatusBg = new Graphics();
    this.hudContainer.addChild(this.hudStatusBg);

    // Separate styles (the old code shared one mutable TextStyle and tinted
    // it with the team colour, turning both lines an unreadable red).
    const weaponLabel = new Text({
      text: 'Grenade',
      style: textStyle({ size: 15, weight: '600' }),
    });
    weaponLabel.position.set(16, 62);
    this.weaponText = weaponLabel as unknown as Sprite;
    this.hudContainer.addChild(weaponLabel);

    const timerLabel = new Text({
      text: 'YOUR TURN',
      style: textStyle({
        size: 16,
        weight: '700',
        color: COLORS.gold,
        display: true,
      }),
    });
    timerLabel.position.set(16, 86);
    this.timerText = timerLabel as unknown as Sprite;
    this.hudContainer.addChild(timerLabel);

    this.screenHudContainer.addChild(this.hudContainer);

    // Full-screen explosion flash, behind the HUD panels (kept subtle).
    this.flashOverlay = new Graphics();
    this.flashOverlay.eventMode = 'none';
    this.flashOverlay.alpha = 0;
    this.screenHudContainer.addChildAt(this.flashOverlay, 0);

    this.windIndicator = new WindIndicator();
    this.windIndicator.container.position.set(this.app.screen.width - 220, 10);
    this.hudContainer.addChild(this.windIndicator.container);

    this.teamHealthSummary = new TeamHealthSummary();
    this.teamHealthSummary.container.position.set(this.app.screen.width - 220, 50);
    this.hudContainer.addChild(this.teamHealthSummary.container);

    this.weatherIndicator = new WeatherIndicator();
    this.hudContainer.addChild(this.weatherIndicator.container);

    this.floatingDamage = new FloatingDamageManager();
    this.layers.effects.addChild(this.floatingDamage.container);

    this.powerBar = new PowerBar(this.app.screen.width, this.app.screen.height);
    this.screenHudContainer.addChild(this.powerBar.container);

    this.itemBar = new ItemBar(this.app.screen.width, this.app.screen.height, (itemId) =>
      this.useItem(itemId),
    );
    this.screenHudContainer.addChild(this.itemBar.container);

    this.commentary = new CommentaryManager();
    this.commentary.setScreenSize(this.app.screen.width);
    this.hudContainer.addChild(this.commentary.container);

    let killFeedContainer = document.getElementById('kill-feed-container');
    if (!killFeedContainer) {
      killFeedContainer = document.createElement('div');
      killFeedContainer.id = 'kill-feed-container';
      document.body.appendChild(killFeedContainer);
    }
    this.killFeed = new KillFeed(killFeedContainer);

    let statsPanelContainer = document.getElementById('stats-panel-container');
    if (!statsPanelContainer) {
      statsPanelContainer = document.createElement('div');
      statsPanelContainer.id = 'stats-panel-container';
      document.body.appendChild(statsPanelContainer);
    }
    this.statsPanel = new StatsPanel(statsPanelContainer);

    if (this.isSpectator()) {
      this.spectatorHUD = new SpectatorHUD();
      this.spectatorHUD.setPosition(10, 10);
      this.hudContainer.addChild(this.spectatorHUD.container);
      this.spectatorHUD.show();
    }

    this.createWeaponPanel();
  }

  private weaponIconCache = new Map<string, Texture>();

  /**
   * Weapon-panel icon: the loaded sprite if one exists, otherwise a stylized
   * procedural icon for the GunBound-inspired weapons (drawn to a texture so
   * the panel can scale it like any other), falling back to white.
   */
  private getWeaponIconTexture(id: string): Texture {
    const loaded = this.spriteManager.weaponIconTextures.get(id);
    if (loaded) return loaded;
    const cached = this.weaponIconCache.get(id);
    if (cached) return cached;
    const g = this.drawWeaponIcon(id);
    if (!g) return Texture.WHITE;
    const tex = this.app.renderer.generateTexture(g);
    g.destroy();
    this.weaponIconCache.set(id, tex);
    return tex;
  }

  /** Stylized icons for the GunBound mobiles (no licensed art needed). */
  /**
   * Polished procedural icons for the GunBound-inspired weapons that have no
   * ripped sprite (grub/aduka/frog/raon). Drawn once into a cached texture, so
   * shading/highlights are cheap. All share a ~44px box with bold dark
   * outlines + a top sheen so they read at panel size and match the chrome.
   */
  private drawWeaponIcon(id: string): Graphics | null {
    const g = new Graphics();
    const OUT = 0x10140a; // shared dark outline

    if (id === 'grub') {
      // Plump green grub: humped body, big eye, little antenna.
      g.ellipse(23, 27, 16, 11).fill({ color: 0x3f9c2e }).stroke({ width: 2, color: OUT });
      for (const cx of [13, 22, 31]) {
        g.circle(cx, 21, 6.5).fill({ color: 0x57c23f });
      }
      g.ellipse(23, 22, 15, 7).fill({ color: 0x6fd24f, alpha: 0.55 }); // top sheen
      g.ellipse(23, 27, 16, 11).stroke({ width: 2, color: OUT });
      // Head + face
      g.circle(35, 22, 9).fill({ color: 0x57c23f }).stroke({ width: 2, color: OUT });
      g.circle(37, 19, 3.4).fill({ color: 0xffffff }).stroke({ width: 1, color: OUT });
      g.circle(38, 19, 1.6).fill({ color: 0x101010 });
      g.moveTo(35, 13).lineTo(38, 8).stroke({ width: 1.6, color: OUT });
      g.circle(38.5, 7, 1.8).fill({ color: 0xffd84d });
      return g;
    }

    if (id === 'grubSS') {
      // Electric SS orb: glowing yellow sphere ringed by sparks.
      g.circle(22, 24, 13).fill({ color: 0xffd21f }).stroke({ width: 2, color: OUT });
      g.circle(22, 24, 8).fill({ color: 0xfff19a });
      g.circle(18, 20, 3.5).fill({ color: 0xffffff, alpha: 0.9 });
      for (const [sx, sy, ex, ey] of [
        [22, 11, 22, 3],
        [35, 24, 43, 24],
        [22, 37, 22, 45],
        [9, 24, 1, 24],
        [31, 15, 37, 9],
        [13, 33, 7, 39],
      ] as const) {
        g.moveTo(sx, sy).lineTo(ex, ey).stroke({ width: 2.2, color: 0xfff19a });
      }
      return g;
    }

    if (id === 'nak') {
      // Subterranean shell: a brass-tipped shell diving nose-down into a mound.
      g.ellipse(23, 33, 20, 7).fill({ color: 0x7a5530, alpha: 0.55 }); // dirt mound
      g.moveTo(3, 31).lineTo(43, 31).stroke({ width: 2, color: 0x6b4a2a }); // ground line
      g.roundRect(18, 6, 10, 21, 3).fill({ color: 0x3a4150 }).stroke({ width: 2, color: OUT });
      g.rect(18, 7, 10, 3).fill({ color: 0x20242e }); // dark tail band
      g.ellipse(23, 27, 5, 3.5).fill({ color: 0xc8a23a }).stroke({ width: 1, color: OUT }); // brass nose
      // dashed resurface arc
      for (const [ax, ay] of [
        [33, 34],
        [37, 30],
        [39, 25],
      ] as const) {
        g.circle(ax, ay, 1.3).fill({ color: 0xffd84d });
      }
      return g;
    }

    if (id === 'adukaSS') {
      // Phase shell: a charged shell punching through a ghosted wall, cyan glow.
      g.rect(20, 4, 8, 40).fill({ color: 0x3a4f5e, alpha: 0.35 }); // ghosted wall it phases through
      g.ellipse(24, 24, 11, 15).fill({ color: 0x29e0e0, alpha: 0.22 }); // aura
      g.roundRect(19, 8, 10, 22, 3).fill({ color: 0x243a4a }).stroke({ width: 2, color: OUT });
      g.rect(19, 9, 10, 3).fill({ color: 0x18222a }); // tail band
      g.ellipse(24, 29, 5, 3.5).fill({ color: 0x66f2f2 }).stroke({ width: 1, color: OUT }); // glowing nose
      // motion streaks
      for (const sy of [14, 20, 26] as const) {
        g.moveTo(13, sy).lineTo(8, sy).stroke({ width: 2, color: 0x66f2f2, alpha: 0.7 });
      }
      return g;
    }

    if (id === 'lightningT1' || id === 'lightningT2') {
      // Electric orb low, with bolt(s) striking down from the cloud above.
      g.ellipse(23, 9, 14, 6)
        .fill({ color: 0x3a4f6e, alpha: 0.85 })
        .stroke({ width: 1.5, color: OUT }); // cloud
      g.circle(23, 34, 9).fill({ color: 0x66e0ff }).stroke({ width: 2, color: OUT });
      g.circle(20, 31, 3).fill({ color: 0xffffff, alpha: 0.9 });
      if (id === 'lightningT1') {
        g.poly([23, 14, 18, 24, 25, 24, 21, 32, 30, 22, 24, 22])
          .fill({ color: 0xffe14d })
          .stroke({ width: 1.4, color: 0xc78a10 });
      } else {
        // V: two bolts splaying from the cloud onto either side of the orb.
        for (const dir of [-1, 1] as const) {
          g.poly([
            23,
            14,
            23 + dir * 5,
            23,
            23 + dir * 9,
            22,
            23 + dir * 13,
            32,
            23 + dir * 10,
            22,
            23 + dir * 6,
            23,
          ])
            .fill({ color: 0xffe14d })
            .stroke({ width: 1.2, color: 0xc78a10 });
        }
      }
      return g;
    }

    if (id === 'kalsiddon') {
      // A bomb at the apex splitting into homing missiles fanning down.
      g.circle(23, 12, 7).fill({ color: 0x2e3650 }).stroke({ width: 2, color: OUT }); // bomb
      g.rect(16, 10.5, 14, 3).fill({ color: 0xe0483a });
      for (const [ex, dir] of [
        [10, -1],
        [23, 0],
        [36, 1],
      ] as const) {
        const sx = 23 + dir * 3;
        // little missile bodies fanning down toward the ground
        g.moveTo(sx, 18).lineTo(ex, 40).stroke({ width: 3, color: 0xb0431f });
        g.poly([ex, 42, ex - 3, 36, ex + 3, 36]).fill({ color: 0xffd84d }); // nose
        g.circle(sx, 18, 1.6).fill({ color: 0xffd84d });
      }
      return g;
    }

    if (id === 'trico') {
      // Three leafy lettuce heads, two flanking a center one.
      for (const [cx, cy, r] of [
        [23, 24, 12],
        [12, 30, 8],
        [34, 30, 8],
      ] as const) {
        g.circle(cx, cy, r).fill({ color: 0x57c23f }).stroke({ width: 2, color: OUT });
        g.moveTo(cx, cy - r + 1)
          .lineTo(cx, cy + r - 1)
          .stroke({ width: 1, color: 0x2f7a22, alpha: 0.7 });
        g.moveTo(cx - r + 1, cy)
          .lineTo(cx + r - 1, cy)
          .stroke({ width: 1, color: 0x2f7a22, alpha: 0.7 });
        g.circle(cx - r * 0.3, cy - r * 0.3, r * 0.3).fill({ color: 0x9be07f, alpha: 0.85 });
      }
      return g;
    }

    if (id === 'knight') {
      // Three swords raining point-down onto a ground marker.
      g.moveTo(6, 42).lineTo(40, 42).stroke({ width: 2, color: 0x6b7689 }); // ground
      for (const [sx, topY] of [
        [13, 6],
        [23, 3],
        [33, 8],
      ] as const) {
        const tip = 38;
        g.moveTo(sx, topY).lineTo(sx, tip).stroke({ width: 3, color: 0xd8e2f0 }); // blade
        g.moveTo(sx, tip)
          .lineTo(sx - 3, tip - 5)
          .lineTo(sx + 3, tip - 5)
          .fill({ color: 0xeef4ff }); // point
        g.moveTo(sx - 5, topY + 4)
          .lineTo(sx + 5, topY + 4)
          .stroke({ width: 2.5, color: 0xc8a23a }); // guard
        g.circle(sx, topY + 1, 2).fill({ color: 0xffd84d }); // pommel
      }
      return g;
    }

    if (id === 'aduka') {
      // Thor energy orb: glossy violet sphere struck by a gold lightning bolt.
      g.circle(22, 26, 12).fill({ color: 0x7a2fd0 }).stroke({ width: 2, color: OUT });
      g.ellipse(19, 21, 6, 4).fill({ color: 0xc79bff, alpha: 0.7 }); // sheen
      g.circle(22, 26, 12).stroke({ width: 2, color: 0x3a1370, alpha: 0.6 });
      // Lightning bolt
      g.poly([24, 4, 13, 24, 21, 24, 16, 40, 33, 18, 24, 18])
        .fill({ color: 0xffe14d })
        .stroke({ width: 1.6, color: 0xc78a10 });
      return g;
    }

    if (id === 'frogLeft' || id === 'frogRight') {
      const faceLeft = id === 'frogLeft';
      // Body
      g.ellipse(22, 28, 15, 10).fill({ color: 0x3aa34a }).stroke({ width: 2, color: OUT });
      g.ellipse(22, 25, 13, 5).fill({ color: 0x5ec968, alpha: 0.6 });
      // Eye bumps
      for (const ex of [15, 29]) {
        g.circle(ex, 15, 6).fill({ color: 0x3aa34a }).stroke({ width: 2, color: OUT });
        g.circle(ex, 14, 3).fill({ color: 0xffffff }).stroke({ width: 1, color: OUT });
        g.circle(ex + (faceLeft ? -1 : 1), 14, 1.6).fill({ color: 0x101010 });
      }
      // Smile
      g.moveTo(15, 30).quadraticCurveTo(22, 35, 29, 30).stroke({ width: 2, color: OUT });
      // Direction chevron (which way it walks)
      const ax = faceLeft ? 5 : 39;
      const dir = faceLeft ? -1 : 1;
      g.poly([ax, 28, ax - dir * 7, 23, ax - dir * 7, 33])
        .fill({ color: 0xffd84d })
        .stroke({ width: 1.4, color: 0xc78a10 });
      return g;
    }

    if (id === 'raon') {
      // Penguin bot: dark body, white belly, orange beak/feet, antenna light.
      g.ellipse(22, 26, 12, 14).fill({ color: 0x232a3a }).stroke({ width: 2, color: OUT });
      g.ellipse(22, 28, 7, 10).fill({ color: 0xf2f2f2 });
      g.ellipse(20, 18, 5, 4).fill({ color: 0x4a5468, alpha: 0.6 });
      // Feet
      g.ellipse(16, 38, 4, 2.2).fill({ color: 0xffa024 }).stroke({ width: 1, color: OUT });
      g.ellipse(28, 38, 4, 2.2).fill({ color: 0xffa024 }).stroke({ width: 1, color: OUT });
      // Eyes + beak
      for (const ex of [18, 26]) {
        g.circle(ex, 20, 2.6).fill({ color: 0xffffff }).stroke({ width: 1, color: OUT });
        g.circle(ex, 20, 1.3).fill({ color: 0x101010 });
      }
      g.poly([19, 24, 25, 24, 22, 28]).fill({ color: 0xffa024 }).stroke({ width: 1, color: OUT });
      // Antenna light
      g.moveTo(22, 12).lineTo(22, 6).stroke({ width: 1.6, color: 0x9aa3b2 });
      g.circle(22, 5, 3).fill({ color: 0xff3b30 }).stroke({ width: 1, color: OUT });
      return g;
    }

    if (id === 'jdT2' || id === 'jdSS') {
      const pull = id === 'jdT2';
      const core = pull ? 0x2a6cff : 0xff7a2a;
      const glow = pull ? 0xbfe0ff : 0xffe0b0;
      // Electric orb core.
      g.circle(23, 24, 11).fill({ color: core }).stroke({ width: 2, color: OUT });
      g.circle(19, 20, 3.5).fill({ color: 0xffffff, alpha: 0.9 });
      // Magnet (pull = arrows inward) / anti-magnet (push = arrows outward).
      for (let a = 0; a < 4; a++) {
        const ang = (a / 4) * Math.PI * 2 + Math.PI / 4;
        const dx = Math.cos(ang);
        const dy = Math.sin(ang);
        const near = 14;
        const far = 21;
        const tip = pull ? near : far;
        const tail = pull ? far : near;
        const hx = 23 + dx * tip;
        const hy = 24 + dy * tip;
        g.moveTo(23 + dx * tail, 24 + dy * tail).lineTo(hx, hy).stroke({ width: 2, color: glow });
        // arrowhead at the tip
        const perp = ang + Math.PI / 2;
        g.moveTo(hx, hy)
          .lineTo(hx - dx * 3 + Math.cos(perp) * 2.4, hy - dy * 3 + Math.sin(perp) * 2.4)
          .moveTo(hx, hy)
          .lineTo(hx - dx * 3 - Math.cos(perp) * 2.4, hy - dy * 3 - Math.sin(perp) * 2.4)
          .stroke({ width: 2, color: glow });
      }
      return g;
    }

    return null;
  }

  /** Weapons shown in the panel for a team: with tank selection, only the
   *  team's tank-type kit (its preset weapons); otherwise the legacy mode
   *  whitelist (or all). */
  private buildWeaponSlots(team: Team | null): WeaponSlot[] {
    const weapons = weaponRegistry.getAll();
    const whitelist = this.settings.mode.items.whitelist;
    const tankKit =
      this.settings.mode.tankSelection && team
        ? new Set(Object.keys(getTankType(team.tankTypeId).ammoPreset))
        : null;
    const slots: WeaponSlot[] = [];
    for (const weapon of weapons) {
      if (tankKit) {
        if (!tankKit.has(weapon.id)) continue;
      } else if (whitelist && !whitelist.includes(weapon.id)) {
        continue;
      }
      const texture = this.getWeaponIconTexture(weapon.id);
      const ammo = team ? team.getAmmo(weapon.id) : weapon.maxAmmo;
      slots.push({ weapon, ammo, texture, textureSelected: texture });
    }
    return slots;
  }

  /** First weapon in the panel order this team can actually fire (ammo != 0).
   *  Used as a fallback default when the remembered weapon is unavailable for
   *  the tank (e.g. the wizard has no grenade). */
  private firstUsableWeaponId(team: Team | null): string | null {
    if (!team) return null;
    for (const slot of this.buildWeaponSlots(team)) {
      if (team.hasAmmo(slot.weapon.id)) return slot.weapon.id;
    }
    return null;
  }

  private createWeaponPanel(): void {
    const currentTeam = this.state.getCurrentTeam();
    this.weaponPanel = new WeaponPanel({
      weapons: this.buildWeaponSlots(currentTeam),
      onSelect: (weapon) => {
        this.weaponJustSelected = true;
        this.selectWeaponWithNetwork(weapon.id);
        setTimeout(() => {
          this.weaponJustSelected = false;
        }, 300);
      },
    });

    this.weaponPanel.container.position.set(10, 70);
    this.hudContainer.addChild(this.weaponPanel.container);
  }

  /** Rebuild the panel membership for the current team's tank (turn change) and
   *  re-apply the selected weapon highlight. */
  private refreshWeaponPanelForTeam(team: Team | null): void {
    if (!this.weaponPanel || !this.settings.mode.tankSelection) return;
    this.weaponPanel.setWeapons(this.buildWeaponSlots(team));
    this.weaponPanel.setSelectedWeapon(this.weaponSelection.getSelectedWeaponId() ?? '');
    this.lastPanelTeamId = team?.id ?? null;
  }

  private getWeaponPanelPosition(): { x: number; y: number } {
    if (isMobileDevice()) {
      const MOBILE_PANEL_WIDTH = 300;
      const MOBILE_PANEL_HEIGHT = 400;
      const x = Math.max(10, (this.app.screen.width - MOBILE_PANEL_WIDTH) / 2);
      const y = Math.max(10, (this.app.screen.height - MOBILE_PANEL_HEIGHT) / 2 - 50);
      return { x, y };
    }
    return { x: 10, y: 70 };
  }

  private updateTerrainTexture(): void {
    this.terrainSprite.texture.source.update();
  }

  private setupInput(): void {
    window.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        this.exportGameState();
        return;
      }
      this.handleKeyDown(e);
    });
    window.addEventListener('keyup', (e) => this.handleKeyUp(e));

    this.app.canvas.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.app.canvas.addEventListener('click', (e) => this.handleClick(e));
    this.app.canvas.addEventListener('wheel', (e) => this.handleWheel(e), {
      passive: false,
    });

    this.setupCameraDrag();
    this.setupPinchZoom();

    this.targetingCursor = new Graphics();
    this.targetingCursor.circle(0, 0, 15);
    this.targetingCursor.stroke({ width: 2, color: 0xff0000 });
    this.targetingCursor.moveTo(-20, 0);
    this.targetingCursor.lineTo(20, 0);
    this.targetingCursor.stroke({ width: 1, color: 0xff0000 });
    this.targetingCursor.moveTo(0, -20);
    this.targetingCursor.lineTo(0, 20);
    this.targetingCursor.stroke({ width: 1, color: 0xff0000 });
    this.targetingCursor.visible = false;
    this.layers.ui.addChild(this.targetingCursor);

    this.lockedTargetIndicator = new Graphics();
    this.lockedTargetIndicator.circle(0, 0, 12);
    this.lockedTargetIndicator.stroke({ width: 3, color: 0x00ff00 });
    this.lockedTargetIndicator.moveTo(-18, 0);
    this.lockedTargetIndicator.lineTo(18, 0);
    this.lockedTargetIndicator.stroke({ width: 2, color: 0x00ff00 });
    this.lockedTargetIndicator.moveTo(0, -18);
    this.lockedTargetIndicator.lineTo(0, 18);
    this.lockedTargetIndicator.stroke({ width: 2, color: 0x00ff00 });
    this.lockedTargetIndicator.visible = false;
    this.layers.ui.addChild(this.lockedTargetIndicator);

    this.girderPreviewGraphics = new Graphics();
    this.girderPreviewGraphics.visible = false;
    this.layers.ui.addChild(this.girderPreviewGraphics);

    this.targetingSystem.setCallbacks({
      onCursorVisibilityChanged: (visible) => {
        this.targetingCursor.visible = visible;
        if (!visible) {
          this.girderPreviewGraphics.visible = false;
        }
      },
      onCursorPositionChanged: (pos) => {
        this.targetingCursor.position.set(pos.x, pos.y);
      },
      onLockedTargetChanged: (target) => {
        if (target) {
          this.lockedTargetIndicator.position.set(target.x, target.y);
          this.lockedTargetIndicator.visible = true;
        } else {
          this.lockedTargetIndicator.visible = false;
        }
      },
      playLockSound: () => {
        soundManager.play('homingLock');
      },
    });

    this.weaponSelection.setCallbacks({
      onWeaponChanged: (_weapon, _previousWeapon) => {
        this.exitTargetingMode();
        this.targetingSystem.unlockTarget();
        if (this.weaponPanel) {
          this.weaponPanel.setSelectedWeapon(this.weaponSelection.getSelectedWeaponId() ?? '');
        }
      },
      onRequiresTargeting: (_weapon) => {
        // Pre-selecting a targeting weapon off turn must not open the
        // targeting cursor; it opens when the turn starts (server restores
        // the weapon and the client mirrors it).
        if (this.networkManager && !this.isMyTurn()) return;
        this.enterTargetingMode();
      },
      playSelectSound: () => {
        soundManager.play('cursorSelect');
      },
      getCurrentWormInfo: () => {
        const worm = this.state.getCurrentWorm();
        return worm ? { wormId: worm.id, teamId: worm.teamId } : null;
      },
    });

    this.chargeState.setCallbacks({
      playChargeSound: (type) => {
        if (type === 'thrown') {
          soundManager.play('throwPowerUp');
        } else {
          soundManager.play('rocketPowerUp');
        }
      },
      stopChargeSound: () => {
        soundManager.stop('throwPowerUp');
        soundManager.stop('rocketPowerUp');
      },
      emitChargeParticles: (x, y) => {
        this.particles.emitChargeTrail(x, y);
      },
    });

    this.toolSystem.setCallbacks({
      destroyTerrainCircle: (x, y, radius) => {
        this.destroyTerrainCircle(x, y, radius);
      },
      playSound: (sound) => {
        soundManager.play(sound);
      },
      onTurnEnd: () => {
        this.state.onWeaponFired(true);
      },
    });

    this.eventBus.on('tool:deactivated', () => {
      const worm = this.state.getCurrentWorm();
      if (worm) {
        worm.immuneToFallDamage = false;
      }
    });

    this.inputManager.setCallbacks({
      onAction: (action: InputAction, data?: unknown) => {
        this.handleInputAction(action, data);
      },
      canProcessInput: () => {
        return !this.pauseMenu.isVisible() || false;
      },
      canControlWorm: () => {
        return this.isMyTurn();
      },
    });

    this.setupVirtualGamepad();
  }

  private setupVirtualGamepad(): void {
    if (!isMobileDevice()) return;

    this.virtualGamepad = new VirtualGamepad(document.body, {
      onStateChange: (state) => {
        if (this.pauseMenu.isVisible()) return;
        const movementPressed = state.left || state.right;
        if (movementPressed && (this.cameraMode === 'manual' || this.cameraMode === 'thrown')) {
          this.cameraMode = 'worm';
          this.thrownWorm = null;
        }
        this.input.left = state.left;
        this.input.right = state.right;
        this.input.up = state.up;
        this.input.down = state.down;
        this.sendHeldInput();
      },
      onJump: () => {
        if (this.pauseMenu.isVisible()) return;
        if (!this.isMyTurn() || this.waitingForCrates || !this.state.canMove()) return;
        if (this.networkManager) {
          this.sendAction({ type: 'jump' });
          this.predictJump('jump');
          return;
        }
        const jumpWorm = this.state.getCurrentWorm();
        if (jumpWorm && jumpWorm.isGrounded && !jumpWorm.hasPendingDamage()) {
          jumpWorm.jump(this.terrain);
          soundManager.playJump();
        }
      },
      onBackflip: () => {
        if (this.pauseMenu.isVisible()) return;
        if (!this.isMyTurn() || this.waitingForCrates || !this.state.canMove()) return;
        if (this.networkManager) {
          this.sendAction({ type: 'backflip' });
          this.predictJump('backflip');
          return;
        }
        const jumpWorm = this.state.getCurrentWorm();
        if (jumpWorm && jumpWorm.isGrounded && !jumpWorm.hasPendingDamage()) {
          jumpWorm.backflip(this.terrain);
          soundManager.playJump();
        }
      },
      onFireStart: () => {
        if (this.pauseMenu.isVisible()) return;
        if (this.networkManager) {
          if (this.isMyTurn()) {
            if (this.targetingSystem.isActive()) {
              const cursorPos = this.targetingSystem.getCursorPosition();
              if (this.weaponSelection.getCategory() === 'homing') {
                this.lockHomingTarget(cursorPos);
              } else {
                this.fireWeaponWithTarget(cursorPos);
              }
            } else {
              this.sendAction({ type: 'firePress' });
            }
          }
          return;
        }
        if (this.crateManager.canDropCrates()) {
          this.crateManager.dropAllCrates();
          return;
        }

        if (!this.isMyTurn()) return;

        if (this.targetingSystem.isActive()) {
          const cursorPos = this.targetingSystem.getCursorPosition();
          if (this.weaponSelection.getCategory() === 'homing') {
            this.lockHomingTarget(cursorPos);
          } else {
            this.fireWeaponWithTarget(cursorPos);
          }
          return;
        }

        const currentWorm = this.state.getCurrentWorm();
        if (currentWorm && isRopeActive(currentWorm)) {
          releaseRope(currentWorm);
          return;
        }
        if (currentWorm && isBungeeActive(currentWorm)) {
          releaseBungee(currentWorm);
          currentWorm.velocity.y = 0;
          this.state.onWeaponFired(true);
          return;
        }
        if (this.toolSystem.isActive()) {
          this.toolSystem.setFireHeld(true);
          return;
        }
        if (this.pendingSheepTurnEnd && this.tryDetonateSheep()) {
          return;
        }
        const jetWormStart = this.state.getCurrentWorm();
        if (jetWormStart?.jetpackActive) {
          this.input.charging = true;
          return;
        }
        if (!this.input.charging && this.state.canFire() && this.canFireWeapon()) {
          if (this.weaponSelection.hasWeaponSelected() && !this.weaponSelection.isPowerCharging()) {
            this.chargeState.setInstantCharge(100);
            this.fireWeapon();
          } else {
            this.input.charging = true;
            this.chargeState.startCharging();
          }
        }
      },
      onFireEnd: () => {
        if (this.pauseMenu.isVisible()) return;
        if (this.networkManager) {
          if (this.isMyTurn() && !this.targetingSystem.isActive()) {
            this.sendAction({ type: 'fireRelease' });
          }
          return;
        }
        if (this.targetingSystem.isActive()) return;
        if (this.toolSystem.isActive()) {
          this.toolSystem.setFireHeld(false);
          return;
        }
        if (this.input.charging && this.isMyTurn()) {
          const worm = this.state.getCurrentWorm();
          if (worm?.jetpackActive) {
            this.input.charging = false;
            return;
          }
          this.fireWeapon();
          this.input.charging = false;
        }
      },
      onWeaponToggle: () => {
        if (this.pauseMenu.isVisible()) return;
        const pos = this.getWeaponPanelPosition();
        this.weaponPanel.toggle(pos.x, pos.y);
      },
      onFuseChange: () => {
        if (this.pauseMenu.isVisible()) return;
        this.chargeState.cycleFuseTimer();
        this.sendAction({ type: 'cycleFuse' });
        this.virtualGamepad?.updateFuseTimer(this.chargeState.getFuseTimer());
      },
      onShootDragStart: () => {
        if (this.pauseMenu.isVisible()) return;
        if (!this.controlledWormForDrag()) return;
        this.dragShot = { power: 0 };
      },
      onShootDragMove: (power) => {
        if (this.dragShot) {
          this.dragShot.power = Math.round(power);
        }
      },
      onShootDragRelease: () => {
        this.releaseDragShot();
      },
      onShootDragCancel: () => {
        this.dragShot = null;
        this.dragShotGraphics?.clear();
      },
    });

    if (this.isDragFireMode()) {
      this.virtualGamepad.setDragShotLayout(true);
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.pauseMenu.isVisible()) return;
    if (!this.targetingSystem.isActive()) return;
    if (isMobileDevice()) return;

    const rect = this.app.canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPos = this.camera.screenToWorld(screenX, screenY);

    const gameX = worldPos.x - WA_PHYSICS.WORLD.CAMERA_PADDING;
    this.targetingSystem.setCursorPosition({ x: gameX, y: worldPos.y });
  }

  private handleClick(e: MouseEvent): void {
    if (this.pauseMenu.isVisible()) return;
    if (this.weaponJustSelected) return;

    if (this.crateManager.canDropCrates()) {
      this.crateManager.dropAllCrates();
      e.preventDefault();
      return;
    }

    if (!this.targetingSystem.isActive() || !this.weaponSelection.hasWeaponSelected()) return;
    if (!this.isMyTurn()) return;
    if (isMobileDevice()) return;

    const rect = this.app.canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPos = this.camera.screenToWorld(screenX, screenY);
    const gamePos = {
      x: worldPos.x - WA_PHYSICS.WORLD.CAMERA_PADDING,
      y: worldPos.y,
    };

    if (this.weaponSelection.getCategory() === 'homing') {
      this.lockHomingTarget(gamePos);
    } else {
      this.fireWeaponWithTarget(gamePos);
    }
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
    this.camera.setZoom(this.camera.getZoom() + zoomDelta);
  }

  private pinchStartDistance = 0;
  private pinchStartZoom = 1;

  private setupPinchZoom(): void {
    const canvas = this.app.canvas;

    canvas.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 2) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          this.pinchStartDistance = Math.sqrt(dx * dx + dy * dy);
          this.pinchStartZoom = this.camera.getZoom();
        }
      },
      { passive: true },
    );

    canvas.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length === 2) {
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (this.pinchStartDistance > 0) {
            const scale = distance / this.pinchStartDistance;
            this.camera.setZoom(this.pinchStartZoom * scale);
          }
        }
      },
      { passive: true },
    );

    canvas.addEventListener(
      'touchend',
      () => {
        this.pinchStartDistance = 0;
      },
      { passive: true },
    );
  }

  private setupCameraDrag(): void {
    const canvas = this.app.canvas;

    canvas.addEventListener('mousedown', (e) => {
      if (e.button === 0 && this.tryStartPowerDrag(e.clientX, e.clientY)) {
        e.preventDefault();
        return;
      }
      if (e.button === 0) {
        // Presses on the item bar are handled by its own pixi buttons.
        const point = this.toScreenPoint(e.clientX, e.clientY);
        if (this.itemBar?.containsPoint(point.x, point.y)) return;
        // Drag fire mode: any left-mouse drag charges the shot (power from
        // drag distance); the camera pans with the right mouse button.
        if (!this.weaponPanel.isVisible() && this.controlledWormForDrag()) {
          this.dragShot = { power: 0 };
          this.dragShotOrigin = e.clientX;
          e.preventDefault();
          return;
        }
      }
      if (
        e.button === 1 ||
        e.button === 2 ||
        (e.button === 0 && !this.targetingSystem.isActive())
      ) {
        this.isDraggingCamera = true;
        this.lastDragPos = { x: e.clientX, y: e.clientY };
        e.preventDefault();
      }
    });

    canvas.addEventListener('mousemove', (e) => {
      if (this.dragShot || this.barPress) {
        this.updatePowerDrag(e.clientX, e.clientY);
        return;
      }
      if (this.isDraggingCamera) {
        const dx = e.clientX - this.lastDragPos.x;
        const dy = e.clientY - this.lastDragPos.y;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
          const isTrackingProjectile =
            this.cameraMode === 'projectile' || this.cameraMode === 'homing';
          if (!isTrackingProjectile) {
            this.cameraMode = 'manual';
          }
        }
        this.camera.pan(-dx, -dy);
        this.lastDragPos = { x: e.clientX, y: e.clientY };
      }
    });

    canvas.addEventListener('mouseup', () => {
      if (this.dragShot || this.barPress) {
        this.releaseDragShot();
      }
      this.isDraggingCamera = false;
    });

    canvas.addEventListener('mouseleave', () => {
      this.dragShot = null;
      this.dragShotOrigin = null;
      this.barPress = null;
      this.dragShotGraphics?.clear();
      this.isDraggingCamera = false;
    });

    canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    let touchDragId: number | null = null;
    let lastTouchPos = { x: 0, y: 0 };

    canvas.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 1) {
          const touch = e.touches[0];
          if (this.tryStartPowerDrag(touch.clientX, touch.clientY)) {
            touchDragId = touch.identifier;
            // Suppress the synthesized mouse events that follow a tap:
            // the emulated mousedown/mouseup would re-run the bar press and
            // toggle the goal marker right back off.
            e.preventDefault();
            return;
          }
          // Taps on the item bar are handled by its own pixi buttons.
          const point = this.toScreenPoint(touch.clientX, touch.clientY);
          if (this.itemBar?.containsPoint(point.x, point.y)) return;
        }
        if (e.touches.length === 1 && !this.targetingSystem.isActive()) {
          const touch = e.touches[0];
          touchDragId = touch.identifier;
          lastTouchPos = { x: touch.clientX, y: touch.clientY };
          const isTrackingProjectile =
            this.cameraMode === 'projectile' || this.cameraMode === 'homing';
          if (!isTrackingProjectile) {
            this.cameraMode = 'manual';
          }
        }
      },
      { passive: false },
    );

    canvas.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length === 1 && touchDragId !== null) {
          const touch = Array.from(e.touches).find((t) => t.identifier === touchDragId);
          if (touch && (this.dragShot || this.barPress)) {
            this.updatePowerDrag(touch.clientX, touch.clientY);
            return;
          }
          if (touch) {
            const dx = touch.clientX - lastTouchPos.x;
            const dy = touch.clientY - lastTouchPos.y;
            this.camera.pan(-dx, -dy);
            lastTouchPos = { x: touch.clientX, y: touch.clientY };
          }
        }
      },
      { passive: true },
    );

    canvas.addEventListener(
      'touchend',
      (e) => {
        for (const touch of Array.from(e.changedTouches)) {
          if (touch.identifier === touchDragId) {
            if (this.dragShot || this.barPress) {
              this.releaseDragShot();
            }
            touchDragId = null;
          }
        }
      },
      { passive: true },
    );
  }

  private fireWeaponWithTarget(target: { x: number; y: number }): void {
    if (this.networkManager) {
      this.sendAction({ type: 'fireTarget', x: target.x, y: target.y });
      if (this.weaponSelection.isWeaponSelected('girder')) {
        this.girderPreviewGraphics.visible = false;
      }
      return;
    }

    const worm = this.state.getCurrentWorm();
    if (!worm) return;

    const success = this.weaponExecutor.fireWithTarget(worm, target);
    if (success && this.weaponSelection.isWeaponSelected('girder')) {
      this.girderPreviewGraphics.visible = false;
    }
  }

  private selectWeapon(weaponId: string): void {
    this.weaponSelection.select(weaponId);
    // Offline authority: remember per worm (online the server owns this).
    if (!this.networkManager) {
      const worm = this.state.getCurrentWorm();
      if (worm) worm.lastWeaponId = weaponId;
    }
  }

  private enterTargetingMode(): void {
    const worm = this.state.getCurrentWorm();
    const weaponId = this.weaponSelection.getSelectedWeaponId() ?? '';
    const initialPos = worm ? { x: worm.position.x, y: worm.position.y - 100 } : { x: 0, y: 0 };
    this.targetingSystem.enter(initialPos, weaponId, worm?.id ?? '');
  }

  private exitTargetingMode(): void {
    this.targetingSystem.exit();
  }

  private lockHomingTarget(target: { x: number; y: number }): void {
    this.targetingSystem.lockTarget(target);
    this.sendAction({ type: 'lockTarget', x: target.x, y: target.y });
  }

  private rotateGirder(direction: number): void {
    const newIndex = this.girderAngleIndex + direction;
    if (newIndex >= GIRDER_ANGLES.length) {
      // Completed full angle rotation forward - cycle to next size, reset angle
      this.girderAngleIndex = 0;
      this.girderSizeIndex = (this.girderSizeIndex + 1) % GIRDER_SIZES.length;
    } else if (newIndex < 0) {
      // Completed full angle rotation backward - cycle to previous size, wrap angle
      this.girderAngleIndex = GIRDER_ANGLES.length - 1;
      this.girderSizeIndex = (this.girderSizeIndex - 1 + GIRDER_SIZES.length) % GIRDER_SIZES.length;
    } else {
      this.girderAngleIndex = newIndex;
    }
    this.sendAction({
      type: 'girderConfig',
      angleIndex: this.girderAngleIndex,
      sizeIndex: this.girderSizeIndex,
    });
    this.updateGirderPreview();
  }

  private updateGirderPreview(): void {
    if (!this.girderPreviewGraphics) return;

    const angle = GIRDER_ANGLES[this.girderAngleIndex];
    const size = GIRDER_SIZES[this.girderSizeIndex];
    const angleRad = (angle * Math.PI) / 180;
    const halfLength = size.length / 2;
    const halfThickness = size.thickness / 2;

    this.girderPreviewGraphics.clear();
    this.girderPreviewGraphics.rect(-halfLength, -halfThickness, size.length, size.thickness);
    this.girderPreviewGraphics.fill({ color: 0x8b4513, alpha: 0.7 });
    this.girderPreviewGraphics.stroke({
      color: 0xffffff,
      width: 1,
      alpha: 0.5,
    });

    this.girderPreviewGraphics.rotation = angleRad;
    const cursorPos = this.targetingSystem.getCursorPosition();
    this.girderPreviewGraphics.position.set(cursorPos.x, cursorPos.y);
    this.girderPreviewGraphics.visible =
      this.targetingSystem.isActive() && this.weaponSelection.isWeaponSelected('girder');
  }

  private placeGirder(x: number, y: number): void {
    const angle = GIRDER_ANGLES[this.girderAngleIndex];
    const ctx = this.terrainCanvas.getContext('2d');
    if (ctx) {
      drawGirderOnTerrain(ctx, x, y, angle, this.girderSizeIndex);
      // Mirror onto the authoritative collision mask.
      const maskCtx = new MaskCanvas(
        this.terrain.getWidth(),
        this.terrain.getHeight(),
        this.terrain.getAlphaData(),
      );
      drawGirderOnTerrain(maskCtx, x, y, angle, this.girderSizeIndex);
      this.terrainDirty = true;
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (this.pauseMenu.isVisible() && e.code !== 'Escape') {
      return;
    }

    const canControl = this.isMyTurn();

    switch (e.code) {
      case 'ArrowLeft':
        this.cameraPanInput.left = true;
        break;
      case 'ArrowRight':
        this.cameraPanInput.right = true;
        break;
      case 'ArrowUp':
        this.cameraPanInput.up = true;
        break;
      case 'ArrowDown':
        this.cameraPanInput.down = true;
        break;
      case 'KeyA':
        this.input.left = true;
        this.sendHeldInput();
        if (this.cameraMode === 'manual' || this.cameraMode === 'thrown') {
          this.cameraMode = 'worm';
          this.thrownWorm = null;
        }
        break;
      case 'KeyD':
        this.input.right = true;
        this.sendHeldInput();
        if (this.cameraMode === 'manual' || this.cameraMode === 'thrown') {
          this.cameraMode = 'worm';
          this.thrownWorm = null;
        }
        break;
      case 'KeyW':
        this.input.up = true;
        this.sendHeldInput();
        if (
          this.targetingSystem.isActive() &&
          this.weaponSelection.isWeaponSelected('girder') &&
          !e.repeat
        ) {
          this.rotateGirder(-1);
        }
        break;
      case 'KeyS':
        this.input.down = true;
        this.sendHeldInput();
        if (
          this.targetingSystem.isActive() &&
          this.weaponSelection.isWeaponSelected('girder') &&
          !e.repeat
        ) {
          this.rotateGirder(1);
        }
        break;
      case 'Enter': {
        if (!canControl || this.waitingForCrates || !this.state.canMove()) break;
        if (e.repeat) break;
        const now = Date.now();
        const jumpWorm = this.state.getCurrentWorm();
        if (jumpWorm && jumpWorm.isGrounded && !jumpWorm.hasPendingDamage()) {
          if (this.pendingJump && now - this.lastJumpTime < this.DOUBLE_TAP_THRESHOLD) {
            this.pendingJump = false;
            this.lastJumpTime = 0;
            if (this.networkManager) {
              this.sendAction({ type: 'backflip' });
              this.predictJump('backflip');
            } else {
              jumpWorm.backflip(this.terrain);
              soundManager.playJump();
            }
          } else {
            this.lastJumpTime = now;
            this.pendingJump = true;
            if (!this.networkManager) {
              jumpWorm.state = 'jumping';
            }
          }
        }
        break;
      }
      case 'Space': {
        if (this.networkManager) {
          if (canControl && !e.repeat) {
            this.sendAction({ type: 'firePress' });
          }
          break;
        }
        if (this.crateManager.canDropCrates()) {
          this.crateManager.dropAllCrates();
          break;
        }
        if (!canControl) break;
        if (this.targetingSystem.isActive() && this.weaponSelection.isWeaponSelected('girder')) {
          this.fireWeaponWithTarget(this.targetingSystem.getCursorPosition());
          break;
        }
        const currentWorm = this.state.getCurrentWorm();
        if (currentWorm && currentWorm.parachuteOpen) {
          currentWorm.closeParachute();
          break;
        }
        if (currentWorm && isRopeActive(currentWorm)) {
          releaseRope(currentWorm);
          break;
        }
        if (currentWorm && isBungeeActive(currentWorm)) {
          releaseBungee(currentWorm);
          currentWorm.velocity.y = 0;
          this.state.onWeaponFired(true);
          break;
        }
        if (this.toolSystem.isActive()) {
          this.toolSystem.setFireHeld(true);
          break;
        }
        if (this.pendingSheepTurnEnd && this.tryDetonateSheep()) {
          break;
        }
        if (this.tryActivateSuperSheep()) {
          break;
        }
        if (!this.input.charging && this.state.canFire() && this.canFireWeapon()) {
          if (this.weaponSelection.hasWeaponSelected() && !this.weaponSelection.isPowerCharging()) {
            this.chargeState.setInstantCharge(100);
            this.fireWeapon();
          } else {
            this.input.charging = true;
            this.chargeState.startCharging();
          }
        }
        break;
      }
      case 'Digit1':
        this.selectWeaponWithNetwork('grenade');
        break;
      case 'Digit2':
        this.selectWeaponWithNetwork('bazooka');
        break;
      case 'Digit3':
        this.selectWeaponWithNetwork('dynamite');
        break;
      case 'Digit4':
        this.selectWeaponWithNetwork('cluster');
        break;
      case 'Digit5':
        this.selectWeaponWithNetwork('shotgun');
        break;
      case 'Digit6':
        this.selectWeaponWithNetwork('rope');
        break;
      case 'Digit7':
        this.selectWeaponWithNetwork('airstrike');
        break;
      case 'Digit8':
        this.selectWeaponWithNetwork('mine');
        break;
      case 'Digit9':
        this.selectWeaponWithNetwork('teleport');
        break;
      case 'Escape':
        if (this.pauseMenu.isVisible()) {
          this.pauseMenu.hide();
        } else if (this.weaponPanel.isVisible()) {
          this.weaponPanel.hide();
        } else if (this.targetingSystem.isActive()) {
          this.exitTargetingMode();
        } else {
          this.pauseMenu.show();
        }
        break;
      case 'Home':
        this.cameraMode = 'worm';
        break;
      case 'Tab': {
        e.preventDefault();
        if (e.shiftKey && this.statsPanel) {
          this.statsPanel.toggle();
        } else {
          const pos = this.getWeaponPanelPosition();
          this.weaponPanel.toggle(pos.x, pos.y);
        }
        break;
      }
      case 'Digit0':
        this.selectWeaponWithNetwork('parachute');
        break;
      case 'KeyH':
        this.selectWeaponWithNetwork('homing');
        break;
      case 'KeyF':
        this.chargeState.cycleFuseTimer();
        this.sendAction({ type: 'cycleFuse' });
        break;
      case 'Backspace': {
        e.preventDefault();
        const pos = this.getWeaponPanelPosition();
        this.weaponPanel.toggle(pos.x, pos.y);
        break;
      }
      case 'KeyQ': {
        if (!canControl) break;
        if (this.networkManager) {
          this.sendAction({ type: 'ropeExtend', delta: -10 });
          break;
        }
        const ropeWorm = this.state.getCurrentWorm();
        if (ropeWorm && isRopeActive(ropeWorm)) {
          extendRope(ropeWorm, -10);
        }
        break;
      }
      case 'KeyE': {
        if (!canControl) break;
        if (this.networkManager) {
          this.sendAction({ type: 'ropeExtend', delta: 10 });
          break;
        }
        const extendWorm = this.state.getCurrentWorm();
        if (extendWorm && isRopeActive(extendWorm)) {
          extendRope(extendWorm, 10);
        }
        break;
      }
    }
  }

  private selectWeaponWithNetwork(weaponId: string): void {
    this.selectWeapon(weaponId);
    if (!this.networkManager) return;
    if (this.isMyTurn()) {
      this.sendAction({ type: 'selectWeapon', weaponId });
      return;
    }
    // Off turn: pre-select for the next turn of every team we own. The
    // server stores it in its per-team weapon memory and restores it when
    // that team's turn starts.
    if (this.session && !this.isSpectator()) {
      const player = this.session.getLocalPlayer();
      for (const team of this.state.teams) {
        if (player.ownsTeam(team.id)) {
          this.networkManager.sendPlayerAction({
            type: 'selectWeapon',
            weaponId,
            teamId: team.id,
          });
        }
      }
    }
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (this.pauseMenu.isVisible()) {
      return;
    }

    const canControl = this.isMyTurn();

    switch (e.code) {
      case 'ArrowLeft':
        this.cameraPanInput.left = false;
        break;
      case 'ArrowRight':
        this.cameraPanInput.right = false;
        break;
      case 'ArrowUp':
        this.cameraPanInput.up = false;
        break;
      case 'ArrowDown':
        this.cameraPanInput.down = false;
        break;
      case 'KeyA':
        this.input.left = false;
        this.sendHeldInput();
        break;
      case 'KeyD':
        this.input.right = false;
        this.sendHeldInput();
        break;
      case 'KeyW':
        this.input.up = false;
        this.sendHeldInput();
        break;
      case 'KeyS':
        this.input.down = false;
        this.sendHeldInput();
        break;
      case 'Enter':
        break;
      case 'Space': {
        if (this.networkManager) {
          if (canControl) {
            this.sendAction({ type: 'fireRelease' });
          }
          break;
        }
        if (this.toolSystem.isActive()) {
          this.toolSystem.setFireHeld(false);
          break;
        }
        const jetWorm = this.state.getCurrentWorm();
        if (jetWorm?.jetpackActive) {
          this.input.charging = false;
          break;
        }
        if (this.input.charging && canControl) {
          this.fireWeapon();
          this.input.charging = false;
        }
        break;
      }
      case 'KeyR': {
        if (!canControl) break;
        if (this.networkManager) {
          this.sendAction({ type: 'releaseRope' });
          break;
        }
        const releaseWorm = this.state.getCurrentWorm();
        if (releaseWorm && isRopeActive(releaseWorm)) {
          releaseRope(releaseWorm);
        }
        break;
      }
    }
  }

  private handleInputAction(action: InputAction, data?: unknown): void {
    switch (action) {
      case 'moveLeft':
      case 'moveRight':
        if (this.cameraMode === 'manual' || this.cameraMode === 'thrown') {
          this.cameraMode = 'worm';
          this.thrownWorm = null;
        }
        break;
      case 'weaponSelect':
        if (typeof data === 'string') {
          this.selectWeaponWithNetwork(data);
        }
        break;
      case 'weaponToggle': {
        const pos = this.getWeaponPanelPosition();
        this.weaponPanel.toggle(pos.x, pos.y);
        break;
      }
      case 'fuseChange':
        this.chargeState.cycleFuseTimer();
        this.sendAction({ type: 'cycleFuse' });
        break;
      case 'cameraReset':
        this.cameraMode = 'worm';
        break;
    }
  }

  private canFireWeapon(): boolean {
    if (!this.weaponSelection.hasWeaponSelected()) return false;
    // No ammo for the selected weapon -> can't fire, so don't even let the
    // player start a drag/charge (-1 means unlimited ammo).
    const weapon = this.weaponSelection.getSelectedWeapon();
    const team = this.state.getCurrentTeam();
    if (weapon && team && team.getAmmo(weapon.id) === 0) return false;
    const maxUses = this.weaponSelection.getMaxUsesPerTurn();
    if (maxUses === -1) return true;
    return this.state.getShotsFired() < maxUses;
  }

  private fireWeapon(): void {
    const worm = this.state.getCurrentWorm();
    if (!worm) return;

    const result = this.weaponExecutor.fire(worm);
    if (result === 'delayed') {
      soundManager.play('shotgunReload');
      const weapon = this.weaponSelection.getSelectedWeapon();
      const power = this.chargeState.releaseCharge();
      if (weapon) {
        setTimeout(() => {
          this.weaponExecutor.executeWeaponFire(worm, weapon, power);
        }, 400);
      }
    }
  }

  private tryDetonateSheep(): boolean {
    return this.weaponExecutor.tryDetonateSheep();
  }

  private tryActivateSuperSheep(): boolean {
    return this.weaponExecutor.tryActivateSuperSheep();
  }

  private activateTool(tool: 'drill' | 'blowtorch'): void {
    const worm = this.state.getCurrentWorm();
    if (worm && tool === 'drill') {
      worm.immuneToFallDamage = true;
    }
    this.toolSystem.activate(tool);
  }

  private updateWeaponPanelAmmo(team: Team): void {
    for (const [weaponId, ammo] of team.inventory) {
      this.weaponPanel.updateAmmo(weaponId, ammo);
    }
  }

  private playWeaponSound(weaponId: string): void {
    switch (weaponId) {
      case 'bazooka':
        soundManager.play('bazookaFire');
        break;
      case 'shotgun':
        soundManager.play('shotgunFire');
        break;
      case 'grenade':
      case 'cluster':
      case 'grub':
      case 'grubSS':
        soundManager.play('grenadeThrow');
        break;
      case 'aduka':
      case 'nak':
      case 'adukaSS':
      case 'lightningT1':
      case 'lightningT2':
      case 'knight':
      case 'jdT2':
      case 'jdSS':
        soundManager.play('bazookaFire');
        break;
      case 'trico':
      case 'kalsiddon':
        soundManager.play('grenadeThrow');
        break;
      case 'frogLeft':
      case 'frogRight':
        soundManager.play('grenadeThrow');
        break;
      case 'raon':
        soundManager.play('grenadeThrow');
        break;
      case 'dynamite':
        soundManager.play('grenadeThrow');
        break;
      case 'mine':
        soundManager.play('mineArm');
        break;
      case 'rope':
        soundManager.play('ropeShoot');
        break;
      case 'teleport':
        soundManager.play('teleport');
        break;
      case 'airstrike':
        soundManager.play('airstrike');
        break;
      case 'homing':
        soundManager.play('bazookaFire');
        break;
      case 'parachute':
        soundManager.play('parachute');
        break;
      case 'firePunch':
        soundManager.play('firePunchImpact');
        break;
      case 'baseballBat':
        soundManager.play('baseballHit');
        break;
      case 'holyHandGrenade':
        break;
      case 'sheep':
        break;
      case 'girder':
        soundManager.play('girderPlace');
        break;
      case 'jetpack':
        break;
    }
  }

  private addProjectile(projectile: Projectile): void {
    const result = this.entityFactory.addProjectile(projectile);

    if (result.shouldTrack) {
      this.trackedProjectile = projectile;
      this.cameraMode = 'projectile';
    }

    const { sprite, scale } = this.spriteManager.createProjectileSprite(projectile.type);
    if (sprite instanceof Sprite) {
      sprite.scale.set(scale);
    }
    sprite.position.set(projectile.position.x, projectile.position.y);
    this.projectileSprites.set(projectile.id, sprite);
    this.layers.projectiles.addChild(sprite);
  }

  private addHomingMissile(missile: HomingMissile): void {
    const result = this.entityFactory.addHomingMissile(missile);

    if (result.shouldTrack) {
      this.trackedHomingMissile = missile;
      this.cameraMode = 'homing';
    }

    const container = this.spriteManager.createHomingMissileSprite();
    container.position.set(missile.position.x, missile.position.y);
    this.homingSprites.set(missile.id, container);
    this.layers.projectiles.addChild(container);
  }

  private addGrave(grave: Grave): void {
    this.entityFactory.addGrave(grave);

    const sprite = this.spriteManager.createGraveSprite();
    sprite.position.set(grave.position.x, grave.position.y);
    this.graveSprites.set(grave.id, sprite);
    this.layers.entities.addChild(sprite);
  }

  private addMine(mine: Mine): void {
    this.entityFactory.addMine(mine);

    const sprite = this.spriteManager.createMineSprite();
    sprite.position.set(mine.position.x, mine.position.y);
    this.mineSprites.set(mine.id, sprite);
    this.layers.projectiles.addChild(sprite);
  }

  private addRaon(raon: MiniRaon): void {
    this.entityFactory.addRaon(raon);

    const originalOnExplode = raon.onExplode;
    raon.onExplode = (x, y) => {
      originalOnExplode?.(x, y);
      if (this.trackedRaon === raon) {
        this.trackedRaon = null;
        this.cameraMode = 'manual';
      }
    };

    // Follow the freshly-fired bot through its arc (the camera returns to the
    // worm at the next turn start, like other thrown weapons).
    this.trackedRaon = raon;
    this.cameraMode = 'raon';

    const sprite = this.spriteManager.createRaonSprite();
    sprite.position.set(raon.position.x + 8, raon.position.y + 7);
    this.raonSprites.set(raon.id, sprite);
    this.layers.projectiles.addChild(sprite);
  }

  private addSheep(sheep: Sheep): void {
    const result = this.entityFactory.addSheep(sheep);

    const originalOnExplode = sheep.onExplode;
    sheep.onExplode = (x, y) => {
      originalOnExplode?.(x, y);
      if (this.pendingSheepTurnEnd) {
        this.pendingSheepTurnEnd = false;
        this.state.onWeaponFired(true);
      }
      if (this.trackedSheep === sheep) {
        this.trackedSheep = null;
        this.cameraMode = 'manual';
      }
    };

    if (result.shouldTrack) {
      this.trackedSheep = sheep;
      this.cameraMode = 'sheep';
    }

    const sprite = this.spriteManager.createSheepSprite(sheep.kind);
    sprite.position.set(sheep.position.x + 8, sheep.position.y + 6);
    this.sheepSprites.set(sheep.id, sprite);
    this.layers.projectiles.addChild(sprite);
  }

  private addSuperSheep(superSheep: SuperSheep): void {
    const result = this.entityFactory.addSuperSheep(superSheep);

    const originalOnExplode = superSheep.onExplode;
    superSheep.onExplode = (x, y) => {
      originalOnExplode?.(x, y);
      if (this.trackedSuperSheep === superSheep) {
        this.trackedSuperSheep = null;
        this.cameraMode = 'manual';
      }
      this.state.onWeaponFired(true);
    };

    if (result.shouldTrack) {
      this.trackedSuperSheep = superSheep;
      this.cameraMode = 'supersheep';
    }

    const container = this.spriteManager.createSuperSheepSprite();
    container.position.set(superSheep.position.x + 8, superSheep.position.y + 6);
    this.superSheepSprites.set(superSheep.id, container);
    this.layers.projectiles.addChild(container);
  }

  private addKamikaze(kamikaze: Kamikaze, sourceWorm: Worm): void {
    const wormSprite = this.wormSprites.get(sourceWorm.id);
    if (wormSprite) {
      wormSprite.setVisible(false);
    }

    const result = this.entityFactory.addKamikaze(kamikaze, sourceWorm);

    const angleIndex = this.spriteManager.getKamikazeAngleIndex(
      kamikaze.directionX,
      kamikaze.directionY,
    );
    const { container, hasTextures, textures } =
      this.spriteManager.createKamikazeSprite(angleIndex);

    if (hasTextures && textures) {
      const kamiSprite = container.children.find((c) => c.label === 'kami') as Sprite;
      if (kamiSprite) {
        kamiSprite.tint = result.teamColor;
      }
      (
        container as Container & {
          frameIndex: number;
          textures: Texture[];
          frameTime: number;
        }
      ).frameIndex = 0;
      (
        container as Container & {
          frameIndex: number;
          textures: Texture[];
          frameTime: number;
        }
      ).textures = textures;
      (
        container as Container & {
          frameIndex: number;
          textures: Texture[];
          frameTime: number;
        }
      ).frameTime = 0;
    }

    const facingLeft = kamikaze.directionX < 0;
    container.scale.x = facingLeft ? -1 : 1;

    container.position.set(kamikaze.position.x, kamikaze.position.y);
    this.kamikazeSprites.set(kamikaze.id, container);
    this.layers.projectiles.addChild(container);
  }

  private addNapalmDroplet(droplet: NapalmDroplet): void {
    this.entityFactory.addNapalmDroplet(droplet);

    const sprite = this.spriteManager.createNapalmDropletSprite();
    sprite.position.set(droplet.position.x, droplet.position.y);
    this.napalmDropletSprites.set(droplet.id, sprite);
    this.layers.projectiles.addChild(sprite);
  }

  private addBarrel(barrel: OilBarrel): void {
    this.entityFactory.addBarrel(barrel);

    const displayObject = this.spriteManager.createBarrelSprite();
    displayObject.position.set(barrel.position.x, barrel.position.y + 12);
    this.barrelSprites.set(barrel.id, displayObject);
    this.layers.entities.addChild(displayObject);
  }

  getWaterLevel(): number {
    return this.currentWaterLevel;
  }

  createExplosion(
    x: number,
    y: number,
    radius: number,
    maxDamage: number,
    fromNetwork = false,
    directHitWorm?: Worm,
    pushMultiplier = 1,
    skipTerrain = false,
    magnet?: { radius: number; strength: number },
  ): void {
    this.explosionManager.createExplosion(
      x,
      y,
      radius,
      maxDamage,
      fromNetwork,
      directHitWorm,
      pushMultiplier,
      skipTerrain,
      magnet,
    );
  }

  private spawnFire(centerX: number, centerY: number, count: number): void {
    for (let i = 0; i < count; i++) {
      const offsetX = getGameRNG().nextFloat(-30, 30);
      const offsetY = getGameRNG().nextFloat(-15, 15);
      const fireX = centerX + offsetX;
      let fireY = centerY + offsetY;

      for (let j = 0; j < 50 && !this.terrain.isSolid(fireX, fireY + 1); j++) {
        fireY++;
      }

      if (fireY < WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel) {
        this.addFire(fireX, fireY);
      }
    }
  }

  private addFire(x: number, y: number): void {
    const fire = new Fire({
      x,
      y,
      duration: getGameRNG().nextFloat(4, 7),
      damagePerSecond: 5,
      spreadChance: 0.15,
      burnRadius: 3,
      burnInterval: 0.5,
    });

    const onSpriteRemove = (fireId: string) => {
      const sprite = this.fireSprites.get(fireId);
      if (sprite) {
        this.layers.effects.removeChild(sprite);
        this.fireSprites.delete(fireId);
      }
    };

    this.entityFactory.addFire(fire, onSpriteRemove);

    fire.onFall = () => {
      if (this.terrain.isSolid(fire.position.x, fire.position.y + 1)) {
        fire.velocity.y = 0;
        while (this.terrain.isSolid(fire.position.x, fire.position.y)) {
          fire.position.y -= 1;
        }
      }

      if (fire.position.y > WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel) {
        fire.extinguish();
      }

      const sprite = this.fireSprites.get(fire.id);
      if (sprite) {
        sprite.position.y = fire.position.y;
      }
    };

    const sprite = this.spriteManager.createFireSprite();
    sprite.position.set(x, y);
    this.fireSprites.set(fire.id, sprite);
    this.layers.effects.addChild(sprite);
  }

  private isMyTurn(): boolean {
    if (!this.session) return true;
    if (this.isSpectator()) return false;
    const currentTeam = this.state.getCurrentTeam();
    if (!currentTeam) return false;
    return this.session.isLocalPlayerTurn(currentTeam.id);
  }

  /** True when the worm belongs to a team owned by the local player. */
  private isOwnTeamWorm(worm: Worm): boolean {
    if (!this.session) return false;
    return this.session.getLocalPlayer().ownsTeam(worm.teamId);
  }

  /**
   * The team whose arsenal the weapon panel should show. On your turn it is
   * the acting team; online and off-turn it is your own (first owned) team so
   * you always see and can pre-select YOUR weapons, never the opponent's.
   * Offline / spectating falls back to the acting team.
   */
  private getPanelTeam(): Team | null {
    if (this.networkManager && this.session && !this.isSpectator() && !this.isMyTurn()) {
      const player = this.session.getLocalPlayer();
      const mine = this.state.teams.find((t) => player.ownsTeam(t.id));
      if (mine) return mine;
    }
    return this.state.getCurrentTeam();
  }

  /**
   * Keep the weapon panel's membership pinned to the controlled team
   * (getPanelTeam). Rebuilds only when that team changes, so on another
   * player's turn the menu shows your own tank's weapons + ammo.
   */
  private syncWeaponPanelTeam(): void {
    if (!this.weaponPanel || !this.settings.mode.tankSelection) return;
    const team = this.getPanelTeam();
    if ((team?.id ?? null) !== this.lastPanelTeamId) {
      this.refreshWeaponPanelForTeam(team);
    }
    // Membership rebuilds are rare (online the controlled team is stable), so
    // keep ammo counts live while the panel is actually open.
    if (team && this.weaponPanel.isVisible()) {
      this.updateWeaponPanelAmmo(team);
    }
  }

  /**
   * The local player's own worms while it is NOT their turn (online only):
   * the worm each owned team would act with next. These may be pre-aimed.
   */
  private myOffTurnWorms(): Worm[] {
    if (!this.networkManager || !this.session) return [];
    if (this.isSpectator() || this.isMyTurn()) return [];
    const player = this.session.getLocalPlayer();
    const worms: Worm[] = [];
    for (const team of this.state.teams) {
      if (!player.ownsTeam(team.id)) continue;
      const worm = team.getCurrentWorm();
      if (worm && worm.state !== 'dead') {
        worms.push(worm);
      }
    }
    return worms;
  }

  /**
   * Off-turn aiming: W/S or the d-pad pre-aims the own worm while an
   * opponent acts. Client-local — snapshots skip the own worms' aim (see
   * applyStateDirectly) and prediction pushes the absolute aim to the
   * server as soon as the turn starts.
   */
  private updateOffTurnAim(deltaTime: number): void {
    if (this.state.phase !== 'playing') return;
    const steps = this.offTurnAimStepper.update(deltaTime, this.input.up, this.input.down);
    if (steps === 0) return;
    for (const worm of this.myOffTurnWorms()) {
      worm.adjustAim(steps * this.settings.mode.aim.stepDegrees);
    }
  }

  /** Online mode: forward a discrete intent to the server simulation. */
  private sendAction(action: PlayerAction): void {
    if (this.networkManager && this.isMyTurn()) {
      this.networkManager.sendPlayerAction(action);
    }
  }

  private sendHeldInput(): void {
    this.sendAction({
      type: 'input',
      left: this.input.left,
      right: this.input.right,
      up: this.input.up,
      down: this.input.down,
    });
  }

  private isSpectator(): boolean {
    if (!this.session) return false;
    return this.session.getLocalPlayer().isSpectator;
  }

  private update(deltaTime: number): void {
    // Skip update if pause menu is visible
    if (this.pauseMenu.isVisible()) {
      return;
    }

    this.gameTick++;

    // Online games are server-authoritative: render delayed, interpolated
    // server snapshots instead of simulating locally.
    if (this.networkManager) {
      this.updateNetworkSnapshots();
    }

    // Online mode: a single Enter tap becomes a jump once the double-tap
    // window (backflip) has passed.
    if (
      this.networkManager &&
      this.pendingJump &&
      Date.now() - this.lastJumpTime >= this.DOUBLE_TAP_THRESHOLD
    ) {
      this.pendingJump = false;
      this.sendAction({ type: 'jump' });
      this.predictJump('jump');
    }

    // The server is the sole authority for online games.
    const isAuthority = !this.networkManager;

    this.simulation.beginTick(isAuthority);
    const currentWorm = this.state.getCurrentWorm();

    if (currentWorm && currentWorm.id !== this.lastTurnWormId && this.state.phase === 'playing') {
      this.lastTurnWormId = currentWorm.id;
      try {
        const team = this.state.teams.find((t) => t.id === currentWorm.teamId);
        if (team) {
          this.commentary.showTurnStart(currentWorm.name, team.name);
          if (this.spectatorHUD) {
            this.spectatorHUD.updateCurrentTeam(team.name, team.color);
          }
          gameLogger.turnStarted(
            team.id,
            team.name,
            currentWorm.id,
            currentWorm.name,
            this.state.roundNumber,
          );
        }
      } catch (e) {
        /* commentary is non-critical */
      }
    }

    if (isAuthority) {
      this.simulation.applyAuthorityInput(deltaTime, this.input, currentWorm);
    } else if (this.networkManager) {
      this.simulation.updateTimersNonAuthority(deltaTime);
    }

    // Only the authority runs physics. Online clients render interpolated
    // server snapshots — local physics would fight the 40Hz corrections
    // (jitter, worms bouncing above the ground). The one exception is the
    // worm we control: it is predicted locally and reconciled to the server.
    if (isAuthority) {
      this.simulation.stepPhysics(deltaTime, true);
      this.simulation.updateDamageResolution(deltaTime);
    } else {
      this.updateLocalPrediction(deltaTime);
      this.updateOffTurnAim(deltaTime);
    }

    this.updateAnimations(deltaTime);
    this.particles.update(deltaTime);
    this.sky?.update(deltaTime);
    this.updateWaterVisual();
    this.updateScreenFlash(deltaTime);
    this.floatingDamage.update(deltaTime);
    this.commentary.update(deltaTime);

    if (this.cameraTransitionDelay > 0) {
      this.cameraTransitionDelay -= deltaTime;
    }

    this.simulation.updateDrownDelay(deltaTime, isAuthority);

    this.cameraController.setMode(this.cameraMode);
    this.cameraController.updatePan(deltaTime, this.cameraPanInput);

    const tracked: TrackedEntities = {
      projectile: this.trackedProjectile,
      homingMissile: this.trackedHomingMissile,
      thrownWorm: this.thrownWorm,
      sheep: this.trackedSheep,
      superSheep: this.trackedSuperSheep,
      raon: this.trackedRaon,
      resolvingWorm: this.resolvingWorm,
      crate: this.trackedCrate,
    };

    const targeting: TargetingState = this.targetingSystem.getState();

    if (this.cameraTransitionDelay > 0) {
      this.cameraTransitionDelay -= deltaTime;
    }

    this.cameraController.updateTarget(deltaTime, currentWorm, tracked, targeting);

    this.trackedProjectile = tracked.projectile;
    this.trackedHomingMissile = tracked.homingMissile;
    this.thrownWorm = tracked.thrownWorm;
    this.cameraMode = this.cameraController.getMode();

    this.camera.update();

    if (this.terrainDirty) {
      this.terrainDirty = false;
      this.updateTerrainTexture();
    }

    this.checkForGameOver();
  }

  private getTerrainFriction(): number {
    return WA_PHYSICS.TERRAIN_FRICTION[this.terrainTheme] ?? WA_PHYSICS.WORM.FRICTION;
  }

  private updateAnimations(deltaTime: number): void {
    for (const team of this.state.teams) {
      for (const worm of team.worms) {
        const animSprite = this.wormSprites.get(worm.id);
        if (animSprite) {
          const isMovingState =
            worm.state === 'walking' ||
            worm.state === 'jumping' ||
            worm.state === 'backflipping' ||
            worm.state === 'falling';
          const hasSignificantVelocity =
            Math.abs(worm.velocity.x) > 5 || Math.abs(worm.velocity.y) > 5;
          // Some tanks (the wizard) idle-bob continuously even when still.
          const shouldAnimate =
            isMovingState || hasSignificantVelocity || animSprite.idleAnimatesWhenStill;
          animSprite.setPaused(!shouldAnimate);
          animSprite.update(deltaTime);

          const flashTimer = this.wormHurtFlashTimers.get(worm.id);
          if (flashTimer !== undefined && flashTimer > 0) {
            const newTimer = flashTimer - deltaTime;
            if (newTimer <= 0) {
              this.wormHurtFlashTimers.delete(worm.id);
              animSprite.setTint(0xffffff);
            } else {
              this.wormHurtFlashTimers.set(worm.id, newTimer);
              const flashIntensity = Math.sin(newTimer * 20) * 0.5 + 0.5;
              const red = Math.floor(255);
              const green = Math.floor(255 * (1 - flashIntensity * 0.7));
              const blue = Math.floor(255 * (1 - flashIntensity * 0.7));
              animSprite.setTint((red << 16) | (green << 8) | blue);
            }
          }
        }
      }
    }
  }

  private flashWormHurt(worm: Worm): void {
    this.wormHurtFlashTimers.set(worm.id, 0.4);
  }

  /**
   * Consume buffered server snapshots at render time, INTERP_DELAY_MS in the
   * past, so entity positions can be interpolated between two consecutive
   * snapshots instead of snapping at the broadcast rate.
   */
  private updateNetworkSnapshots(): void {
    const renderTime = performance.now() - this.INTERP_DELAY_MS;

    while (
      this.networkStateBuffer.length > 0 &&
      this.networkStateBuffer[0].recvTime <= renderTime
    ) {
      const entry = this.networkStateBuffer.shift()!;
      if (entry.state.stateSeq <= this.highestAppliedStateSeq) continue;
      this.highestAppliedStateSeq = entry.state.stateSeq;
      this.applyStateDirectly(entry.state);
      this.lastAppliedNetSnapshot = entry;
    }

    const from = this.lastAppliedNetSnapshot;
    const next = this.networkStateBuffer[0];
    if (from && next && next.recvTime > from.recvTime) {
      const alpha = Math.max(
        0,
        Math.min(1, (renderTime - from.recvTime) / (next.recvTime - from.recvTime)),
      );
      this.interpolateEntityPositions(from.state, next.state, alpha);
    }
  }

  /** The worm we control and predict locally (online play only). */
  private getPredictedWorm(): Worm | null {
    if (!this.networkManager || !this.isMyTurn()) return null;
    if (this.state.phase !== 'playing' && this.state.phase !== 'retreat') return null;
    const worm = this.state.getCurrentWorm();
    if (!worm || worm.state === 'dead' || worm.hasPendingDamage()) return null;
    // Special movement modes stay fully server-driven.
    if (isRopeActive(worm) || isBungeeActive(worm) || worm.jetpackActive || worm.kamikazing)
      return null;
    return worm;
  }

  /** Locally execute a jump/backflip on the predicted worm for instant feedback. */
  private predictJump(kind: 'jump' | 'backflip'): void {
    const worm = this.getPredictedWorm();
    if (worm && worm.isGrounded) {
      if (kind === 'jump' ? worm.jump(this.terrain) : worm.backflip(this.terrain)) {
        soundManager.playJump();
      }
    }
  }

  /**
   * Client-side prediction: the locally-controlled worm applies inputs and
   * movement physics immediately, then is gently reconciled toward the
   * (slightly stale) authoritative server position.
   */
  private updateLocalPrediction(deltaTime: number): void {
    const predicted = this.getPredictedWorm();
    if (predicted?.id !== this.predictedWormId) {
      this.predictedWormId = predicted?.id ?? null;
      this.predictedWormServerPos = null;
      this.predictedWormServerSettled = false;
      this.lastSentAim = null;
    }
    if (!predicted) return;

    const inputDrivesWorm = !this.targetingSystem.isActive() && !this.toolSystem.isActive();
    if (inputDrivesWorm && this.state.canMove() && !this.waitingForCrates) {
      const otherWorms = this.getWorms().filter((w) => w.id !== predicted.id);
      const moveStep = WA_PHYSICS.WORM.WALK_SPEED / WA_PHYSICS.TICK_RATE;
      if (this.input.left || this.input.right) {
        const dir = this.input.left ? 'left' : 'right';
        if (this.state.tryConsumeMovement(moveStep)) {
          predicted.move(dir, this.terrain, otherWorms);
        } else {
          // Movement budget exhausted: the worm may still turn in place.
          predicted.facing = dir;
          predicted.stopWalking();
        }
      } else {
        predicted.stopWalking();
      }
      const aimSteps = this.clientAimStepper.update(deltaTime, this.input.up, this.input.down);
      if (aimSteps !== 0) {
        predicted.adjustAim(aimSteps * this.settings.mode.aim.stepDegrees);
      }
    }

    // Keep the server's facing/aim authoritative state in lockstep with the
    // prediction: held-input timing differences otherwise leave the server
    // facing the other way, mirroring shots for everyone else.
    if (
      this.lastSentAim?.facing !== predicted.facing ||
      this.lastSentAim?.angle !== predicted.aimAngle
    ) {
      this.lastSentAim = {
        facing: predicted.facing,
        angle: predicted.aimAngle,
      };
      this.sendAction({
        type: 'aim',
        facing: predicted.facing,
        angle: predicted.aimAngle,
      });
    }

    predicted.update(deltaTime, this.state.getWind(), this.terrain, this.getTerrainFriction());
    predicted.applyTerrainPhysics(this.terrain, this.getEntityPlatformBounds());

    // Barrels are solid for the predicted worm too (the server resolves
    // this; without it the worm passes through and gets snapped back, and
    // can never climb on top of a barrel).
    const barrelHit = checkWormBarrelCollision(
      predicted,
      this.entityRegistry.getAll<OilBarrel>('barrel'),
    );
    if (barrelHit) {
      resolveWormBarrelCollision(predicted, barrelHit);
    }
    // Slope-relative aim: keep the predicted worm's ground tilt current so the
    // local crosshair/arc rotates with the terrain (the server applies the
    // same tilt at fire time from the relative aim we send).
    predicted.updateGroundTilt(this.terrain);

    // Reconcile toward the latest authoritative position.
    const target = this.predictedWormServerPos;
    if (!target) return;
    const ex = target.x - predicted.position.x;
    const ey = target.y - predicted.position.y;
    const dist = Math.hypot(ex, ey);
    if (dist > 96) {
      // Way off (teleport / knockback we didn't simulate): snap.
      predicted.position.x = target.x;
      predicted.position.y = target.y;
      predicted.velocity.x = 0;
      predicted.velocity.y = 0;
      return;
    }
    // Only reconcile once BOTH sides have settled. While we are moving the
    // server target is stale; and right after we stop, the server is still
    // replaying our last inputs for ~RTT — its position is behind in time,
    // not in disagreement, and pulling toward it visibly rolls the worm
    // back. Once the server's view also stops moving, any remaining offset
    // is a real divergence (usually a pixel or two) and safe to correct.
    const activelyMoving =
      this.input.left ||
      this.input.right ||
      !predicted.isGrounded ||
      Math.abs(predicted.velocity.x) > 5 ||
      Math.abs(predicted.velocity.y) > 5;
    if (activelyMoving || !this.predictedWormServerSettled) return;
    if (dist <= 3) {
      // Small error: correct in a single frame (invisible at 60fps) instead
      // of gliding there.
      predicted.position.x = target.x;
      predicted.position.y = target.y;
    } else {
      // Larger error: smooth glide (~80ms time constant).
      const blend = 1 - Math.exp(-deltaTime * 12);
      predicted.position.x += ex * blend;
      predicted.position.y += ey * blend;
    }
  }

  /** Standable surfaces of active barrels/crates (mirrors PhysicsStep.getPlatformBounds). */
  private getEntityPlatformBounds(): Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> {
    const platforms: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
    }> = [];
    for (const barrel of this.entityRegistry.getAll<OilBarrel>('barrel')) {
      if (barrel.active) platforms.push(barrel.getBounds());
    }
    for (const crate of this.entityRegistry.getAll<Crate>('crate')) {
      if (crate.active) platforms.push(crate.getBounds());
    }
    return platforms;
  }

  /** Lerp live entity positions between two consecutive server snapshots. */
  private interpolateEntityPositions(
    a: GameStateSnapshot,
    b: GameStateSnapshot,
    alpha: number,
  ): void {
    const lerp = (x: number, y: number) => x + (y - x) * alpha;

    const prevWorms = new Map(a.worms.map((w) => [w.id, w]));
    for (const wb of b.worms) {
      const wa = prevWorms.get(wb.id);
      if (!wa) continue;
      if (wb.id === this.predictedWormId) continue;
      const worm = this.findWormById(wb.id);
      if (!worm || worm.state === 'dead') continue;
      worm.position.x = lerp(wa.position.x, wb.position.x);
      worm.position.y = lerp(wa.position.y, wb.position.y);
    }

    const entityTypes = [
      ['projectiles', 'projectile'],
      ['mines', 'mine'],
      ['barrels', 'barrel'],
      ['crates', 'crate'],
      ['napalmDroplets', 'napalmDroplet'],
      ['homingMissiles', 'homingMissile'],
      ['graves', 'grave'],
      ['sheep', 'sheep'],
      ['raons', 'raon'],
    ] as const;
    for (const [field, regType] of entityTypes) {
      const aList = a[field] as
        | Array<{ id: string; position: { x: number; y: number } }>
        | undefined;
      const bList = b[field] as
        | Array<{ id: string; position: { x: number; y: number } }>
        | undefined;
      if (!aList || !bList) continue;
      const prev = new Map(aList.map((e) => [e.id, e]));
      for (const eb of bList) {
        const ea = prev.get(eb.id);
        if (!ea) continue;
        const live = this.entityRegistry.get(regType, eb.id) as unknown as
          | { position: { x: number; y: number } }
          | undefined;
        if (!live) continue;
        live.position.x = lerp(ea.position.x, eb.position.x);
        live.position.y = lerp(ea.position.y, eb.position.y);
      }
    }
  }

  private applyStateDirectly(state: GameStateSnapshot): void {
    // Replay the server's terrain modification log strictly by server index.
    // Local event-driven destruction is just early feedback; re-applying the
    // same erase/draw is idempotent, so the log is the source of truth and
    // both clients converge to the exact server mask (including girders).
    if (
      state.terrainVersion !== undefined &&
      state.terrainModifications &&
      state.terrainVersion > this.appliedTerrainVersion
    ) {
      const missingMods = state.terrainModifications.slice(this.appliedTerrainVersion);
      for (const mod of missingMods) {
        if (mod.type === 'girder') {
          this.applyGirderToTerrain(mod.x, mod.y, mod.angleIndex ?? 0, mod.sizeIndex ?? 1);
        } else {
          this.terrain.applyModifications([mod]);
        }
      }
      if (missingMods.length > 0) {
        this.terrainDirty = true;
      }
      this.appliedTerrainVersion = state.terrainVersion;
    }

    for (const wormSync of state.worms) {
      const worm = this.findWormById(wormSync.id);
      if (worm) {
        const oldHealth = worm.health;
        const wasAlive = worm.state !== 'dead';

        if (worm.id === this.predictedWormId && wormSync.state !== 'dead') {
          // Locally predicted worm: keep our kinematics and remember the
          // server position as the reconciliation target. The target is only
          // trusted for corrections once it stops moving between snapshots
          // (i.e. the server has caught up with our recent inputs).
          const prev = this.predictedWormServerPos;
          this.predictedWormServerPos = {
            x: wormSync.position.x,
            y: wormSync.position.y,
          };
          this.predictedWormServerSettled =
            prev !== null &&
            Math.abs(prev.x - wormSync.position.x) < 0.5 &&
            Math.abs(prev.y - wormSync.position.y) < 0.5;
        } else {
          worm.position.x = wormSync.position.x;
          worm.position.y = wormSync.position.y;
          worm.velocity.x = wormSync.velocity.x;
          worm.velocity.y = wormSync.velocity.y;
          worm.state = wormSync.state;
          // Own worms keep their locally-set facing/aim: pre-aiming off
          // turn would otherwise be stomped by every snapshot (the local
          // aim is pushed to the server when the turn starts).
          if (!this.isOwnTeamWorm(worm)) {
            worm.facing = wormSync.facing;
            worm.aimAngle = wormSync.aimAngle;
          }
          worm.isGrounded = wormSync.isGrounded;
        }
        worm.health = wormSync.health;
        worm.pendingDamage = wormSync.pendingDamage;
        worm.parachuteOpen = wormSync.parachuteOpen;
        if (wormSync.rope && wormSync.rope.active) {
          (worm as any).ropeState = {
            active: wormSync.rope.active,
            anchorX: wormSync.rope.anchorX,
            anchorY: wormSync.rope.anchorY,
            length: wormSync.rope.length,
            angle: wormSync.rope.angle,
            angularVelocity: 0,
            attached: wormSync.rope.attached,
            dirX: wormSync.rope.dirX,
            dirY: wormSync.rope.dirY,
            speed: wormSync.rope.speed,
            maxLength: wormSync.rope.maxLength,
          };
        } else {
          (worm as any).ropeState = undefined;
        }

        const healthDelta = oldHealth - wormSync.health;
        if (healthDelta > 0) {
          soundManager.playDamage();
          this.flashWormHurt(worm);
          if (this.uiSettings.showDamageNumbers) {
            this.floatingDamage.spawn(
              worm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
              worm.position.y,
              healthDelta,
            );
          }
        }

        if (wasAlive && wormSync.state === 'dead') {
          soundManager.playDeath();
        }
      } else {
        gameLogger.networkWormNotFound(wormSync.id);
      }
    }

    this.applyTurnState(state);
    this.syncProjectilesFromState(state);
    this.syncMinesFromState(state);
    this.syncSheepFromState(state);
    this.syncRaonsFromState(state);
    this.syncBarrelsFromState(state);
    this.syncCratesFromState(state);
    this.syncNapalmDropletsFromState(state);
    this.syncHomingMissilesFromState(state);
    this.syncGravesFromState(state);
    this.syncFiresFromState(state);
    this.syncInventoriesFromState(state);
    this.syncStatsFromState(state);
    this.forceUpdateAllSprites();
  }

  private syncInventoriesFromState(state: GameStateSnapshot): void {
    if (!state.teamInventories) return;

    for (const invSync of state.teamInventories) {
      const team = this.state.teams.find((t) => t.id === invSync.teamId);
      if (team) {
        team.applyInventorySnapshot(invSync.inventory);
      }
    }

    const currentTeam = this.state.getCurrentTeam();
    if (currentTeam) {
      this.updateWeaponPanelAmmo(currentTeam);
    }
  }

  private syncStatsFromState(state: GameStateSnapshot): void {
    if (!state.teamStats || !this.statsPanel) return;

    this.statsPanel.setStats(
      state.teamStats.map((ts) => ({
        playerId: ts.teamId,
        playerName: ts.teamName,
        damageDealt: ts.damageDealt,
        kills: ts.kills,
        deaths: ts.deaths,
        shotsFired: ts.shotsFired,
        shotsHit: ts.shotsHit,
      })),
    );
  }

  private syncProjectilesFromState(state: GameStateSnapshot): void {
    const stateIds = new Set(state.projectiles.map((p) => p.id));

    for (const projSync of state.projectiles) {
      let proj = this.entityRegistry.get<Projectile>('projectile', projSync.id);
      if (!proj) {
        proj = new Projectile({
          x: projSync.position.x,
          y: projSync.position.y,
          vx: projSync.velocity.x,
          vy: projSync.velocity.y,
          type: projSync.type,
          timer: projSync.timer,
          explosionRadius: projSync.explosionRadius,
          maxDamage: projSync.maxDamage,
          ownerId: projSync.ownerId,
        });
        (proj as { id: string }).id = projSync.id;
        this.addProjectile(proj);
      } else {
        proj.position.x = projSync.position.x;
        proj.position.y = projSync.position.y;
        proj.velocity.x = projSync.velocity.x;
        proj.velocity.y = projSync.velocity.y;
        proj.timer = projSync.timer;
        proj.rotation = projSync.rotation;
        proj.isOnGround = projSync.isOnGround;
      }
    }

    for (const proj of this.entityRegistry.getAll<Projectile>('projectile')) {
      if (!stateIds.has(proj.id)) {
        proj.destroy();
      }
    }
    this.entityRegistry.removeInactive('projectile');
  }

  private syncMinesFromState(state: GameStateSnapshot): void {
    const stateIds = new Set(state.mines.map((m) => m.id));

    for (const mineSync of state.mines) {
      let mine = this.entityRegistry.get<Mine>('mine', mineSync.id);
      if (!mine) {
        mine = new Mine(mineSync.position.x, mineSync.position.y, '');
        (mine as { id: string }).id = mineSync.id;
        mine.armed = mineSync.armed;
        this.addMine(mine);
      } else {
        mine.position.x = mineSync.position.x;
        mine.position.y = mineSync.position.y;
        mine.velocity.x = mineSync.velocity.x;
        mine.velocity.y = mineSync.velocity.y;
        mine.armed = mineSync.armed;
      }
    }

    for (const mine of this.entityRegistry.getAll<Mine>('mine')) {
      if (!stateIds.has(mine.id)) {
        mine.explode();
      }
    }
    this.entityRegistry.removeInactive('mine');
  }

  /** Sheep + Frog (walking critters) from server snapshots. */
  private syncSheepFromState(state: GameStateSnapshot): void {
    const list = state.sheep ?? [];
    const stateIds = new Set(list.map((s) => s.id));

    for (const sync of list) {
      let sheep = this.entityRegistry.get<Sheep>('sheep', sync.id);
      if (!sheep) {
        sheep = new Sheep(sync.position.x, sync.position.y, sync.facing, sync.ownerTeamId, {
          kind: sync.kind,
          explosionRadius: sync.explosionRadius,
          maxDamage: sync.maxDamage,
        });
        (sheep as { id: string }).id = sync.id;
        sheep.isGrounded = sync.isGrounded;
        this.addSheep(sheep);
      } else {
        sheep.position.x = sync.position.x;
        sheep.position.y = sync.position.y;
        sheep.velocity.x = sync.velocity.x;
        sheep.velocity.y = sync.velocity.y;
        sheep.facing = sync.facing;
        sheep.isGrounded = sync.isGrounded;
      }
    }

    // Removed server-side: just drop it (the boom comes via an explosion event).
    for (const sheep of this.entityRegistry.getAll<Sheep>('sheep')) {
      if (!stateIds.has(sheep.id)) sheep.destroy();
    }
    this.entityRegistry.removeInactive('sheep');
  }

  /** MiniRaon penguin bots from server snapshots. */
  private syncRaonsFromState(state: GameStateSnapshot): void {
    const list = state.raons ?? [];
    const stateIds = new Set(list.map((r) => r.id));

    for (const sync of list) {
      let raon = this.entityRegistry.get<MiniRaon>('raon', sync.id);
      if (!raon) {
        raon = new MiniRaon(
          sync.position.x,
          sync.position.y,
          sync.facing,
          '',
          sync.ownerTeamId,
          sync.velocity.x,
          sync.velocity.y,
        );
        (raon as { id: string }).id = sync.id;
        raon.isGrounded = sync.isGrounded;
        raon.armed = sync.armed;
        this.addRaon(raon);
      } else {
        raon.position.x = sync.position.x;
        raon.position.y = sync.position.y;
        raon.velocity.x = sync.velocity.x;
        raon.velocity.y = sync.velocity.y;
        raon.facing = sync.facing;
        raon.isGrounded = sync.isGrounded;
        raon.armed = sync.armed;
      }
    }

    for (const raon of this.entityRegistry.getAll<MiniRaon>('raon')) {
      if (!stateIds.has(raon.id)) raon.destroy();
    }
    this.entityRegistry.removeInactive('raon');
  }

  private syncBarrelsFromState(state: GameStateSnapshot): void {
    if (!state.barrels) return;
    const stateIds = new Set(state.barrels.map((b) => b.id));

    for (const barrelSync of state.barrels) {
      let barrel = this.entityRegistry.get<OilBarrel>('barrel', barrelSync.id);
      if (!barrel) {
        barrel = new OilBarrel(barrelSync.position.x, barrelSync.position.y);
        (barrel as { id: string }).id = barrelSync.id;
        this.addBarrel(barrel);
      } else {
        barrel.position.x = barrelSync.position.x;
        barrel.position.y = barrelSync.position.y;
        barrel.velocity.x = barrelSync.velocity.x;
        barrel.velocity.y = barrelSync.velocity.y;
        barrel.isGrounded = barrelSync.isGrounded;
      }
    }

    for (const barrel of this.entityRegistry.getAll<OilBarrel>('barrel')) {
      if (!stateIds.has(barrel.id)) {
        barrel.destroy();
      }
    }
    this.entityRegistry.removeInactive('barrel');
  }

  private syncCratesFromState(state: GameStateSnapshot): void {
    if (!state.crates) return;
    const stateIds = new Set(state.crates.map((c) => c.id));

    for (const crateSync of state.crates) {
      let crate = this.entityRegistry.get<Crate>('crate', crateSync.id);
      if (!crate) {
        crate = new Crate(
          crateSync.position.x,
          crateSync.position.y,
          crateSync.type,
          crateSync.contents,
        );
        (crate as { id: string }).id = crateSync.id;
        this.crateManager.addCrate(crate);
      }
      crate.position.x = crateSync.position.x;
      crate.position.y = crateSync.position.y;
      crate.velocity.x = crateSync.velocity.x;
      crate.velocity.y = crateSync.velocity.y;
      crate.parachuteOpen = crateSync.parachuteOpen;
      crate.isGrounded = crateSync.isGrounded;
    }

    for (const crate of this.entityRegistry.getAll<Crate>('crate')) {
      if (!stateIds.has(crate.id)) {
        crate.destroy();
      }
    }
    this.entityRegistry.removeInactive('crate');

    // State-driven camera focus: any airborne crate grabs the camera while
    // it falls. Driven by snapshot state (not the spawn moment), so it also
    // works when a backgrounded tab missed the spawn and refocuses mid-fall.
    if (this.cameraMode === 'worm' || this.cameraMode === 'manual') {
      const falling = this.entityRegistry
        .getAll<Crate>('crate')
        .find((c) => c.active && stateIds.has(c.id) && !c.isGrounded);
      if (falling) {
        this.trackedCrate = falling;
        this.cameraMode = 'crate';
      }
    }

    // Release the camera once the tracked crate lands or disappears
    // (the authority does this via PhysicsStep's onCratesSettled).
    if (this.cameraMode === 'crate') {
      const tracked = this.trackedCrate;
      if (!tracked || !stateIds.has(tracked.id) || tracked.isGrounded) {
        this.trackedCrate = null;
        this.cameraMode = 'worm';
      }
    }
    // Online, derive the crate-wait state from the authoritative snapshot:
    // while any crate is still airborne the server pauses the turn, so local
    // prediction must not move the worm either (it would just rubber-band).
    // Snapshot-driven, so it can never wedge: it clears once crates land.
    this.waitingForCrates = state.crates.some((c) => !c.isGrounded);
  }

  private syncNapalmDropletsFromState(state: GameStateSnapshot): void {
    if (!state.napalmDroplets) return;
    const stateIds = new Set(state.napalmDroplets.map((n) => n.id));

    for (const dropletSync of state.napalmDroplets) {
      let droplet = this.entityRegistry.get<NapalmDroplet>('napalmDroplet', dropletSync.id);
      if (!droplet) {
        droplet = new NapalmDroplet(dropletSync.position.x, dropletSync.position.y, 0);
        (droplet as { id: string }).id = dropletSync.id;
        this.addNapalmDroplet(droplet);
      } else {
        droplet.position.x = dropletSync.position.x;
        droplet.position.y = dropletSync.position.y;
        droplet.velocity.x = dropletSync.velocity.x;
        droplet.velocity.y = dropletSync.velocity.y;
        droplet.active = dropletSync.active;
      }
    }

    for (const droplet of this.entityRegistry.getAll<NapalmDroplet>('napalmDroplet')) {
      if (!stateIds.has(droplet.id)) {
        droplet.destroy();
      }
    }
    this.entityRegistry.removeInactive('napalmDroplet');
  }

  private syncHomingMissilesFromState(state: GameStateSnapshot): void {
    if (!state.homingMissiles) return;
    const stateIds = new Set(state.homingMissiles.map((h) => h.id));

    for (const homingSync of state.homingMissiles) {
      let homing = this.entityRegistry.get<HomingMissile>('homingMissile', homingSync.id);
      if (!homing) {
        homing = new HomingMissile({
          x: homingSync.position.x,
          y: homingSync.position.y,
          vx: homingSync.velocity.x,
          vy: homingSync.velocity.y,
          targetX: homingSync.targetX,
          targetY: homingSync.targetY,
          ownerId: homingSync.ownerTeamId,
        });
        (homing as { id: string }).id = homingSync.id;
        this.addHomingMissile(homing);
        gameLogger.networkEntitySynced('homingMissile', homingSync.id, 'created');
      } else {
        homing.position.x = homingSync.position.x;
        homing.position.y = homingSync.position.y;
        homing.velocity.x = homingSync.velocity.x;
        homing.velocity.y = homingSync.velocity.y;
        homing.target.x = homingSync.targetX;
        homing.target.y = homingSync.targetY;
        homing.homingTimer = homingSync.homingTimer;
        homing.active = homingSync.active;
        gameLogger.networkEntitySynced('homingMissile', homingSync.id, 'updated');
      }
    }

    for (const homing of this.entityRegistry.getAll<HomingMissile>('homingMissile')) {
      if (!stateIds.has(homing.id)) {
        gameLogger.networkEntitySynced('homingMissile', homing.id, 'destroyed');
        homing.destroy();
      }
    }
    this.entityRegistry.removeInactive('homingMissile');
  }

  private syncGravesFromState(state: GameStateSnapshot): void {
    if (!state.graves) return;
    const stateIds = new Set(state.graves.map((g) => g.id));

    for (const graveSync of state.graves) {
      let grave = this.entityRegistry.get<Grave>('grave', graveSync.id);
      if (!grave) {
        grave = new Grave(graveSync.position.x, graveSync.position.y);
        (grave as { id: string }).id = graveSync.id;
        grave.health = graveSync.health;
        grave.isGrounded = graveSync.isGrounded;
        this.addGrave(grave);
        gameLogger.networkEntitySynced('grave', graveSync.id, 'created');
      } else {
        grave.position.x = graveSync.position.x;
        grave.position.y = graveSync.position.y;
        grave.velocity.x = graveSync.velocity.x;
        grave.velocity.y = graveSync.velocity.y;
        grave.active = graveSync.active;
        grave.health = graveSync.health;
        grave.isGrounded = graveSync.isGrounded;
        gameLogger.networkEntitySynced('grave', graveSync.id, 'updated');
      }
    }

    for (const grave of this.entityRegistry.getAll<Grave>('grave')) {
      if (!stateIds.has(grave.id)) {
        gameLogger.networkEntitySynced('grave', grave.id, 'destroyed');
        grave.destroy();
      }
    }
    this.entityRegistry.removeInactive('grave');
  }

  private syncFiresFromState(state: GameStateSnapshot): void {
    if (!state.fires) return;
    const stateIds = new Set(state.fires.map((f) => f.id));

    for (const fireSync of state.fires) {
      let fire = this.entityRegistry.get<Fire>('fire', fireSync.id);
      if (!fire) {
        const CLIENT_NO_SPREAD = 0;
        fire = new Fire({
          x: fireSync.position.x,
          y: fireSync.position.y,
          duration: fireSync.duration,
          damagePerSecond: 5,
          spreadChance: CLIENT_NO_SPREAD,
          burnRadius: 3,
          burnInterval: 0.5,
        });
        (fire as { id: string }).id = fireSync.id;
        (fire as any).hasSpread = fireSync.hasSpread;

        fire.onBurnTerrain = (bx, by, radius) => {
          const offsetX = getGameRNG().nextFloat(-radius * 1.5, radius * 1.5);
          const offsetY = getGameRNG().nextFloat(-radius * 0.5, radius * 1.5);
          this.destroyTerrainCircle(bx + offsetX, by + offsetY, radius);
          this.terrainDirty = true;
        };

        fire.onFall = () => {
          if (this.terrain.isSolid(fire!.position.x, fire!.position.y + 1)) {
            fire!.velocity.y = 0;
            while (this.terrain.isSolid(fire!.position.x, fire!.position.y)) {
              fire!.position.y -= 1;
            }
          }

          if (fire!.position.y > WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel) {
            fire!.extinguish();
          }

          const sprite = this.fireSprites.get(fire!.id);
          if (sprite) {
            sprite.position.y = fire!.position.y;
          }
        };

        fire.onExtinguish = () => {
          const sprite = this.fireSprites.get(fire!.id);
          if (sprite) {
            this.layers.effects.removeChild(sprite);
            this.fireSprites.delete(fire!.id);
          }
        };

        this.entityRegistry.add('fire', fire);

        const sprite = new Graphics();
        sprite.position.set(fireSync.position.x, fireSync.position.y);
        this.fireSprites.set(fire.id, sprite);
        this.layers.effects.addChild(sprite);
        gameLogger.networkEntitySynced('fire', fireSync.id, 'created');
      } else {
        fire.position.x = fireSync.position.x;
        fire.position.y = fireSync.position.y;
        fire.velocity.x = fireSync.velocity.x;
        fire.velocity.y = fireSync.velocity.y;
        fire.active = fireSync.active;
        (fire as any).hasSpread = fireSync.hasSpread;
        gameLogger.networkEntitySynced('fire', fireSync.id, 'updated');
      }
    }

    for (const fire of this.entityRegistry.getAll<Fire>('fire')) {
      if (!stateIds.has(fire.id)) {
        gameLogger.networkEntitySynced('fire', fire.id, 'destroyed');
        fire.extinguish();
      }
    }
    this.entityRegistry.removeInactive('fire');
  }

  private render(_interpolation: number): void {
    for (const team of this.state.teams) {
      for (const worm of team.worms) {
        const animSprite = this.wormSprites.get(worm.id);
        if (!animSprite) {
          console.warn(
            `[RENDER] No sprite for worm: ${worm.id}, sprite map keys: ${[...this.wormSprites.keys()].join(', ')}`,
          );
          continue;
        }

        animSprite.setPosition(
          worm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
          worm.position.y + WA_PHYSICS.WORM.HEIGHT + animSprite.footOffsetPx,
        );
        animSprite.setFacing(worm.facing === 'right' ? -1 : 1);
        // Slope-relative aim (GunBound): sit the worm on the slope. The raw
        // ground slope is positive when the ground rises to the right; a
        // counter-clockwise (negative) sprite rotation leans the worm back into
        // that hill. Facing-independent (the x-mirror leaves the axis intact),
        // 0 in classic mode / mid-air (no ground under the feet).
        animSprite.setRotation((-worm.computeGroundSlopeDeg(this.terrain, false) * Math.PI) / 180);
        animSprite.setVisible(worm.state !== 'dead');
        animSprite.setAlpha(worm.health > 0 ? 1 : 0.3);

        // Animation FSM (resolver): one pure function picks the anim from the
        // worm's gameplay state + overlays. "charging" = the active worm is
        // charging a shot by ANY method - spacebar hold (input.charging), a
        // free/power-bar/mobile drag (dragShot, which only feeds chargeState at
        // release), or the synced charge power (online spectators).
        const isActiveWorm = worm.id === this.state.getCurrentWorm()?.id;
        const targetAnim = resolveTankAnimState({
          wormState: worm.state,
          kamikazing: worm.kamikazing,
          parachuteOpen: worm.parachuteOpen,
          charging:
            isActiveWorm &&
            (this.input.charging ||
              this.dragShot !== null ||
              this.chargeState.getChargePower() > 0),
          hasChargeAnim: animSprite.hasAnimation('charge'),
          // Player's walk INTENT (active worm) or actual horizontal motion -
          // immune to the single-frame state blips physics emits on terrain.
          movingHorizontally:
            (isActiveWorm && (this.input.left || this.input.right)) ||
            Math.abs(worm.velocity.x) > 5,
        });
        if (animSprite.getCurrentAnimation() !== targetAnim) {
          animSprite.setAnimation(targetAnim);
        }

        if (worm.kamikazing) {
          animSprite.setFacing(worm.kamikazeDirection.x >= 0 ? -1 : 1);
        }

        const healthBar = this.wormHealthBars.get(worm.id);
        if (healthBar) {
          healthBar.position.set(worm.position.x + WA_PHYSICS.WORM.WIDTH / 2, worm.position.y - 25);
          healthBar.visible = worm.state !== 'dead';

          const healthFill = healthBar.getChildByLabel('healthFill') as Graphics;
          if (healthFill) {
            const healthPercent = worm.health / worm.maxHealth;
            healthFill.clear();
            healthFill.rect(-19, 3, 38 * healthPercent, 4);
            const healthColor =
              healthPercent > 0.5 ? 0x00ff00 : healthPercent > 0.25 ? 0xffff00 : 0xff0000;
            healthFill.fill({ color: healthColor });
          }
        }
      }
    }

    // Smoke trail behind flying projectiles (every other frame, while moving;
    // not the fast Aduka tracers, which are their own visual). New projectiles
    // that spawn next to the active worm get a muzzle flash (a fired shot, as
    // opposed to a mid-air sub-munition like a cluster fragment).
    this.projectileTrailFrame ^= 1;
    const prevSeen = this.projectileSeen;
    const seen = new Set<string>();
    const curWorm = this.state.getCurrentWorm();
    for (const projectile of this.entityRegistry.getAll<Projectile>('projectile')) {
      seen.add(projectile.id);
      const sprite = this.projectileSprites.get(projectile.id);
      if (sprite) {
        this.spriteManager.updateProjectileSprite(sprite, projectile);
        // Hide the Nak shell while it is tunnelling underground (its center is
        // inside solid terrain) so it looks subterranean. Works for both the
        // local sim and synced online clients (both have the collision mask).
        if (projectile.type === 'nak') {
          sprite.visible = !this.terrain.isSolid(projectile.position.x, projectile.position.y);
        }
      }
      if (projectile.type !== 'aduka') {
        if (!prevSeen.has(projectile.id) && curWorm) {
          const dx = projectile.position.x - curWorm.position.x;
          const dy = projectile.position.y - curWorm.position.y;
          if (dx * dx + dy * dy < 52 * 52) {
            this.particles.emitMuzzleFlash(
              projectile.position.x,
              projectile.position.y,
              Math.atan2(projectile.velocity.y, projectile.velocity.x),
            );
          }
        }
        if (this.projectileTrailFrame === 0) {
          const speed = Math.hypot(projectile.velocity.x, projectile.velocity.y);
          if (speed > 45) {
            this.particles.emitTrail(projectile.position.x, projectile.position.y);
          }
        }
      }
    }
    this.projectileSeen = seen;

    this.renderRope();
    this.particles.render();

    const projectileIds = new Set(
      this.entityRegistry.getAll<Projectile>('projectile').map((p) => p.id),
    );
    this.spriteManager.cleanupOrphanedSprites(this.projectileSprites, projectileIds);

    for (const mine of this.entityRegistry.getAll<Mine>('mine')) {
      const sprite = this.mineSprites.get(mine.id);
      if (sprite) {
        this.spriteManager.updateMineSprite(sprite, mine);
      }
    }

    const mineIds = new Set(this.entityRegistry.getAll<Mine>('mine').map((m) => m.id));
    this.spriteManager.cleanupOrphanedSprites(this.mineSprites, mineIds);

    for (const homing of this.entityRegistry.getAll<HomingMissile>('homingMissile')) {
      const container = this.homingSprites.get(homing.id);
      if (container) {
        this.spriteManager.updateHomingMissileSprite(container, homing);
      }
    }

    const homingIds = new Set(
      this.entityRegistry.getAll<HomingMissile>('homingMissile').map((h) => h.id),
    );
    this.spriteManager.cleanupOrphanedSprites(this.homingSprites, homingIds, true);

    for (const grave of this.entityRegistry.getAll<Grave>('grave')) {
      const sprite = this.graveSprites.get(grave.id);
      if (sprite) {
        this.spriteManager.updateGraveSprite(sprite, grave);
      }
    }

    const graveIds = new Set(this.entityRegistry.getAll<Grave>('grave').map((g) => g.id));
    this.spriteManager.cleanupOrphanedSprites(this.graveSprites, graveIds);

    for (const crate of this.entityRegistry.getAll<Crate>('crate')) {
      const container = this.crateSprites.get(crate.id);
      if (container) {
        this.spriteManager.updateCrateSprite(container, crate);
      }
    }

    const crateIds = new Set(this.entityRegistry.getAll<Crate>('crate').map((c) => c.id));
    this.spriteManager.cleanupOrphanedSprites(this.crateSprites, crateIds);

    for (const barrel of this.entityRegistry.getAll<OilBarrel>('barrel')) {
      const sprite = this.barrelSprites.get(barrel.id);
      if (sprite) {
        this.spriteManager.updateBarrelSprite(sprite, barrel);
      }
    }

    const barrelIds = new Set(this.entityRegistry.getAll<OilBarrel>('barrel').map((b) => b.id));
    this.spriteManager.cleanupOrphanedSprites(this.barrelSprites, barrelIds);

    for (const sheep of this.entityRegistry.getAll<Sheep>('sheep')) {
      const sprite = this.sheepSprites.get(sheep.id);
      if (sprite) {
        this.spriteManager.updateSheepSprite(sprite, sheep);
      }
    }

    const sheepIds = new Set(this.entityRegistry.getAll<Sheep>('sheep').map((s) => s.id));
    this.spriteManager.cleanupOrphanedSprites(this.sheepSprites, sheepIds);

    for (const raon of this.entityRegistry.getAll<MiniRaon>('raon')) {
      const sprite = this.raonSprites.get(raon.id);
      if (sprite) {
        this.spriteManager.updateRaonSprite(sprite, raon);
      }
    }
    const raonIds = new Set(this.entityRegistry.getAll<MiniRaon>('raon').map((r) => r.id));
    this.spriteManager.cleanupOrphanedSprites(this.raonSprites, raonIds);

    for (const ss of this.entityRegistry.getAll<SuperSheep>('superSheep')) {
      const container = this.superSheepSprites.get(ss.id);
      if (container) {
        this.spriteManager.updateSuperSheepSprite(container, ss);
      }
    }

    const superSheepIds = new Set(
      this.entityRegistry.getAll<SuperSheep>('superSheep').map((ss) => ss.id),
    );
    this.spriteManager.cleanupOrphanedSprites(this.superSheepSprites, superSheepIds);

    for (const kamikaze of this.entityRegistry.getAll<Kamikaze>('kamikaze')) {
      const container = this.kamikazeSprites.get(kamikaze.id);
      if (container) {
        this.spriteManager.updateKamikazeSprite(container, kamikaze, 0.016);
      }
    }

    const kamikazeIds = new Set(this.entityRegistry.getAll<Kamikaze>('kamikaze').map((k) => k.id));
    this.spriteManager.cleanupOrphanedSprites(this.kamikazeSprites, kamikazeIds);

    for (const droplet of this.entityRegistry.getAll<NapalmDroplet>('napalmDroplet')) {
      const sprite = this.napalmDropletSprites.get(droplet.id);
      if (sprite) {
        this.spriteManager.updateNapalmDropletSprite(sprite, droplet);
      }
    }

    const dropletIds = new Set(
      this.entityRegistry.getAll<NapalmDroplet>('napalmDroplet').map((d) => d.id),
    );
    this.spriteManager.cleanupOrphanedSprites(this.napalmDropletSprites, dropletIds);

    for (const fire of this.entityRegistry.getAll<Fire>('fire')) {
      const sprite = this.fireSprites.get(fire.id);
      if (sprite) {
        this.spriteManager.updateFireSprite(sprite, fire);
      }
    }

    const fireIds = new Set(this.entityRegistry.getAll<Fire>('fire').map((f) => f.id));
    this.spriteManager.cleanupOrphanedSprites(this.fireSprites, fireIds);

    const currentWorm = this.state.getCurrentWorm();
    if (currentWorm && this.state.phase === 'playing') {
      // Keep the displayed aim (crosshair, arc, angle readout) in sync with the
      // slope for whoever is aiming - including an enemy we're spectating
      // online, whose groundTiltDeg the authority never pushes to us.
      currentWorm.groundTiltDeg =
        currentWorm.computeGroundSlopeDeg(this.terrain, false) *
        (currentWorm.facing === 'right' ? 1 : -1);
      const aimDir = currentWorm.getAimDirection();
      const chargePower = this.chargeState.getChargePower();
      const distance = 50 + chargePower * 0.5;
      const cx = currentWorm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
      const cy = currentWorm.position.y + WA_PHYSICS.WORM.HEIGHT / 2;
      this.crosshairSprite.position.set(cx + aimDir.x * distance, cy + aimDir.y * distance);
      this.crosshairSprite.visible = true;

      // Aim angle readout (last shot memory lives beside the power bar,
      // private to the owning player).
      this.aimAngleText.text = `${Math.round(currentWorm.getEffectiveAimAngle())}\u00b0`;
      this.aimAngleText.position.set(cx, currentWorm.position.y - 34);
      this.aimAngleText.visible = true;
      this.renderAimArc(cx, cy, currentWorm);

      this.chargeBarGraphics.clear();
      if (chargePower > 0) {
        const barStart = 20;
        const barEnd = distance - 5;
        const barLength = (barEnd - barStart) * (chargePower / 100);
        const startX = cx + aimDir.x * barStart;
        const startY = cy + aimDir.y * barStart;
        const endX = cx + aimDir.x * (barStart + barLength);
        const endY = cy + aimDir.y * (barStart + barLength);

        const r = Math.round(255 * Math.min(1, chargePower / 50));
        const g = Math.round(255 * Math.max(0, 1 - chargePower / 100));
        const color = (r << 16) | (g << 8) | 0;

        this.chargeBarGraphics.moveTo(startX, startY);
        this.chargeBarGraphics.lineTo(endX, endY);
        this.chargeBarGraphics.stroke({ width: 4, color, alpha: 0.8 });
      }
    } else {
      this.crosshairSprite.visible = false;
      this.chargeBarGraphics.clear();
      this.aimAngleText.visible = false;
      this.aimArcGraphics.clear();
    }

    // Pre-aim readout above the own worm while an opponent acts.
    const offTurnWorm = this.myOffTurnWorms()[0] ?? null;
    if (offTurnWorm && this.state.phase === 'playing') {
      this.offTurnAimText.text = `${Math.round(offTurnWorm.aimAngle)}°`;
      this.offTurnAimText.position.set(
        offTurnWorm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
        offTurnWorm.position.y - 34,
      );
      this.offTurnAimText.visible = true;
    } else {
      this.offTurnAimText.visible = false;
    }

    // The touch controls have no business covering the game-over screen or
    // the pause/settings menus.
    if (this.virtualGamepad) {
      const wantGamepad = this.state.phase !== 'finished' && !this.pauseMenu.isVisible();
      if (wantGamepad !== this.virtualGamepad.isVisible()) {
        if (wantGamepad) {
          this.virtualGamepad.show();
        } else {
          this.virtualGamepad.hide();
        }
      }
    }

    this.syncWeaponPanelTeam();
    this.renderDragShot();
    this.updateItemBar();
    this.renderWeather();

    if (this.networkOSD) {
      this.networkOSD.update();
    }

    this.renderHUD();
  }

  private renderHUD(): void {
    // Players (on OR off turn) see their own selection - off-turn that is the
    // weapon they're pre-selecting for next turn. Only a pure spectator, who
    // can't pre-select, sees the active player's synced weapon instead.
    const weaponName = this.isSpectator()
      ? this.syncedSelectedWeaponId
        ? (weaponRegistry.get(this.syncedSelectedWeaponId)?.name ?? '...')
        : '...'
      : this.weaponSelection.getName();
    const timerText = this.timerText as unknown as Text;
    const weaponText = this.weaponText as unknown as Text;

    let weaponLine = `${weaponName}  ·  Fuse ${this.chargeState.getFuseTimer()}s`;
    if (Number.isFinite(this.state.movementRemaining)) {
      weaponLine += `  ·  Move ${Math.max(0, Math.ceil(this.state.movementRemaining))}`;
    }
    const selectedWeapon = this.weaponSelection.getSelectedWeapon();
    if (this.state.getTeamDelays() && selectedWeapon) {
      weaponLine += `  ·  Delay ${getWeaponDelay(selectedWeapon)}`;
    }
    weaponText.text = weaponLine;

    this.windIndicator.update(
      this.state.wind,
      this.state.windY,
      this.settings.mode.wind.maxStrength,
      this.settings.mode.wind.model === 'vector360',
    );

    // Wind gust sfx whenever the wind actually changes (re-roll, item, weather).
    if (
      this.lastWindSfx &&
      (this.lastWindSfx.x !== this.state.wind || this.lastWindSfx.y !== this.state.windY)
    ) {
      soundManager.play('windGust');
    }
    this.lastWindSfx = { x: this.state.wind, y: this.state.windY };

    // The summary doubles as the turn-order list. With a delay queue, lowest
    // delay acts next. Otherwise (round-robin) it shows the real upcoming
    // order: the current team first, then the rest wrapping around
    // (A,B,C,D -> after A acts -> B,C,D,A). Alliance identity is carried by
    // the shared colour (no letter prefix) so teams read cleanly.
    const teamDelays = this.state.getTeamDelays();
    const delayOrdersTurns = this.settings.mode.turnOrder.kind === 'delay';
    let sortedTeams: Team[];
    if (teamDelays && delayOrdersTurns) {
      sortedTeams = [...this.state.teams].sort(
        (a, b) => (teamDelays[a.id] ?? 0) - (teamDelays[b.id] ?? 0),
      );
    } else {
      const teams = this.state.teams;
      const ci = teams.length > 0 ? this.state.currentTeamIndex % teams.length : 0;
      sortedTeams = [...teams.slice(ci), ...teams.slice(0, ci)];
    }
    const teamHealthData = sortedTeams.map((team) => ({
      id: team.id,
      name: team.name + (teamDelays ? ` \u00b7 ${teamDelays[team.id] ?? 0}` : ''),
      color: team.color,
      totalHealth: team.getTotalHealth(),
      maxHealth: team.worms.reduce((sum, w) => sum + w.maxHealth, 0),
      allianceColor: ALLIANCE_COLORS[team.allianceId] ?? undefined,
    }));
    this.teamHealthSummary.update(teamHealthData);

    const secs = Math.max(0, Math.ceil(this.state.turnTimeRemaining));
    const currentWorm = this.state.getCurrentWorm();
    const team = this.state.getCurrentTeam();
    let turnLabel = '';
    if (currentWorm && team && this.session) {
      const owner = this.session.getTeamOwner(team.id);
      turnLabel = this.isMyTurn() ? 'YOUR TURN' : `${owner?.name || 'Unknown'}'s turn`;
    }
    timerText.text = turnLabel ? `${turnLabel}  —  ${secs}s` : `${secs}s`;
    // Urgency cue that never relies on colour alone (the count is always shown).
    timerText.style.fill = secs <= 5 ? COLORS.lcdRed : COLORS.gold;

    this.layoutStatusPanel();
  }

  /** Size/redraw the backing panel behind the top-left status text (on change). */
  /** Brief warm screen flash on an explosion. Gentle, and only for sizable
   * blasts, so ordinary grenades don't strobe the screen (gated by the
   * camera-shake setting at the call site for accessibility). */
  private flashScreen(radius: number): void {
    const intensity = Math.min(0.18, Math.max(0, (radius - 45) / 350));
    if (intensity <= 0) return;
    if (intensity > this.flashAlpha) {
      this.flashAlpha = intensity;
      this.flashOverlay.tint = 0xffe6b0;
    }
  }

  private updateScreenFlash(deltaTime: number): void {
    if (this.flashAlpha <= 0) {
      if (this.flashOverlay.alpha !== 0) this.flashOverlay.alpha = 0;
      return;
    }
    this.flashAlpha = Math.max(0, this.flashAlpha - deltaTime * 1.8);
    this.flashOverlay.alpha = this.flashAlpha;
  }

  private layoutStatusPanel(): void {
    const w = this.weaponText as unknown as Text;
    const t = this.timerText as unknown as Text;
    const padX = 12;
    const padY = 8;
    const left = Math.min(w.x, t.x) - padX;
    const top = Math.min(w.y, t.y) - padY;
    const width = Math.max(w.x + w.width, t.x + t.width) + padX - left;
    const height = Math.max(w.y + w.height, t.y + t.height) + padY - top;
    if (Math.abs(width - this.hudStatusSize.w) < 1 && Math.abs(height - this.hudStatusSize.h) < 1) {
      return;
    }
    this.hudStatusSize = { w: width, h: height };
    const g = this.hudStatusBg;
    g.clear();
    g.roundRect(left, top, width, height, 12).fill({
      color: COLORS.bg,
      alpha: 0.72,
    });
    g.roundRect(left + 2, top + 2, width - 4, Math.min(12, height * 0.4), 10).fill({
      color: COLORS.white,
      alpha: 0.06,
    });
    g.roundRect(left, top, width, height, 12).stroke({
      width: 2,
      color: COLORS.bevelLight,
      alpha: 0.6,
    });
  }

  private focusCurrentWorm(): void {
    const worm = this.state.getCurrentWorm();
    if (worm) {
      this.cameraController.focusWorm(worm);
    }
  }

  private forceUpdateAllSprites(): void {
    for (const team of this.state.teams) {
      for (const worm of team.worms) {
        const animSprite = this.wormSprites.get(worm.id);
        if (animSprite) {
          animSprite.setPosition(
            worm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
            worm.position.y + WA_PHYSICS.WORM.HEIGHT + animSprite.footOffsetPx,
          );
          animSprite.setVisible(worm.state !== 'dead');
        } else {
          console.warn(`[FORCE] No sprite for worm: ${worm.id}`);
        }

        const healthBar = this.wormHealthBars.get(worm.id);
        if (healthBar) {
          healthBar.position.set(worm.position.x + WA_PHYSICS.WORM.WIDTH / 2, worm.position.y - 25);
          healthBar.visible = worm.state !== 'dead';
        }
      }
    }
  }

  private resetInput(): void {
    this.input.left = false;
    this.input.right = false;
    this.input.up = false;
    this.input.down = false;
    this.input.jump = false;
    this.input.fire = false;
    this.input.charging = false;
    this.chargeState.reset();
    this.pendingJump = false;
    this.lastJumpTime = 0;
    this.toolSystem.setFireHeld(false);
  }

  private setupGameLogger(): void {
    gameLogger.setContextProvider(() => ({
      tick: this.gameTick,
      phase: this.state.phase,
      currentTeamId: this.state.getCurrentTeam()?.id || null,
      currentWormId: this.state.getCurrentWorm()?.id || null,
      isHost: this.session ? this.session.getRoom().isHost(this.session.getLocalPlayer().id) : true,
      isMyTurn: this.isMyTurn(),
    }));

    gameLogger.subscribeToEventBus(this.eventBus);
    gameLogger.gameStarted(
      this.state.teams.length,
      !this.networkManager ||
        this.session?.getRoom().isHost(this.session.getLocalPlayer().id) ||
        false,
    );
  }

  start(): void {
    this.gameLoop.start();
    this.gameStartTime = Date.now();

    const mode = this.networkManager ? 'multiplayer' : 'local';
    const playerCount = this.networkManager ? this.networkManager.getPeerCount() + 1 : 1;
    analytics.gameStarted(mode, playerCount, this.state.teams.length);
  }

  stop(): void {
    this.gameLoop.stop();
  }

  addEntity(entity: Entity): void {
    if (entity instanceof Projectile) {
      this.addProjectile(entity);
    } else if (entity instanceof Sheep) {
      this.addSheep(entity);
    } else if (entity instanceof HomingMissile) {
      this.addHomingMissile(entity);
    }
  }

  removeEntity(id: string): void {
    this.entityRegistry.remove('projectile', id);
  }

  getWorms(): Worm[] {
    const worms: Worm[] = [];
    for (const team of this.state.teams) {
      worms.push(...team.worms);
    }
    return worms;
  }

  getIsMyTurn(): boolean {
    return this.isMyTurn();
  }

  get totalTurnCount(): number {
    return this.simulation.getTotalTurnCount();
  }

  getWind(): number {
    return this.state.getWind();
  }

  getTerrain(): { isSolid(x: number, y: number): boolean } {
    return this.terrain;
  }

  destroyTerrainCircle(
    x: number,
    y: number,
    radius: number,
    type: 'drill' | 'blowtorch' = 'drill',
  ): void {
    this.terrain.destroyCircle(x, y, radius, type);
    this.terrainSprite.texture.source.update();
  }

  spawnFireAt(x: number, y: number): void {
    let fireY = y;
    for (let j = 0; j < 50 && !this.terrain.isSolid(x, fireY + 1); j++) {
      fireY++;
    }
    if (fireY < WA_PHYSICS.WORLD.HEIGHT - this.currentWaterLevel) {
      this.addFire(x, fireY);
    }
  }

  getNetworkManager(): any {
    return this.networkManager;
  }

  getState(): GameState {
    return this.state;
  }

  private checkForGameOver(): void {
    if (this.state.phase === 'finished' && !this.gameOverScreen.isVisible()) {
      try {
        if (this.state.winner) {
          const allies = this.state.getWinnerAllies();
          const label =
            allies.length > 1 ? allies.map((t) => t.name).join(' + ') : this.state.winner.name;
          this.commentary.showTeamWins(label);
        }
      } catch {}

      const duration = (Date.now() - this.gameStartTime) / 1000;
      const stats = this.statsPanel?.getStats();
      this.gameOverScreen.show(
        this.state.winner,
        this.state.teams,
        stats,
        duration,
        this.simulation.getTotalTurnCount(),
      );

      const winnerName = this.state.winner?.name || 'Draw';
      analytics.gameEnded(winnerName, this.state.roundNumber, duration);
    }
  }

  private restartGame(): void {
    window.location.reload();
  }

  private setupNetworkHandlers(): void {
    if (!this.networkManager) return;

    this.networkManager.addEventHandlers({
      onGameStateReceived: (message) => this.applyNetworkState(message),
      onGameInputReceived: () => {},
      onGameEventReceived: (event) => this.handleNetworkEvent(event),
      onReconnected: () => {
        if (this.resyncManager) {
          console.log('[Game] Reconnected - requesting game state resync');
          this.resyncManager.startResync();
        }
      },
    });

    // The server broadcasts the initial keyframe right after game-start,
    // while this client is still loading the game scene — so it is missed.
    // Fetch a fresh baseline now that the handlers are wired.
    this.networkManager.requestKeyframe();
  }

  private serializeGameState(): GameStateSnapshot {
    const worms: WormSync[] = [];
    for (const team of this.state.teams) {
      for (const worm of team.worms) {
        const ropeState = (worm as any).ropeState;
        worms.push({
          id: worm.id,
          name: worm.name,
          teamId: worm.teamId,
          position: { ...worm.position },
          velocity: { ...worm.velocity },
          health: worm.health,
          pendingDamage: worm.pendingDamage,
          state: worm.state,
          facing: worm.facing,
          aimAngle: worm.aimAngle,
          isGrounded: worm.isGrounded,
          parachuteOpen: worm.parachuteOpen,
          rope: ropeState
            ? {
                active: ropeState.active,
                anchorX: ropeState.anchorX,
                anchorY: ropeState.anchorY,
                length: ropeState.length,
                angle: ropeState.angle,
                attached: ropeState.attached,
                dirX: ropeState.dirX,
                dirY: ropeState.dirY,
                speed: ropeState.speed,
                maxLength: ropeState.maxLength,
              }
            : undefined,
        });
      }
    }

    const projectiles: ProjectileSync[] = this.entityRegistry
      .getAll<Projectile>('projectile')
      .map((p) => ({
        id: p.id,
        type: p.type,
        position: { ...p.position },
        velocity: { ...p.velocity },
        timer: p.timer,
        rotation: p.rotation,
        isOnGround: p.isOnGround,
        explosionRadius: p.explosionRadius,
        maxDamage: p.maxDamage,
        ownerId: p.ownerId,
      }));

    const mines: MineSync[] = this.entityRegistry.getAll<Mine>('mine').map((m) => ({
      id: m.id,
      position: { ...m.position },
      velocity: { ...m.velocity },
      armed: m.armed,
      timer: m.armTimer,
      explosionRadius: m.explosionRadius,
      maxDamage: m.maxDamage,
    }));

    const barrels: BarrelSync[] = this.entityRegistry.getAll<OilBarrel>('barrel').map((b) => ({
      id: b.id,
      position: { ...b.position },
      velocity: { ...b.velocity },
      isGrounded: b.isGrounded,
    }));

    const crates: CrateSync[] = this.entityRegistry.getAll<Crate>('crate').map((c) => ({
      id: c.id,
      position: { ...c.position },
      velocity: { ...c.velocity },
      type: c.type,
      contents: c.contents,
      parachuteOpen: c.parachuteOpen,
      isGrounded: c.isGrounded,
    }));

    const napalmDroplets: NapalmDropletSync[] = this.entityRegistry
      .getAll<NapalmDroplet>('napalmDroplet')
      .map((n) => ({
        id: n.id,
        position: { ...n.position },
        velocity: { ...n.velocity },
        active: n.active,
      }));

    const homingMissiles: HomingMissileSync[] = this.entityRegistry
      .getAll<HomingMissile>('homingMissile')
      .map((h) => ({
        id: h.id,
        position: { ...h.position },
        velocity: { ...h.velocity },
        active: h.active,
        targetX: h.target.x,
        targetY: h.target.y,
        homingTimer: h.homingTimer,
        ownerTeamId: h.ownerId,
        explosionRadius: h.explosionRadius,
        maxDamage: h.maxDamage,
      }));

    const graves: NetGraveSync[] = this.entityRegistry.getAll<Grave>('grave').map((g) => ({
      id: g.id,
      position: { ...g.position },
      velocity: { ...g.velocity },
      active: g.active,
      health: g.health,
      isGrounded: g.isGrounded,
    }));

    const fires: NetFireSync[] = this.entityRegistry.getAll<Fire>('fire').map((f) => ({
      id: f.id,
      position: { ...f.position },
      velocity: { ...f.velocity },
      active: f.active,
      duration: f.duration,
      remainingLife: f.getRemainingLife(),
      hasSpread: (f as any).hasSpread ?? false,
    }));

    const teamWormIndices: Record<string, number> = {};
    for (const team of this.state.teams) {
      teamWormIndices[team.id] = team.currentWormIndex;
    }

    const teamInventories = this.state.teams.map((team) => ({
      teamId: team.id,
      inventory: team.getInventorySnapshot(),
    }));

    this.stateSeqCounter++;
    return {
      tick: this.gameTick,
      stateSeq: this.stateSeqCounter,
      turn: {
        currentTeamIndex: this.state.currentTeamIndex,
        teamWormIndices,
        turnTimeRemaining: this.state.turnTimeRemaining,
        phase: this.state.phase,
        wind: this.state.wind,
        chargePower: this.chargeState.getChargePower(),
        turnSequence: this.state.turnSequence,
        selectedWeaponId: this.weaponSelection.getSelectedWeaponId() ?? undefined,
      },
      worms,
      projectiles,
      mines,
      barrels,
      crates,
      napalmDroplets,
      homingMissiles,
      graves,
      fires,
      terrainChanges: [],
      terrainVersion: this.terrain.getTerrainVersion(),
      terrainModifications: this.terrain.getModifications(),
      teamInventories,
      teamStats: this.statsPanel?.getStats().map((s) => ({
        teamId: s.playerId,
        teamName: s.playerName,
        damageDealt: s.damageDealt,
        kills: s.kills,
        deaths: s.deaths,
        shotsFired: s.shotsFired,
        shotsHit: s.shotsHit,
      })),
    };
  }

  private applyNetworkState(message: GameStateMessage): void {
    let state: GameStateSnapshot;

    if (message.kind === 'keyframe') {
      state = message.state;
      this.baselineSnapshot = state;
      if (this.resyncManager?.isActive()) {
        this.resyncManager.receiveState(state);
      }
    } else {
      if (!this.baselineSnapshot) {
        console.warn('[Game] Received delta without baseline, requesting keyframe');
        if (this.resyncManager && !this.resyncManager.isActive()) {
          this.resyncManager.startResync();
        }
        return;
      }
      const reconstructed = applyDelta(this.baselineSnapshot, message.delta);
      if (reconstructed === null) {
        console.warn('[Game] Delta seq gap detected, requesting keyframe');
        if (this.resyncManager && !this.resyncManager.isActive()) {
          this.resyncManager.startResync();
        }
        return;
      }
      state = reconstructed;
      this.baselineSnapshot = state;
    }

    if (state.stateSeq <= this.highestAppliedStateSeq) {
      return;
    }
    gameLogger.networkStateReceived(state.tick, state.turn.phase, state.turn.currentTeamIndex);

    // De-jitter arrivals: pace buffered snapshots by server tick spacing
    // (60Hz sim) so bursty network delivery doesn't judder the interpolation
    // timeline, while staying within 50ms of real arrival to track drift.
    const now = performance.now();
    let recvTime = now;
    if (this.lastNetArrival) {
      const expected =
        this.lastNetArrival.recvTime +
        Math.max(0, state.tick - this.lastNetArrival.tick) * (1000 / 60);
      recvTime = Math.min(Math.max(expected, now - 50), now + 50);
    }
    this.lastNetArrival = { tick: state.tick, recvTime };
    this.networkStateBuffer.push({ state, recvTime });

    // ~1.5s of 40Hz snapshots: enough headroom for background-tab throttling
    // (the hidden-tab interval ticks about once per second).
    while (this.networkStateBuffer.length > 60) {
      this.networkStateBuffer.shift();
    }
  }

  private applyTurnState(state: GameStateSnapshot): void {
    this.state.currentTeamIndex = state.turn.currentTeamIndex;
    this.state.turnTimeRemaining = state.turn.turnTimeRemaining;
    this.state.wind = state.turn.wind;

    for (const team of this.state.teams) {
      if (state.turn.teamWormIndices[team.id] !== undefined) {
        team.currentWormIndex = state.turn.teamWormIndices[team.id];
      }
    }

    this.state.phase = state.turn.phase;
    // Mirror the authority's movement lock so input is gated locally (no
    // walk-then-snap-back after firing online).
    this.state.syncedMovementLock = state.turn.movementLocked ?? false;
    this.gameTick = state.tick;

    if (state.turn.turnSequence !== undefined) {
      this.state.turnSequence = state.turn.turnSequence;
    }

    this.state.windY = state.turn.windY ?? 0;
    if (state.turn.movementRemaining !== undefined) {
      this.state.movementRemaining = state.turn.movementRemaining;
    }
    if (state.turn.teamDelays) {
      this.state.applyTeamDelays(state.turn.teamDelays);
    }
    if (state.turn.teamItems) {
      for (const team of this.state.teams) {
        const itemsSnapshot = state.turn.teamItems[team.id];
        if (itemsSnapshot) {
          team.applyItemsSnapshot(itemsSnapshot);
        }
      }
    }
    this.state.itemUsedThisTurn = state.turn.itemUsed ?? false;
    if (this.networkManager) {
      this.weatherSystem.applySync(state.turn.weather);
    }

    if (state.turn.chargePower !== undefined) {
      const prevCharge = this.chargeState.getChargePower();
      this.chargeState.setInstantCharge(state.turn.chargePower);
      // Online, charge accumulation happens on the server, so the charge
      // sound must be driven by snapshot transitions.
      if (this.networkManager) {
        if (prevCharge === 0 && state.turn.chargePower > 0) {
          const category = this.weaponSelection.getCategory();
          soundManager.play(category === 'thrown' ? 'throwPowerUp' : 'rocketPowerUp');
        } else if (prevCharge > 0 && state.turn.chargePower === 0) {
          soundManager.stop('throwPowerUp');
          soundManager.stop('rocketPowerUp');
        }
      }
    }

    this.syncedSelectedWeaponId = state.turn.selectedWeaponId ?? null;

    // The server restores each team's remembered weapon at turn start.
    // Mirror it exactly once per turn; afterwards the local selection is the
    // source of truth (otherwise snapshots still echoing the previous weapon
    // revert a selection the player just made).
    const turnSeq = state.turn.turnSequence;
    if (turnSeq !== undefined && turnSeq !== this.weaponSyncedTurnSeq) {
      this.weaponSyncedTurnSeq = turnSeq;
      // Re-select even when the id already matches (off-turn pre-selection):
      // selecting a targeting weapon must (re-)open the targeting cursor now
      // that it is our turn.
      if (state.turn.selectedWeaponId && this.isMyTurn()) {
        this.selectWeapon(state.turn.selectedWeaponId);
      }
    }
  }

  /** The local player's own last shot, shown on the power bar at all times
   *  (including other players' turns) and replaced only by their next shot. */
  private getMyLastShot(): LastShotInfo | null {
    return this.myLastShot;
  }

  /** Presentation/terrain effects driven by authoritative server events. */
  private handleNetworkEvent(event: GameEvent): void {
    switch (event.type) {
      case 'explosion':
        this.createExplosion(event.x, event.y, event.radius, event.maxDamage, true);
        break;
      case 'wormDied': {
        const worm = this.findWormById(event.wormId);
        if (worm) worm.die();
        break;
      }
      case 'weaponFired':
        this.playWeaponSound(event.weaponId);
        break;
      case 'girderPlaced':
        this.applyGirderToTerrain(event.x, event.y, event.angleIndex, event.sizeIndex);
        break;
      case 'turnEnd':
        break;
      case 'resyncResponse':
        // Client received resync response from host
        if (this.resyncManager) {
          this.resyncManager.receiveState(event.state);
        }
        break;
      case 'gameOver':
        this.state.phase = 'finished';
        this.state.winner = event.winnerId
          ? (this.state.teams.find((t) => t.id === event.winnerId) ?? null)
          : null;
        break;
      case 'forceStable':
        this.worldStabilityChecker.forceWorldStable(true);
        break;
      case 'terrainDestroy':
        this.terrain.destroyCircle(event.x, event.y, event.radius);
        this.terrainSprite.texture.source.update();
        break;
      case 'sound':
        soundManager.play(event.soundId as Parameters<typeof soundManager.play>[0]);
        break;
      case 'itemUsed':
        this.onItemUsed(event.itemId, event.teamId);
        break;
      case 'thorZap':
        // The event carries the exact beam origin (fromX/fromY) and colour, so
        // Lightning's V, Knight's satellite-over-firer beams and Thor/Aduka
        // zaps all render with the right geometry. fromY 0 = satellite default.
        this.showThorZap(
          event.fromX,
          event.fromY > 0 ? event.fromY : THOR_ALTITUDE + 16,
          event.x,
          event.y,
          event.color ?? 0xff5555,
        );
        break;
      case 'clusterSpawn': {
        for (const projData of event.projectiles) {
          const projectile = new Projectile({
            x: projData.position.x,
            y: projData.position.y,
            vx: projData.velocity.x,
            vy: projData.velocity.y,
            type: projData.type,
            timer: projData.timer,
            explosionRadius: projData.explosionRadius,
            maxDamage: projData.maxDamage,
            ownerId: projData.ownerId,
            bounceH: projData.type === 'bananalet' ? 0.95 : 0.8,
            bounceV: projData.type === 'bananalet' ? 0.8 : 0.4,
            friction: 0.99,
            windAffected: false,
          });
          projectile.id = projData.id;
          this.addEntity(projectile);
        }
        break;
      }
    }
  }

  private findWormById(wormId: string): Worm | null {
    for (const team of this.state.teams) {
      for (const worm of team.worms) {
        if (worm.id === wormId) return worm;
      }
    }
    return null;
  }

  /** Draw a girder onto both the visual canvas and the collision mask (idempotent). */
  private applyGirderToTerrain(x: number, y: number, angleIndex: number, sizeIndex: number): void {
    const ctx = this.terrainCanvas.getContext('2d');
    if (!ctx) return;
    const angle = GIRDER_ANGLES[angleIndex] ?? GIRDER_ANGLES[0];
    drawGirderOnTerrain(ctx, x, y, angle, sizeIndex);
    const maskCtx = new MaskCanvas(
      this.terrain.getWidth(),
      this.terrain.getHeight(),
      this.terrain.getAlphaData(),
    );
    drawGirderOnTerrain(maskCtx, x, y, angle, sizeIndex);
    this.terrainDirty = true;
  }

  exportGameState(): void {
    const serialized = this.createSerializedState();
    const jsonString = JSON.stringify(serialized, null, 2);
    const filename = generateExportFilename();
    triggerDownload(jsonString, filename);
    console.log(
      `[STATE] Exported game state to ${filename} (${(jsonString.length / 1024 / 1024).toFixed(2)} MB)`,
    );
  }

  private createSerializedState(): SerializedGameState {
    const rng = getGameRNG();
    const ctx: SerializationContext = {
      entityRegistry: this.entityRegistry,
      gameTick: this.gameTick,
      terrainSeed: this._terrainSeed,
      terrainCanvas: this.terrainCanvas,
      settings: this.settings,
      currentWaterLevel: this.currentWaterLevel,
      totalTurnCount: this.simulation.getTotalTurnCount(),
      suddenDeathActive: this.suddenDeathManager.isActive(),
      serializeGameState: () => this.serializeGameState(),
      getRngSeed: () => rng.getSeed(),
      getTerrainModifications: () => this.terrain.getModifications(),
      getLogBuffer: () => gameLogger.getLogBuffer(),
      getLogCategories: () => gameLogger.getSettings().categories,
      gameStartTime: this.gameStartTime,
    };
    return createSerializedStateImpl(ctx);
  }

  loadState(jsonString: string): void {
    const serialized = parseSerializedState(jsonString);
    console.log(
      `[STATE] Loading game state from tick ${serialized.tick}, version ${serialized.version}`,
    );

    this.gameTick = serialized.tick;
    this.currentWaterLevel = serialized.waterLevel;
    this.simulation.setTotalTurnCount(serialized.totalTurnCount);
    this.suddenDeathManager.setActive(serialized.suddenDeathActive);

    setGameSeed(serialized.rngState.seed);

    const imageData = decodeTerrainData(
      serialized.terrainData,
      serialized.terrainDimensions.width,
      serialized.terrainDimensions.height,
    );
    const ctx = this.terrainCanvas.getContext('2d');
    if (ctx) {
      ctx.putImageData(imageData, 0, 0);
      this.terrain.refreshFullImageData();
      this.updateTerrainTexture();
    }

    this.updateWaterVisual();

    this.applyStateDirectly(serialized.gameState);

    this.restoreExtendedEntities(serialized.extendedEntities);

    this.forceUpdateAllSprites();
    this.cameraMode = 'worm';
    this.focusCurrentWorm();

    console.log(`[STATE] Game state restored to tick ${this.gameTick}`);
  }

  private restoreExtendedEntities(extended: SerializedGameState['extendedEntities']): void {
    restoreExtendedEntitiesImpl(extended, {
      entityRegistry: this.entityRegistry,
    });
  }

  updateUISettings(settings: {
    showDamageNumbers?: boolean;
    cameraShake?: boolean;
    showNetworkOSD?: boolean;
  }): void {
    if (settings.showDamageNumbers !== undefined) {
      this.uiSettings.showDamageNumbers = settings.showDamageNumbers;
    }
    if (settings.cameraShake !== undefined) {
      this.uiSettings.cameraShake = settings.cameraShake;
    }
    if (settings.showNetworkOSD !== undefined) {
      this.uiSettings.showNetworkOSD = settings.showNetworkOSD;
      localStorage.setItem('vvorms_show_network_osd', settings.showNetworkOSD ? '1' : '0');
      if (this.networkOSD) {
        if (settings.showNetworkOSD) {
          this.networkOSD.show();
        } else {
          this.networkOSD.hide();
        }
      }
    }
  }

  private handleQuitToLobby(): void {
    this.destroy();
    if (this.config.onQuitToLobby) {
      this.config.onQuitToLobby();
    }
  }

  destroy(): void {
    this.stop();
    this.hudResizeObserver?.disconnect();
    this.virtualGamepad?.destroy();
    this.killFeed?.destroy();
    this.statsPanel?.destroy();
    this.app.destroy(true);
  }
}
